import '../styles/conference.css';
import { Track, RemoteTrack } from 'livekit-client';
import { LiveKitClientManager, ChatMessage } from './livekit-client.js';
import { P2PMeshManager } from './p2p-mesh-manager.js';
import { VORBELLO_LOGO } from '../assets/logo.js';
import { APP_VERSION, APP_BUILD_DATE, APP_BUILD_TAG } from '../version.js';
import { fixWebmDuration } from './webm-duration-fixer.js';

export interface ConferenceConfig {
  roomName: string;
  roomSlug: string;
  token: string;
  requiresVerifiedServer?: boolean;
  onServerConnected?: () => void;
  livekitUrl: string;
  displayName: string;
  appMode?: 'demo' | 'production';
  simulatedParticipants?: number;
  initialCameraEnabled?: boolean;
  initialMicEnabled?: boolean;
  initialStream?: MediaStream | null;
  isAdmin?: boolean;
  isModerator?: boolean;
  isHost?: boolean;
  isCreator?: boolean;
  role?: 'ADMIN' | 'MODERATOR' | 'MEMBER' | 'admin' | 'moderator' | 'user';
  roomSettings?: {
    type?: 'permanent' | 'meeting';
    muteOnEntry?: boolean;
    cameraOffOnEntry?: boolean;
    waitingRoomEnabled?: boolean;
    waitForHost?: boolean;
    allowJoinBeforeHost?: boolean;
    allowGuestInvites?: boolean;
    allowScreenShare?: 'hosts_only' | 'everyone';
    attendanceTracking?: boolean;
    autoRecord?: boolean;
    autoRecordFormat?: 'video-fhd' | 'video-hd' | 'audio';
    autoRecordLayout?: 'composite' | 'screen_only' | 'cameras_only';
    autoRecordContainer?: 'webm' | 'mp4';
    autoRecordNotify?: boolean;
    creatorName?: string;
    pinCode?: string;
  };
  onLeave: (reason?: string) => void;
}

// Crisp SVG Vector Icons for Ultra-Premium Aesthetics
const ICONS = {
  micOn: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  micOff: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"/><path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 5"/><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  camOn: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>`,
  camOff: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"/><path d="m22 8-6 4 6 4V8Z"/><path d="M14 6H4a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8"/><path d="m10 10-6 6"/></svg>`,
  screen: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/><path d="m9 10 3-3 3 3"/><path d="M12 7v6"/></svg>`,
  chat: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>`,
  users: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
  leave: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-6-6 19.8 19.8 0 0 1-3.12-8.68A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91"/><line x1="23" x2="1" y1="1" y2="23"/></svg>`,
  moreVert: `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>`,
  pencil: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>`,
  pin: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" x2="12" y1="17" y2="22"/><path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"/></svg>`,
  trash: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>`,
  micSm: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  micOffSm: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><line x1="2" x2="22" y1="2" y2="22"/><path d="M18.89 13.23A7.12 7.12 0 0 0 19 12v-2"/><path d="M5 10v2a7 7 0 0 0 12 5"/><path d="M15 9.34V5a3 3 0 0 0-5.68-1.33"/><path d="M9 9v3a3 3 0 0 0 5.12 2.12"/><line x1="12" x2="12" y1="19" y2="22"/></svg>`,
  hourglass: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>`,
  userX: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" x2="9" y1="9" y2="15"/><line x1="9" x2="15" y1="9" y2="15"/></svg>`,
  hand: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/><path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v2"/><path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/><path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15"/></svg>`,
};

export class ConferenceUI {
  private config: ConferenceConfig;
  private manager: LiveKitClientManager;
  private container: HTMLElement | null = null;
  private timerInterval: any = null;
  private elapsedSeconds = 0;
  private isChatOpen = false;
  private isParticipantsOpen = false;
  private pinnedIdentity: string | null = null;
  private isStageLockedByAdmin: boolean = false;
  private messages: ChatMessage[] = [];
  private isLocalHandRaised = false;
  private unreadChatCount = 0;
  private userExplicitlyMuted = false;
  private participants: Array<{
    identity: string;
    name: string;
    isSpeaking: boolean;
    isMuted?: boolean;
    isCamOff?: boolean;
    isHandRaised?: boolean;
    role?: 'admin' | 'moderator' | 'user';
  }> = [];
  private p2pMesh: P2PMeshManager | null = null;
  private isP2PMode = false;
  private isScreenShareFlowInProgress = false;

  // Local Meeting Recording State (Zoom Style)
  private isRecording = false;
  private roomRecordingActive = false;
  private roomRecorderName = '';
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingSeconds = 0;
  private recordingStartTime = 0;
  private recordingStopTime = 0;
  private recordingInterval: any = null;
  private recordingStream: MediaStream | null = null;
  private recordingMode: 'video' | 'audio' = 'video';
  private selectedRecordingType: 'video-hd' | 'video-fhd' | 'audio' = 'video-hd';
  private selectedRecordingLayout: 'composite' | 'screen_only' | 'cameras_only' = 'composite';
  private selectedRecordingContainer: 'webm' | 'mp4' = 'webm';
  private recordingContainer: 'webm' | 'mp4' = 'webm';
  private recordingQuality: 'hd' | 'fhd' = 'hd';
  private recordedMimeType: string = '';
  private recordingCanvasInterval: any = null;
  private recordingAudioCtx: AudioContext | null = null;
  private recordingAudioDest: MediaStreamAudioDestinationNode | null = null;
  private recordingAudioNodes: AudioNode[] = [];
  private recordingLocalMicSource: MediaStreamAudioSourceNode | null = null;
  private recordingLocalMicTrack: MediaStreamTrack | null = null;
  private recordingScreenAudioSource: MediaStreamAudioSourceNode | null = null;
  private recordingScreenAudioTrack: MediaStreamTrack | null = null;
  private recordingAudioSyncInterval: any = null;
  private telemetryInterval: any = null;
  private beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  // Screen/Window picker state
  private availableSources: Array<{ id: string; name: string; thumbnail: string | null; isScreen: boolean }> = [];
  private selectedSourceId: string | null = null;
  private selectedSourceName: string = '';
  private currentPickerTab: 'windows' | 'screens' = 'windows';

  // Global moderation toggle states
  private isAllMuted = false;
  private isAllCamOff = false;

  // Web Audio VU Meter state
  private vuAudioContext: AudioContext | null = null;
  private vuMediaStream: MediaStream | null = null;
  private vuAnimationId: number | null = null;
  private trackHealthInterval: any = null;
  private localAudioMonitorInterval: any = null;
  private localAudioMonitorCtx: AudioContext | null = null;
  private localAudioMonitorAnalyser: AnalyserNode | null = null;
  private currentViewMode: 'gallery' | 'speaker' = 'gallery';
  private userExplicitlyChangedViewMode = false;
  private activeSpeakerIdentity: string | null = null;
  private selectedVideoEffect: string = typeof localStorage !== 'undefined' ? (localStorage.getItem('vorbello_video_effect') || 'none') : 'none';
  private lastAttachedRemoteTrack: RemoteTrack | null = null;

  // Active Speaker Anti-Flicker Arbiter (Zoom & Google Meet Hysteresis)
  private currentStageSpeakerId: string | null = null;
  private stageSpeakerSwitchTime: number = 0;
  private candidateSpeakerId: string | null = null;
  private candidateSpeakerStartTime: number = 0;
  private lastStageRenderedSpeakerId: string | null = null;
  private readonly SPEAKER_MIN_HOLD_MS = 1500;
  private readonly SPEAKER_TAKEOVER_MS = 500;

  // Whiteboard state
  private isWhiteboardOpen = false;
  private wbCanvas: HTMLCanvasElement | null = null;
  private wbCtx: CanvasRenderingContext2D | null = null;
  private wbColor = '#38bdf8';
  private wbSize = 3;
  private wbTool: 'pen' | 'rect' | 'circle' | 'eraser' = 'pen';
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;

  private isPollsOpen = false;
  private activePoll: { id: string; question: string; options: string[]; votes: { [optIndex: number]: number }; totalVotes: number; myVoteIndex: number | null; creatorName: string; isClosed: boolean } | null = null;
  public role: 'admin' | 'moderator' | 'user' = 'user';
  public isAdmin: boolean = false;
  public isModerator: boolean = false;
  public isHost: boolean = false;

  // Waiting Room & In-meeting File Sharing State
  public isWaitingInLobby = false;
  private waitingParticipants: Array<{ identity: string; name: string; timestamp: number }> = [];
  private pendingFileAttachment: { name: string; size: number; type: string; dataUrl: string } | null = null;
  private waitingRoomKnockInterval: any = null;
  private participantSearchQuery: string = '';

  public get canInvite(): boolean {
    if (this.isAdmin || this.isModerator) return true;
    return this.config.roomSettings?.allowGuestInvites !== false;
  }

  constructor(config: ConferenceConfig) {
    this.config = config;
    const adminLoggedIn = typeof localStorage !== 'undefined' && localStorage.getItem('vorbello_admin_authenticated') === 'true';
    const moderatorLoggedIn = typeof localStorage !== 'undefined' && localStorage.getItem('vorbello_moderator_authenticated') === 'true';
    const rawRole = (config.role || (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_user_role') : null) || '').toLowerCase();

    if ((config.isAdmin || rawRole === 'admin') && adminLoggedIn) {
      this.role = 'admin';
      this.isAdmin = true;
      this.isModerator = true;
      this.isHost = config.isHost !== undefined ? config.isHost : true;
      this.currentViewMode = 'gallery';
      this.pinnedIdentity = null;
    } else if (config.isModerator || moderatorLoggedIn || rawRole === 'moderator') {
      this.role = 'moderator';
      this.isAdmin = false;
      this.isModerator = true;
      this.isHost = Boolean(config.isHost);
      this.currentViewMode = 'gallery';
      this.pinnedIdentity = null;
    } else {
      this.role = 'user';
      this.isAdmin = false;
      this.isModerator = false;
      this.isHost = false;
    }

    if (this.config.roomSettings?.autoRecordLayout) {
      this.selectedRecordingLayout = this.config.roomSettings.autoRecordLayout;
    } else {
      const savedLayout = (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_default_auto_record_layout') : null) as any;
      if (savedLayout === 'composite' || savedLayout === 'screen_only' || savedLayout === 'cameras_only') {
        this.selectedRecordingLayout = savedLayout;
      }
    }
    const isDesktopApp = typeof window !== 'undefined' && Boolean((window as any).vorbelloDesktop?.isDesktop);
    const savedContainer = (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_default_auto_record_container') : null) as any;
    if (this.config.roomSettings?.autoRecordContainer) {
      this.selectedRecordingContainer = (this.config.roomSettings.autoRecordContainer === 'mp4' && isDesktopApp) ? 'mp4' : 'webm';
    } else if (savedContainer === 'mp4' && isDesktopApp) {
      this.selectedRecordingContainer = 'mp4';
    }
    this.manager = new LiveKitClientManager({
      onTrackSubscribed: (track, pub, participant) => {
        console.info('[LiveKit UI] TrackSubscribed:', track.kind, track.source, 'from', participant.identity);
        if (track.kind === Track.Kind.Audio) {
          this.ensureRemoteAudioElement(participant.identity, track, pub);
          if (this.isRecording) {
            setTimeout(() => this.syncRecordingAudioInputs(), 150);
          }
        } else if (track.kind === Track.Kind.Video && track.source === Track.Source.Camera) {
          const p = this.participants.find(part => part.identity === participant.identity);
          if (p) p.isCamOff = false;
          const tile = document.getElementById(`tile-${participant.identity}`);
          if (tile) {
            let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
            const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
            if (!videoEl) {
              videoEl = document.createElement('video');
              videoEl.className = 'remote-cam-video';
              videoEl.autoplay = true;
              videoEl.playsInline = true;
              videoEl.setAttribute('playsinline', '');
              videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:inherit; z-index:1;';
              tile.prepend(videoEl);
            }
            track.attach(videoEl);
            videoEl.style.display = 'block';
            videoEl.play().catch(() => {});
            if (avatar) avatar.style.display = 'none';
          }
        } else if (track.kind === Track.Kind.Video && track.source === Track.Source.ScreenShare) {
          console.info('[LiveKit UI] Remote screen share track subscribed from', participant.identity);
          this.updateStageLayout();
          setTimeout(() => this.updateStageLayout(), 150);
          setTimeout(() => this.updateStageLayout(), 500);
        }
        this.addOrUpdateParticipant(participant.identity, participant.name || 'Participant');
        this.renderVideoGrid();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onTrackUnsubscribed: (track, _pub, participant) => {
        console.info('[LiveKit UI] TrackUnsubscribed:', track.kind, track.source, 'from', participant.identity);
        if (track.kind === Track.Kind.Audio) {
          const isScreenAudio = _pub?.source === Track.Source.ScreenShareAudio || track?.source === Track.Source.ScreenShareAudio;
          const elementId = isScreenAudio ? `audio-remote-${participant.identity}-screen` : `audio-remote-${participant.identity}`;
          const audioEl = document.getElementById(elementId) as HTMLAudioElement | null;
          if (audioEl) {
            track.detach(audioEl);
            audioEl.srcObject = null;
            audioEl.remove();
          }
        } else if (track.kind === Track.Kind.Video) {
          if (track.source === Track.Source.Camera) {
            const tile = document.getElementById(`tile-${participant.identity}`);
            if (tile) {
              const videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
              if (videoEl) {
                track.detach(videoEl);
                videoEl.srcObject = null;
                videoEl.style.display = 'none';
              }
              const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
              if (avatar) avatar.style.display = 'flex';
            }
          } else if (track.source === Track.Source.ScreenShare) {
            console.info('[LiveKit UI] Remote screen share unsubscribed/ended by', participant.identity);
            if (!this.manager.isScreenShareEnabled) {
              const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
              if (spotlightVideo) {
                try { track.detach(spotlightVideo); } catch (_) {}
                spotlightVideo.srcObject = null;
                spotlightVideo.pause();
              }
              const spotlightContainer = document.getElementById('spotlight-container');
              if (spotlightContainer) spotlightContainer.style.display = 'none';
              const stageArea = document.getElementById('stage-area');
              if (stageArea) stageArea.classList.remove('has-spotlight');
            }
            this.updateStageLayout();
          }
        }
        this.renderVideoGrid();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onTrackMuted: (pub, participant) => {
        console.info('[LiveKit] Track muted:', pub.kind, pub.source, participant.identity);
        const p = this.participants.find(part => part.identity === participant.identity);
        if (p) {
          if (pub.kind === Track.Kind.Audio) {
            if (pub.source === Track.Source.ScreenShareAudio) {
              const screenAudioEl = document.getElementById(`audio-remote-${participant.identity}-screen`) as HTMLAudioElement | null;
              if (screenAudioEl) screenAudioEl.muted = true;
            } else {
              p.isMuted = true;
              // IMMEDIATELY MUTE REMOTE AUDIO ELEMENT WITHOUT PAUSING (preserves autoplay permissions on unmute)
              const audioEl = document.getElementById(`audio-remote-${participant.identity}`) as HTMLAudioElement | null;
              if (audioEl) {
                audioEl.muted = true;
              }
              this.setParticipantSpeaking(participant.identity, false);
            }
          } else if (pub.kind === Track.Kind.Video) {
            if (pub.source === Track.Source.Camera) {
              p.isCamOff = true;
              const tile = document.getElementById(`tile-${participant.identity}`);
              if (tile) {
                const videoEl = tile.querySelector('video.remote-cam-video') as HTMLElement | null;
                const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                if (videoEl) videoEl.style.display = 'none';
                if (avatar) avatar.style.display = 'flex';
              }
            } else if (pub.source === Track.Source.ScreenShare) {
              console.info('[LiveKit UI] Remote screen share muted by', participant.identity);
              this.updateStageLayout();
            }
          }
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
      },
      onTrackUnmuted: (pub, participant) => {
        console.info('[LiveKit] Track unmuted:', pub.kind, pub.source, participant.identity);
        const p = this.participants.find(part => part.identity === participant.identity);
        if (p) {
          if (pub.kind === Track.Kind.Audio) {
            if (pub.source === Track.Source.ScreenShareAudio) {
              const elementId = `audio-remote-${participant.identity}-screen`;
              let audioEl = document.getElementById(elementId) as HTMLAudioElement | null;
              if (!audioEl && pub.track) {
                this.ensureRemoteAudioElement(participant.identity, pub.track, pub);
              } else if (audioEl) {
                audioEl.muted = false;
                audioEl.volume = 1.0;
                if (pub.track) pub.track.attach(audioEl);
                if (audioEl.paused) audioEl.play().catch(() => {});
              }
            } else {
              p.isMuted = false;
              let audioEl = document.getElementById(`audio-remote-${participant.identity}`) as HTMLAudioElement | null;
              if (!audioEl && pub.track) {
                this.ensureRemoteAudioElement(participant.identity, pub.track, pub);
              } else if (audioEl) {
                audioEl.muted = false;
                audioEl.volume = 1.0;
                if (pub.track) pub.track.attach(audioEl);
                if (audioEl.paused) {
                  audioEl.play().catch(() => {});
                }
              }
              this.setParticipantSpeaking(participant.identity, false);
            }
          } else if (pub.kind === Track.Kind.Video) {
            if (pub.source === Track.Source.Camera) {
              p.isCamOff = false;
              const tile = document.getElementById(`tile-${participant.identity}`);
              if (tile) {
                let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
                const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                if (!videoEl) {
                  videoEl = document.createElement('video');
                  videoEl.className = 'remote-cam-video';
                  videoEl.autoplay = true;
                  videoEl.playsInline = true;
                  videoEl.setAttribute('playsinline', '');
                  videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:inherit; z-index:1;';
                  tile.prepend(videoEl);
                }
                if (pub.track) {
                  pub.track.attach(videoEl);
                }
                videoEl.style.display = 'block';
                videoEl.play().catch(() => {});
                if (avatar) avatar.style.display = 'none';
              }
            } else if (pub.source === Track.Source.ScreenShare) {
              console.info('[LiveKit UI] Remote screen share unmuted by', participant.identity);
              this.updateStageLayout();
            }
          }
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
      },
      onReconnecting: () => {
        console.warn('[LiveKit] Reconnecting to room...');
        const pill = document.getElementById('conf-status-pill');
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> Reconectare Rețea în Curs...`;
        }
      },
      onReconnected: () => {
        console.info('[LiveKit UI] Reconnected - performing full track resync');
        const pill = document.getElementById('conf-status-pill');
        if (pill) {
          const isAdmin = typeof localStorage !== 'undefined' && localStorage.getItem('vorbello_admin_authenticated') === 'true';
          const pillText = isAdmin ? 'Server VPS Conectat (LiveKit WebRTC)' : 'Conectat HD';
          pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> ${pillText}`;
        }
        // Force full resync of all remote audio/video after reconnect
        this.resyncAllRemoteTracks();
        this.renderVideoGrid();
        this.updateStageLayout();
      },
      onTrackPublished: (pub, participant) => {
        console.info('[LiveKit UI] TrackPublished:', pub.kind, pub.source, 'from', participant.identity);
        this.addOrUpdateParticipant(participant.identity, participant.name || 'Participant');
        if (!pub.isSubscribed) {
          pub.setSubscribed(true);
        }
        this.renderVideoGrid();
        this.updateControlsState();
        this.updateStageLayout();
        const myIdentity = this.manager.getRoom()?.localParticipant?.identity || '';
        const isRemote = participant.identity !== myIdentity && participant.identity !== 'self';
        if (pub.source === Track.Source.ScreenShare && isRemote) {
          if (this.manager.isScreenShareEnabled) {
            console.info('[LiveKit UI] Remote screen share arrived, yielding local screen share (Zoom/Meet standard)...');
            this.showFloatingToast(`🖥️ ${participant.name || 'Un participant'} a preluat prezentarea ecranului. Partajarea ta a fost oprită.`, 4500);
            this.manager.enableScreenShare(false).catch(e => console.warn(e));
            this.handleScreenShareState();
          }
          setTimeout(() => this.updateStageLayout(), 250);
          setTimeout(() => this.updateStageLayout(), 600);
          setTimeout(() => this.updateStageLayout(), 1200);
        }
      },
      onTrackUnpublished: (pub, participant) => {
        console.info('[LiveKit UI] TrackUnpublished:', pub.kind, pub.source, 'from', participant.identity);
        const myIdentity = this.manager.getRoom()?.localParticipant?.identity || '';
        const isRemote = participant.identity !== myIdentity && participant.identity !== 'self';
        if (pub.source === Track.Source.ScreenShare && isRemote) {
          // If local participant is currently sharing, do NOT close spotlight! Local share is active!
          if (this.manager.isScreenShareEnabled) {
            console.info('[LiveKit UI] Remote screen track unpublished, keeping spotlight for local share.');
            this.updateStageLayout();
            return;
          }
          const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
          if (spotlightVideo) {
            if (pub.track) {
              try { pub.track.detach(spotlightVideo); } catch (_) {}
            }
            spotlightVideo.srcObject = null;
            spotlightVideo.pause();
          }
          const spotlightContainer = document.getElementById('spotlight-container');
          if (spotlightContainer) spotlightContainer.style.display = 'none';
          const stageArea = document.getElementById('stage-area');
          if (stageArea) stageArea.classList.remove('has-spotlight');
          this.updateStageLayout();
        }
      },
      onLocalTrackPublished: (pub) => {
        console.info('[LiveKit UI] LocalTrackPublished:', pub.kind, pub.source);
        this.updateSelfTile();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onLocalTrackUnpublished: (pub) => {
        console.info('[LiveKit UI] LocalTrackUnpublished:', pub.kind, pub.source);
        this.updateSelfTile();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onParticipantConnected: (participant) => {
        this.addOrUpdateParticipant(participant.identity, participant.name || 'Participant');
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();

        // Only the Primary Room Host broadcasts layout sync to new participants, and only if a stage is currently pinned
        if (this.isHost && this.pinnedIdentity) {
          setTimeout(() => {
            this.broadcastAdminLayoutSync().catch(() => {});
          }, 800);
        }

        if (this.isWaitingInLobby) {
          const isStrictWaitingRoom = !!this.config.roomSettings?.waitingRoomEnabled;
          try {
            const meta = JSON.parse(participant.metadata || '{}');
            const isHostPart = meta.isAdmin || meta.isHost || meta.role === 'admin' || meta.role === 'ADMIN';
            if (isHostPart) {
              if (isStrictWaitingRoom) {
                console.info('[ConferenceUI] Host joined room in Waiting Room mode; sending knock for admission approval...');
                this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
              } else {
                console.info('[ConferenceUI] Host has joined the room! Automatically admitting waiting participant from wait-for-host...');
                this.handleWaitingRoomDecision('admitted');
              }
            }
          } catch (_) {}
        }
      },
      onParticipantDisconnected: (participant) => {
        console.info('[LiveKit] Participant disconnected:', participant.identity);
        this.participants = this.participants.filter(p => p.identity !== participant.identity);
        const audioEl = document.getElementById(`audio-remote-${participant.identity}`);
        if (audioEl) audioEl.remove();
        const tile = document.getElementById(`tile-${participant.identity}`);
        if (tile) tile.remove();
        if (this.pinnedIdentity === participant.identity) this.pinnedIdentity = null;
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();
      },
      onActiveSpeakersChanged: (speakers) => {
        const activeIds = new Set(speakers.map(s => s.identity));
        const room = this.manager.getRoom();
        const selfIdentity = room?.localParticipant?.identity;
        const isSelfInSpeakers = selfIdentity ? activeIds.has(selfIdentity) : false;

        // 1. Update visual speaking indicators instantly for all participant tiles
        this.participants.forEach(p => {
          if (p.identity === 'self') {
            if (isSelfInSpeakers && this.manager.isMicEnabled) {
              this.setParticipantSpeaking('self', true);
            } else {
              this.setParticipantSpeaking('self', false);
            }
          } else {
            const isSpeaking = activeIds.has(p.identity) && !p.isMuted;
            this.setParticipantSpeaking(p.identity, isSpeaking);
            if (isSpeaking) {
              const audioEl = document.getElementById(`audio-remote-${p.identity}`) as HTMLAudioElement | null;
              if (audioEl) {
                if (audioEl.muted) audioEl.muted = false;
                if (audioEl.paused) audioEl.play().catch(() => {});
              }
            }
          }
        });

        // 2. Anti-Flicker Stage Arbiter (Zoom & Google Meet Hysteresis)
        const stageArea = document.getElementById('stage-area');
        const isSpotlightActive = stageArea?.classList.contains('has-spotlight') || this.shouldUseFilmstrip() || this.currentViewMode === 'speaker';

        // Only dynamically arbitrate stage when stage is NOT locked to pinned admin and not screen sharing
        const hasRemoteScreenShare = Array.from(room?.remoteParticipants?.values() || []).some(rp => rp.isScreenShareEnabled);
        if (isSpotlightActive && !this.pinnedIdentity && !this.manager.isScreenShareEnabled && !hasRemoteScreenShare) {
          const now = Date.now();
          let topSpeakerId: string | null = null;
          for (const s of speakers) {
            const sId = s.identity === selfIdentity ? 'self' : s.identity;
            const p = this.participants.find(part => part.identity === sId);
            const isMuted = sId === 'self' ? !this.manager.isMicEnabled : !!p?.isMuted;
            if (!isMuted) {
              topSpeakerId = sId;
              break;
            }
          }

          if (topSpeakerId) {
            this.activeSpeakerIdentity = topSpeakerId;

            if (!this.currentStageSpeakerId) {
              // No current stage speaker: immediate placement
              this.currentStageSpeakerId = topSpeakerId;
              this.stageSpeakerSwitchTime = now;
              this.candidateSpeakerId = null;
              this.updateStageLayout();
            } else if (this.currentStageSpeakerId === topSpeakerId) {
              // Current speaker is continuing to speak: reset challenger
              this.candidateSpeakerId = null;
            } else {
              // Challenger wants the stage: require sustained speech & hold duration to prevent flickering
              const timeOnStage = now - this.stageSpeakerSwitchTime;
              if (this.candidateSpeakerId !== topSpeakerId) {
                this.candidateSpeakerId = topSpeakerId;
                this.candidateSpeakerStartTime = now;
              } else {
                const candidateDuration = now - this.candidateSpeakerStartTime;
                if (timeOnStage >= this.SPEAKER_MIN_HOLD_MS && candidateDuration >= this.SPEAKER_TAKEOVER_MS) {
                  this.currentStageSpeakerId = topSpeakerId;
                  this.stageSpeakerSwitchTime = now;
                  this.candidateSpeakerId = null;
                  this.updateStageLayout();
                }
              }
            }
          }
        }
      },
      onChatMessage: (msg) => {
        const existingIdx = this.messages.findIndex(m => m.id === msg.id);
        if (existingIdx !== -1) {
          this.messages[existingIdx] = msg;
        } else {
          this.messages.push(msg);
        }
        this.renderChatMessages();
        if (!this.isChatOpen && !msg.isSelf) {
          this.unreadChatCount++;
          this.updateChatBadge();
          this.playChatChime();
          this.showZoomChatNotification(msg);
        } else if (this.isChatOpen && !msg.isSelf) {
          this.playChatChime();
        }
      },
      onFileTransferProgress: (info) => {
        let existing = this.messages.find(m => m.id === info.transferId);
        if (!existing) {
          existing = {
            id: info.transferId,
            senderName: info.senderName,
            senderIdentity: info.senderIdentity,
            text: info.text,
            timestamp: info.timestamp,
            isSelf: false,
            file: {
              name: info.name,
              size: info.size,
              type: '',
              dataUrl: '',
              progress: info.progress,
            } as any,
          };
          this.messages.push(existing);
          this.renderChatMessages();
          if (!this.isChatOpen) {
            this.unreadChatCount++;
            this.updateChatBadge();
          }
        } else {
          if (existing.file) {
            (existing.file as any).progress = info.progress;
          }
          const bar = document.getElementById(`transfer-bar-${info.transferId}`);
          const pct = document.getElementById(`transfer-pct-${info.transferId}`);
          if (bar && pct) {
            bar.style.width = `${info.progress}%`;
            pct.textContent = `${info.progress}%`;
          } else {
            this.renderChatMessages();
          }
        }
      },
      onModerationMessage: (data) => {
        console.info('[LiveKit] Moderation message received:', data);
        const room = this.manager.getRoom();
        const localPart = room?.localParticipant;
        const myIdentity = (localPart?.identity || '').trim();
        const myName = (localPart?.name || this.config.displayName || '').trim().toLowerCase();

        const senderId = (data.senderIdentity || '').trim();
        const isSender = (senderId !== '' && (senderId === myIdentity || (localPart && senderId === localPart.identity)));

        // If I am the sender who triggered this command, do not mute/disable myself:
        if (isSender) return;

        const targetId = (data.targetIdentity || '').trim();
        const targetName = (data.targetName || '').trim().toLowerCase();

        const isTargeted = targetId === 'self' ||
          (targetId !== '' && (
            targetId === myIdentity ||
            targetId.toLowerCase() === myIdentity.toLowerCase() ||
            (myIdentity.length > 3 && targetId.includes(myIdentity)) ||
            (targetId.length > 3 && myIdentity.includes(targetId))
          )) ||
          (targetName !== '' && myName !== '' && (
            targetName === myName ||
            myName.includes(targetName) ||
            targetName.includes(myName)
          ));

        if (data.action === 'mute-all' || (data.action === 'mute-user' && isTargeted)) {
          this.manager.enableMicrophone(false).then(() => {
            this.userExplicitlyMuted = true;
            this.manager.isMicEnabled = false;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isMuted = true;
            this.updateControlsState();
            this.setParticipantSpeaking('self', false);
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('🔇 Gazda a oprit microfonul dumneavoastră.');
          });
        } else if (data.action === 'unmute-all' || (data.action === 'unmute-user' && isTargeted)) {
          this.manager.enableMicrophone(true).then(() => {
            this.userExplicitlyMuted = false;
            this.manager.isMicEnabled = true;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isMuted = false;
            this.updateControlsState();
            this.setParticipantSpeaking('self', false);
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('🔊 Gazda a reactivat microfonul dumneavoastră.');
          }).catch(() => {
            this.showFloatingToast('🔊 Gazda vă invită să porniți microfonul.');
          });
        } else if (data.action === 'stop-cam-all' || (data.action === 'stop-cam-user' && isTargeted)) {
          this.manager.enableCamera(false).then(() => {
            this.manager.isCameraEnabled = false;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isCamOff = true;
            this.updateControlsState();
            this.updateSelfTile();
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('📷 Gazda a oprit camera dumneavoastră.');
          });
        } else if (data.action === 'start-cam-all' || (data.action === 'start-cam-user' && isTargeted)) {
          this.manager.enableCamera(true).then(() => {
            this.manager.isCameraEnabled = true;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isCamOff = false;
            this.updateControlsState();
            this.updateSelfTile();
            this.renderVideoGrid();
            this.renderParticipantsList();
            this.showFloatingToast('📹 Gazda a reactivat camera dumneavoastră.');
          }).catch(() => {
            this.showFloatingToast('📹 Gazda vă invită să porniți camera.');
          });
        } else if (data.action === 'kick') {
          if (isTargeted) {
            console.warn('[LiveKit UI] This participant was kicked by host. Disconnecting...');
            this.showFloatingToast('⛔ Ați fost eliminat din conferință de către gazdă.');
            try {
              this.manager.enableMicrophone(false);
              this.manager.enableCamera(false);
            } catch (_) {}
            setTimeout(async () => {
              await this.cleanup();
              this.config.onLeave('⛔ Ați fost eliminat din conferință de către gazdă.');
            }, 300);
          } else {
            // Remove the kicked participant from our local list and stage grid
            const kickedId = data.targetIdentity;
            console.info('[LiveKit UI] Remote participant kicked by host:', kickedId, data.targetName);
            if (kickedId) {
              this.participants = this.participants.filter(p => p.identity !== kickedId && (!targetName || p.name.trim().toLowerCase() !== targetName));
              const audioEl = document.getElementById(`audio-remote-${kickedId}`);
              if (audioEl) audioEl.remove();
              const tile = document.getElementById(`tile-${kickedId}`);
              if (tile) tile.remove();
              if (this.pinnedIdentity === kickedId) this.pinnedIdentity = null;
              this.renderVideoGrid();
              this.renderParticipantsList();
              this.updateControlsState();
              this.updateStageLayout();
            }
          }
        } else if (data.action === 'rename-user') {
          const target = data.targetIdentity;
          const newName = (data.newName || '').trim();
          if (newName) {
            if (isTargeted) {
              this.config.displayName = newName;
              this.manager.displayName = newName;
              try { localStorage.setItem('vorbello_display_name', newName); } catch (_) {}
              const selfP = this.participants.find(p => p.identity === 'self');
              if (selfP) selfP.name = `${newName} (Tu)`;
              this.showFloatingToast(`✏️ Numele dumneavoastră a fost actualizat la "${newName}".`);
            } else if (target) {
              const p = this.participants.find(part => part.identity === target);
              if (p) p.name = newName;
            }
            this.renderVideoGrid();
            this.renderParticipantsList();
          }
        }
      },
      onScreenShareStatus: async (data) => {
        console.info('[LiveKit UI] Screen share status:', data);
        const myIdentity = this.manager.getRoom()?.localParticipant?.identity || '';
        const isRemote = data.identity !== myIdentity && data.identity !== 'self';
        if (data.isSharing && isRemote) {
          // If local participant was already sharing, yield and stop local share (Zoom/Meet standard behavior)
          if (this.manager.isScreenShareEnabled) {
            console.info('[ConferenceUI] Another participant started sharing screen. Stopping local screen share...');
            this.showFloatingToast(`🖥️ ${data.sharerName || 'Un alt participant'} a preluat partajarea ecranului. Partajarea ta a fost oprită.`, 4500);
            try {
              await this.manager.enableScreenShare(false);
            } catch (_) {}
            this.handleScreenShareState();
          } else {
            this.showFloatingToast(`🖥️ ${data.sharerName} a început partajarea ecranului.`);
          }

          const room = this.manager.getRoom();
          if (room) {
            const rp = room.remoteParticipants.get(data.identity);
            if (rp) {
              const sp = rp.getTrackPublication(Track.Source.ScreenShare);
              if (sp && !sp.isSubscribed) {
                sp.setSubscribed(true);
              }
            }
          }
          this.updateStageLayout();
          setTimeout(() => this.updateStageLayout(), 250);
          setTimeout(() => this.updateStageLayout(), 600);
          setTimeout(() => this.updateStageLayout(), 1200);
        } else if (!data.isSharing && isRemote) {
          if (this.manager.isScreenShareEnabled) {
            console.info('[LiveKit UI] Remote screen share ended, keeping local share active.');
            this.updateStageLayout();
            return;
          }
          const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
          if (spotlightVideo) {
            spotlightVideo.srcObject = null;
            spotlightVideo.pause();
          }
          const spotlightContainer = document.getElementById('spotlight-container');
          if (spotlightContainer) spotlightContainer.style.display = 'none';
          const stageArea = document.getElementById('stage-area');
          if (stageArea) stageArea.classList.remove('has-spotlight');
          this.updateStageLayout();
        } else {
          this.updateStageLayout();
        }
      },
      onAudioPlaybackChanged: (canPlayback) => {
        console.info('[LiveKit UI] Audio playback capability changed:', canPlayback);
        this.handleAudioPlaybackBanner(!canPlayback);
      },
      onLocalScreenStreamReady: (stream) => {
        console.info('[Vorbello Conference] onLocalScreenStreamReady received immediately (0ms lag)');
        const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
        if (spotlightVideo) {
          spotlightVideo.muted = true;
          spotlightVideo.playsInline = true;
          spotlightVideo.setAttribute('muted', '');
          spotlightVideo.setAttribute('playsinline', '');
          if (spotlightVideo.srcObject !== stream) {
            spotlightVideo.srcObject = stream;
          }
          spotlightVideo.onloadedmetadata = () => {
            spotlightVideo.play().catch(() => {});
          };
          spotlightVideo.play().catch(() => {});
        }
        const hasAudio = stream.getAudioTracks().length > 0;
        if (hasAudio) {
          this.showFloatingToast('🔊 Partajare ACTIVĂ CU SUNET (sunetul de sistem/filă se transmite tuturor)', 4500);
        } else {
          this.showFloatingToast('ℹ️ Partajare activă FĂRĂ SUNET. Sfat: Pentru sunet, la alegerea ecranului bifați „Partajează și sunetul din sistem” (Share audio) sau alegeți o filă Chrome.', 7000);
        }
        this.handleScreenShareState();
        this.updateStageLayout();
      },
      onLocalScreenShareEnded: () => {
        console.info('[Vorbello Conference] Local screen share ended');
        this.handleScreenShareState();
        this.updateControlsState();
      },
      onDisconnected: () => {
        console.info('[Vorbello Conference] Disconnected event received');
        const pill = document.getElementById('conf-status-pill');
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> În Curs de Reconectare...`;
        }
      },
      onHandRaise: (data) => {
        console.info('[LiveKit] Hand raise received:', data);
        if (data.identity === 'all' || (data as any).isLowerAll) {
          this.isLocalHandRaised = false;
          this.participants.forEach(part => { part.isHandRaised = false; });
          this.showFloatingToast(`✋ Administratorul a coborât toate mâinile din sală.`);
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateControlsState();
          return;
        }
        const p = this.participants.find(part => part.identity === data.identity);
        if (p) {
          p.isHandRaised = data.isRaised;
        } else if (data.identity === 'self') {
          this.isLocalHandRaised = data.isRaised;
          const selfP = this.participants.find(part => part.identity === 'self');
          if (selfP) selfP.isHandRaised = data.isRaised;
        }
        if (data.isRaised) {
          const name = p ? p.name : data.participantName;
          this.showFloatingToast(`✋ ${name} a ridicat mâna!`);
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();
      },
      onReaction: (data) => {
        console.info('[LiveKit] Reaction received:', data);
        this.triggerFloatingReaction(data.emoji, data.identity, data.participantName);
      },
      onRecordingStatus: (data) => {
        console.info('[LiveKit UI] Remote recording status received:', data);
        this.roomRecordingActive = !!data.isRecording;
        this.roomRecorderName = data.recorderName || '';
        this.updateRecordingUI(this.isRecording || this.roomRecordingActive);
        if (data.isRecording) {
          this.showFloatingToast(`🔴 ${data.recorderName} a pornit înregistrarea ședinței.`);
        } else {
          this.showFloatingToast('⏹️ Înregistrarea ședinței a fost oprită.');
        }
      },
      onRecordingCommand: (data) => {
        console.warn('[LiveKit UI] Remote recording command ignored. Local recording is strictly controlled by host:', data);
      },
      onMeetingEndedByHost: (data) => {
        console.warn('[ConferenceUI] Meeting ended by host for all participants:', data);
        this.cleanup();
        this.config.onLeave(data.reason || `⚠️ Gazda (${data.hostName || 'organizatorul'}) a încheiat ședința pentru toți participanții.`);
      },
      onWhiteboardDraw: (data) => {
        this.handleRemoteWhiteboardDraw(data);
      },
      onWhiteboardClear: () => {
        this.clearWhiteboard(false);
      },
      onWhiteboardToggle: (data) => {
        if (data.isOpen) {
          this.openWhiteboard(false);
          this.showFloatingToast(`🎨 ${data.senderName} a deschis Tabla Interactivă.`);
        } else {
          this.closeWhiteboard(false);
          this.showFloatingToast(`🎨 ${data.senderName} a închis Tabla Interactivă.`);
        }
      },
      onPollLaunch: (data) => {
        this.handleRemotePollLaunch(data);
      },
      onPollVote: (data) => {
        this.handleRemotePollVote(data);
      },
      onPollClose: (data) => {
        this.handleRemotePollClose(data);
      },
      onWaitingRoomKnock: (data) => {
        this.handleWaitingRoomKnock(data);
      },
      onWaitingRoomDecision: (data) => {
        const room = this.manager.getRoom();
        const myIdentity = room?.localParticipant?.identity || 'self';
        const myName = (this.config.displayName || '').trim().toLowerCase();
        const targetId = (data.targetIdentity || '').trim();
        if (targetId === myIdentity || targetId === 'self' || targetId.toLowerCase() === myName) {
          this.handleWaitingRoomDecision(data.decision);
          return;
        }

        // Real-time synchronization across co-admins and attendees:
        this.waitingParticipants = this.waitingParticipants.filter(w => w.identity !== targetId);
        const banner = document.getElementById('waiting-room-host-banner');
        if (banner) {
          if (this.waitingParticipants.length === 0) {
            banner.remove();
          } else {
            const next = this.waitingParticipants[0];
            this.showWaitingRoomBanner(next.name, next.identity);
          }
        }
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();
        this.updateStageLayout();
      },
      onWaitingRoomMoveBack: (data) => {
        const room = this.manager.getRoom();
        const myIdentity = room?.localParticipant?.identity || 'self';
        const targetId = (data.targetIdentity || '').trim();
        if (targetId === myIdentity || targetId === 'self') {
          this.enterWaitingRoomLobby(true);
          return;
        }
        const p = this.participants.find(part => part.identity === targetId);
        if (p && !this.waitingParticipants.some(w => w.identity === targetId)) {
          this.waitingParticipants.push({
            identity: p.identity,
            name: p.name,
            timestamp: Date.now(),
          });
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateControlsState();
          this.updateStageLayout();
        }
      },
      onHostStatus: (data) => {
        console.info('[ConferenceUI] Received host_status broadcast from host:', data.hostName);
        if (this.isWaitingInLobby) {
          const isStrictWaitingRoom = !!this.config.roomSettings?.waitingRoomEnabled;
          if (isStrictWaitingRoom) {
            console.info('[ConferenceUI] Host is online; sending waiting_room_knock for admission approval...');
            this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
          } else {
            console.info('[ConferenceUI] Host is online; auto-admitting from wait-for-host...');
            this.handleWaitingRoomDecision('admitted');
          }
        }
      },
      onAdminLayoutSync: (data) => {
        console.info('[ConferenceUI] Received admin layout sync from host:', data);
        const myLiveKitId = this.manager.getRoom()?.localParticipant?.identity || '';
        if (data.senderIdentity && data.senderIdentity === myLiveKitId) return;

        // If I am the Primary Room Host, do not let non-primary co-hosts override my stage layout!
        if (this.isHost && !data.isPrimaryHost) {
          console.info('[ConferenceUI] Preserving Primary Host stage layout; ignoring secondary admin sync');
          return;
        }

        const isMePinned = Boolean(data.pinnedIdentity && (data.pinnedIdentity === myLiveKitId || data.pinnedIdentity === 'self'));

        this.isStageLockedByAdmin = Boolean(data.isStageLocked && data.pinnedIdentity);

        if (data.viewMode === 'speaker') {
          this.currentViewMode = 'speaker';
          this.pinnedIdentity = data.pinnedIdentity ? (isMePinned ? 'self' : data.pinnedIdentity) : null;
          const viewIcon = document.getElementById('view-mode-icon');
          const viewText = document.getElementById('view-mode-text');
          const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
          const sheetViewText = document.getElementById('sheet-label-view-mode');
          if (viewIcon) viewIcon.innerText = this.isStageLockedByAdmin ? '🔒' : '👤';
          if (viewText) viewText.innerText = this.isStageLockedByAdmin ? 'Scenă Fixată' : 'Vorbitor Activ';
          if (sheetViewIcon) sheetViewIcon.innerText = this.isStageLockedByAdmin ? '🔒' : '👤';
          if (sheetViewText) sheetViewText.innerText = this.isStageLockedByAdmin ? 'Scenă Fixată' : 'Mod: Vorbitor';
          if (data.pinnedName) {
            this.showFloatingToast(`👑 Gazda a fixat scena pe: ${data.pinnedName}`);
          }
        } else {
          this.currentViewMode = 'gallery';
          this.pinnedIdentity = null;
          this.isStageLockedByAdmin = false;
          const viewIcon = document.getElementById('view-mode-icon');
          const viewText = document.getElementById('view-mode-text');
          const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
          const sheetViewText = document.getElementById('sheet-label-view-mode');
          if (viewIcon) viewIcon.innerText = '⊞';
          if (viewText) viewText.innerText = 'Galerie';
          if (sheetViewIcon) sheetViewIcon.innerText = '⊞';
          if (sheetViewText) sheetViewText.innerText = 'Mod: Galerie';
          this.showFloatingToast('👑 Gazda a setat modul: Galerie');
        }
        this.updateStageLayout();
        this.renderVideoGrid();
        this.renderParticipantsList();
      },
    });
    this.manager.displayName = config.displayName;
  }

  public setParticipantSpeaking(identity: string, isSpeaking: boolean) {
    const p = this.participants.find(part => part.identity === identity);
    if (!p) return;

    const isSelf = identity === 'self';
    const isMuted = isSelf ? !this.manager.isMicEnabled : !!p.isMuted;
    const effectiveSpeaking = isSpeaking && !isMuted;

    p.isSpeaking = effectiveSpeaking;

    const tile = document.getElementById(`tile-${identity}`);
    if (tile) {
      tile.classList.toggle('speaking', effectiveSpeaking);
      const iconEl = tile.querySelector('.speaker-indicator-icon');
      if (iconEl) iconEl.textContent = effectiveSpeaking ? '🔊' : '👤';

      const audioInd = tile.querySelector('.tile-audio-indicator') as HTMLElement | null;
      if (audioInd) {
        if (isMuted) {
          audioInd.className = 'tile-audio-indicator is-muted';
          audioInd.innerHTML = '<span class="indicator-icon">🔇</span> Microfon Oprit';
        } else if (effectiveSpeaking) {
          audioInd.className = 'tile-audio-indicator is-speaking';
          audioInd.innerHTML = '<span class="audio-wave-anim"><span></span><span></span><span></span></span> 🔊 Vorbește (Sunet Live)';
        } else {
          audioInd.className = 'tile-audio-indicator';
          audioInd.innerHTML = '<span class="indicator-icon">🎤</span> HD Audio Pornit';
        }
      }
    }
  }

  private startLocalAudioLevelMonitor() {
    if (this.localAudioMonitorInterval) {
      clearInterval(this.localAudioMonitorInterval);
      this.localAudioMonitorInterval = null;
    }

    if (!this.manager.isMicEnabled) {
      this.setParticipantSpeaking('self', false);
      return;
    }

    try {
      const room = this.manager.getRoom();
      const localMicPub = room?.localParticipant?.getTrackPublication(Track.Source.Microphone);
      const mst = (localMicPub?.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
      const stream = (mst && mst.readyState === 'live') ? new MediaStream([mst]) : this.manager.getLocalMicStream();

      if (stream && stream.getAudioTracks().length > 0) {
        const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
        if (AudioCtx) {
          if (!this.localAudioMonitorCtx || this.localAudioMonitorCtx.state === 'closed') {
            this.localAudioMonitorCtx = new AudioCtx();
          }
          if (this.localAudioMonitorCtx.state === 'suspended') {
            this.localAudioMonitorCtx.resume().catch(() => {});
          }
          const src = this.localAudioMonitorCtx.createMediaStreamSource(stream);
          this.localAudioMonitorAnalyser = this.localAudioMonitorCtx.createAnalyser();
          this.localAudioMonitorAnalyser.fftSize = 128;
          this.localAudioMonitorAnalyser.smoothingTimeConstant = 0.4;
          src.connect(this.localAudioMonitorAnalyser);

          const data = new Uint8Array(this.localAudioMonitorAnalyser.frequencyBinCount);
          this.localAudioMonitorInterval = setInterval(() => {
            if (!this.manager.isMicEnabled) {
              this.setParticipantSpeaking('self', false);
              return;
            }
            if (!this.localAudioMonitorAnalyser) return;
            this.localAudioMonitorAnalyser.getByteFrequencyData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) sum += data[i];
            const avg = sum / data.length;
            this.setParticipantSpeaking('self', avg > 10);
          }, 120);
        }
      }
    } catch (e) {
      console.warn('[ConferenceUI] Local audio monitor error:', e);
    }
  }

  public async start(): Promise<void> {
    this.renderLayout();
    this.startTimer();

    // Early global mobile/tablet touch unlock for WebRTC media playback
    if (typeof window !== 'undefined' && !(window as any)._vorbelloEarlyTouchUnlockAttached) {
      (window as any)._vorbelloEarlyTouchUnlockAttached = true;
      const unlockMedia = async () => {
        try {
          if (this.manager.getRoom() && !this.manager.getRoom()?.canPlaybackAudio) {
            await this.manager.getRoom()?.startAudio();
          }
        } catch (_) {}
        document.querySelectorAll('audio').forEach((a: any) => {
          if (a.paused && !a.muted) a.play().catch(() => {});
        });
        document.querySelectorAll('video').forEach((v: any) => {
          if (v.srcObject && v.paused) v.play().catch(() => {});
        });
      };
      ['touchstart', 'touchend', 'pointerdown', 'click'].forEach(evt => {
        window.addEventListener(evt, unlockMedia, { passive: true });
      });
    }

    const initCam = this.config.initialCameraEnabled !== undefined ? this.config.initialCameraEnabled : true;
    const initMic = this.config.initialMicEnabled !== undefined ? this.config.initialMicEnabled : !this.userExplicitlyMuted;

    this.manager.isCameraEnabled = initCam;
    this.manager.isMicEnabled = initMic;
    if (!initMic) this.userExplicitlyMuted = true;

    // Add self to participants list
    this.participants.push({
      identity: 'self',
      name: `${this.config.displayName} (Tu)`,
      isSpeaking: false,
      isMuted: !initMic,
      isCamOff: !initCam,
      role: this.role,
    });

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const pCountParam = this.config.requiresVerifiedServer ? 0 : this.config.simulatedParticipants || parseInt(urlParams.get('participants') || '0', 10);
      if (pCountParam > 1) {
        const names = ['Ana Popescu', 'Mihai Ionescu', 'Elena Dumitru', 'Radu Vasilescu', 'Cristian Tudor', 'Maria Enache', 'Andrei Dobre'];
        for (let i = 0; i < pCountParam - 1; i++) {
          this.participants.push({
            identity: `part-${i + 1}`,
            name: names[i] || `Participant ${i + 1}`,
            isSpeaking: i === 0,
            isMuted: i % 2 === 1,
            isCamOff: false,
            role: i === 0 ? 'moderator' : 'user',
          });
        }
      }
    } catch (_) {}

    this.updateStageLayout();
    this.renderVideoGrid();
    this.updateControlsState();

    // 1. Immediately adopt transferred stream or acquire local camera preview (zero delay / no black screen!)
    if (this.config.initialStream && this.config.initialStream.active) {
      this.manager.setInitialCameraStream(this.config.initialStream);
    } else if (this.manager.isCameraEnabled) {
      try {
        await this.manager.enableCamera(true);
      } catch (camErr) {
        console.warn('[ConferenceUI] Immediate camera preview error:', camErr);
      }
    }
    this.updateSelfTile();

    (window as any).activeConference = this;
    (window as any).vorbelloDisconnect = () => this.cleanup();
    (window as any).vorbelloSetParticipants = (n: number) => {
      const names = ['Ana Popescu', 'Mihai Ionescu', 'Elena Dumitru', 'Radu Vasilescu', 'Cristian Tudor', 'Maria Enache', 'Andrei Dobre'];
      this.participants = [{
        identity: 'self',
        name: `${this.config.displayName} (Tu)`,
        isSpeaking: false,
        isMuted: false,
        isCamOff: false,
        role: this.role,
      }];
      for (let i = 0; i < n - 1; i++) {
        this.participants.push({
          identity: `part-${i + 1}`,
          name: names[i] || `Participant ${i + 1}`,
          isSpeaking: i === 0,
          isMuted: i % 2 === 1,
          isCamOff: false,
          role: i === 0 ? 'moderator' : 'user',
        });
      }
      this.updateStageLayout();
      this.renderVideoGrid();
      this.renderParticipantsList();
    };
    this.beforeUnloadHandler = () => {
      this.cleanup();
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);

    const isProduction = this.config.appMode === 'production';

    try {
      await this.manager.connect(this.config.livekitUrl, this.config.token);
      this.config.onServerConnected?.();

      const shouldWaitForHost = !this.isHost && (this.config.roomSettings?.waitForHost !== false);
      const isWaitingRoomUser = !this.isHost && (this.config.roomSettings?.waitingRoomEnabled || shouldWaitForHost);
      if (isWaitingRoomUser) {
        // If it's a waiting-for-host room, check if the host is already in the room
        let hostPresent = false;
        const livekitRoom = this.manager.getRoom();
        if (livekitRoom) {
          livekitRoom.remoteParticipants.forEach(p => {
            try {
              const meta = JSON.parse(p.metadata || '{}');
              if (meta.isAdmin || meta.isHost || meta.role === 'admin' || meta.role === 'ADMIN') {
                hostPresent = true;
              }
            } catch (_) {}
          });
        }

        if (this.config.roomSettings?.waitingRoomEnabled || !hostPresent) {
          console.info('[ConferenceUI] Connected in Waiting Room mode (waiting for host approval or host presence)...');
          this.enterWaitingRoomLobby(false);
          await this.manager.sendWaitingRoomKnock(this.config.displayName);
          this.waitingRoomKnockInterval = setInterval(() => {
            if (this.isWaitingInLobby) {
              this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
            } else {
              clearInterval(this.waitingRoomKnockInterval);
              this.waitingRoomKnockInterval = null;
            }
          }, 8000);
          return;
        }
      }

      if (this.isHost || this.isAdmin) {
        this.manager.sendDataMessage({
          type: 'host_status',
          hostConnected: true,
          hostName: this.config.displayName,
        }).catch(() => {});
        // Also broadcast initial layout so that Admin is immediately spotlighted on stage for everyone
        setTimeout(() => {
          this.broadcastAdminLayoutSync().catch(() => {});
        }, 1200);
      }

      // Immediately sync all remote participants already in the room upon connection:
      const livekitRoom = this.manager.getRoom();
      if (livekitRoom) {
        livekitRoom.remoteParticipants.forEach(rp => {
          this.addOrUpdateParticipant(rp.identity, rp.name || 'Participant');
        });
      }
      this.resyncAllRemoteTracks();
      this.renderVideoGrid();
      this.renderParticipantsList();

      const pill = document.getElementById('conf-status-pill');
      if (pill) {
        const isAdmin = typeof localStorage !== 'undefined' && localStorage.getItem('vorbello_admin_authenticated') === 'true';
        const pillText = isAdmin ? 'Server VPS Conectat (LiveKit WebRTC)' : 'Conectat HD';
        pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> ${pillText}`;
      }
      this.updateTelemetryPopover(true);

      // 2. Publish camera to room
      try {
        console.info('[ConferenceUI] Publishing camera to room...');
        if (this.manager.isCameraEnabled) {
          await this.manager.enableCamera(true);
        }
      } catch (camErr) {
        console.warn('[ConferenceUI] Publish camera warning:', camErr);
      }

      // Fast track publication queue for LiveKit Single PeerConnection (Protocol 15)
      await new Promise(r => setTimeout(r, 100));

      // 3. Publish microphone to room ONLY if user did not explicitly mute and isMicEnabled is true
      try {
        console.info('[ConferenceUI] Publishing microphone to room...');
        if (!this.userExplicitlyMuted && this.manager.isMicEnabled) {
          await this.manager.enableMicrophone(true);
        } else {
          console.info('[ConferenceUI] User selected mute; keeping microphone muted.');
          this.manager.isMicEnabled = false;
          const selfP = this.participants.find(p => p.identity === 'self');
          if (selfP) selfP.isMuted = true;
          this.updateControlsState();
        }
      } catch (micErr) {
        console.warn('[ConferenceUI] Publish microphone warning:', micErr);
      }

      this.updateControlsState();
      this.updateSelfTile();

      if (livekitRoom) {
        livekitRoom.remoteParticipants.forEach(rp => {
          this.addOrUpdateParticipant(rp.identity, rp.name || 'Participant');
        });
      }
      this.resyncAllRemoteTracks();
      this.renderVideoGrid();
      this.renderParticipantsList();

      // Ensure periodic resync during the first 2 seconds to catch any slow-joining peers:
      setTimeout(() => {
        this.resyncAllRemoteTracks();
        this.renderVideoGrid();
        this.renderParticipantsList();
      }, 500);
      setTimeout(() => {
        this.resyncAllRemoteTracks();
        this.renderVideoGrid();
        this.renderParticipantsList();
      }, 1500);

      if (this.manager.isMicEnabled) {
        this.startLocalAudioLevelMonitor();
      }
    } catch (err) {
      console.warn('[Vorbello Conference] Connecting to LiveKit:', err);
      if (this.config.requiresVerifiedServer) {
        await this.cleanup();
        throw new Error('Conectarea la serverul Vorbello nu a reușit. Prezența PP nu a fost confirmată. Încearcă din nou sau contactează organizatorul.');
      }
      const pill = document.getElementById('conf-status-pill');
      if (isProduction) {
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> În Așteptare Server VPS (Port 7880)`;
        }
        this.updateTelemetryPopover(false, 'În Așteptare Server VPS (Port 7880)');
        this.updateSelfTile();
      } else {
        console.warn('[Vorbello Conference] Live SFU offline, activating interactive demo mode:', err);
        if (pill) {
          pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> Mod Demo / Test Activ (Fără Server)`;
        }
        this.updateTelemetryPopover(false, 'Mod Demo Local');
        this.updateSelfTile();

        // Add simulated remote participants ONLY in demo mode
        this.addOrUpdateParticipant('part-ana', 'Ana Popescu (Moderator)');
        this.addOrUpdateParticipant('part-mihai', 'Mihai Ionescu (Membru)');
        this.renderVideoGrid();
        this.renderParticipantsList();
        this.updateControlsState();

        this.messages.push({
          id: 'msg-welcome',
          senderName: 'Ana Popescu',
          senderIdentity: 'part-ana',
          text: 'Bună ziua! Aceasta este o cameră de test Vorbello. Puteți porni camera, microfonul și ecranul din butoanele de jos.',
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          isSelf: false,
        });
        this.renderChatMessages();
      }
    }

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const pCountParam = this.config.requiresVerifiedServer ? 0 : parseInt(urlParams.get('participants') || '0', 10);
      if (pCountParam > 1) {
        (window as any).vorbelloSetParticipants?.(pCountParam);
      }
    } catch (_) {}

    // Auto-record strictly if room configured and current session is the actual host/creator
    if (this.config.roomSettings?.autoRecord && this.isHost) {
      setTimeout(() => {
        if (!this.isRecording) {
          if (this.config.roomSettings?.autoRecordLayout) {
            this.selectedRecordingLayout = this.config.roomSettings.autoRecordLayout;
          }
          const rawFormat = this.config.roomSettings?.autoRecordFormat;
          const isDesktop = typeof window !== 'undefined' && Boolean((window as any).vorbelloDesktop?.isDesktop);
          const rawContainer = (this.config.roomSettings?.autoRecordContainer === 'mp4' && isDesktop) ? 'mp4' : 'webm';
          const format: 'video-fhd' | 'video-hd' | 'audio' = 
            (rawFormat === 'video-fhd' || rawFormat === 'audio') ? rawFormat : 'video-hd';
          const container: 'webm' | 'mp4' = (rawContainer === 'mp4') ? 'mp4' : 'webm';
          const layoutName = this.selectedRecordingLayout === 'cameras_only'
            ? 'Cameră Curată (Doar Camera Mea Admin)'
            : (this.selectedRecordingLayout === 'screen_only' ? 'Doar Ecran' : 'Studio PiP');
          const formatLabel = `${format === 'video-fhd' ? 'Video Full HD (1080p)' : (format === 'audio' ? 'Doar Audio HD' : 'Video HD (720p)')} [${container.toUpperCase()}] • Layout: ${layoutName}`;
          console.info(`[ConferenceUI] Auto-record is enabled for this room; starting host recording in format: ${formatLabel}...`);
          this.startRecording(format, container).catch(e => console.warn('[ConferenceUI] Auto-record failed:', e));
        }
      }, 1500);
    }

    // Start periodic track health monitoring
    this.startTrackHealthChecker();
  }

  public async startP2PMeshFallback(): Promise<void> {
    console.info('[ConferenceUI] Starting P2P WebRTC Mesh Fallback via signal.php...');
    this.isP2PMode = true;

    const pill = document.getElementById('conf-status-pill');
    if (pill) {
      pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> Conectat Direct (P2P WebRTC)`;
    }
    this.updateTelemetryPopover(true, 'Conexiune P2P WebRTC Directă Activă');

    // Build combined local stream for P2P mesh (both camera and microphone)
    const combinedTracks: MediaStreamTrack[] = [];
    const camStream = this.manager.getLocalCameraStream();
    if (camStream && camStream.active && camStream.getVideoTracks().length > 0) {
      combinedTracks.push(...camStream.getVideoTracks());
    }
    const micStream = this.manager.getLocalMicStream();
    if (micStream && micStream.active && micStream.getAudioTracks().length > 0) {
      combinedTracks.push(...micStream.getAudioTracks());
    }

    const needVideo = this.manager.isCameraEnabled && combinedTracks.filter(t => t.kind === 'video').length === 0;
    const needAudio = this.manager.isMicEnabled && combinedTracks.filter(t => t.kind === 'audio').length === 0;

    if (needVideo || needAudio) {
      try {
        const freshStream = await navigator.mediaDevices.getUserMedia({
          video: needVideo ? { facingMode: 'user' } : false,
          audio: needAudio ? { echoCancellation: true, noiseSuppression: true } : false,
        });
        freshStream.getTracks().forEach(t => combinedTracks.push(t));
      } catch (e) {
        console.warn('[P2P UI] getUserMedia fallback warning:', e);
      }
    }

    const localStream = new MediaStream(combinedTracks);

    // Attach local stream to self tile
    if (localStream && localStream.getVideoTracks().length > 0) {
      const selfTile = document.getElementById('tile-self');
      if (selfTile) {
        let videoEl = selfTile.querySelector('video') as HTMLVideoElement | null;
        if (videoEl && videoEl.srcObject !== localStream) {
          videoEl.srcObject = localStream;
          videoEl.play().catch(() => {});
        }
      }
    }

    const peerId = 'peer_' + Math.random().toString(36).substring(2, 9);
    this.p2pMesh = new P2PMeshManager({
      peerId,
      displayName: this.config.displayName,
      roomSlug: this.config.roomSlug || 'sala-vps-live',
      callbacks: {
        onParticipantConnected: (identity, name) => {
          console.info('[P2P UI] Remote participant connected:', identity, name);
          this.addOrUpdateParticipant(identity, name);
          this.showFloatingToast(`👋 ${name} s-a conectat în sală!`);
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateStageLayout();
        },
        onParticipantDisconnected: (identity) => {
          console.info('[P2P UI] Remote participant left:', identity);
          const p = this.participants.find(part => part.identity === identity);
          if (p) {
            this.showFloatingToast(`🚪 ${p.name} a părăsit conferința`);
          }
          this.participants = this.participants.filter(part => part.identity !== identity);
          const audioEl = document.getElementById(`audio-remote-${identity}`);
          if (audioEl) audioEl.remove();
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.updateStageLayout();
        },
        onRemoteTrack: (identity, name, track, stream) => {
          console.info('[P2P UI] Remote track received:', track.kind, 'from', name, identity);
          this.addOrUpdateParticipant(identity, name);
          const p = this.participants.find(part => part.identity === identity);

          if (track.kind === 'video') {
            if (p) p.isCamOff = false;
            this.renderVideoGrid();
            const attachVideo = () => {
              const tile = document.getElementById(`tile-${identity}`);
              if (tile) {
                let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
                const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                if (!videoEl) {
                  videoEl = document.createElement('video');
                  videoEl.className = 'remote-cam-video';
                  videoEl.autoplay = true;
                  videoEl.playsInline = true;
                  videoEl.muted = true; // Crucial for mobile autoplay!
                  videoEl.setAttribute('playsinline', '');
                  videoEl.setAttribute('muted', '');
                  videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:inherit; z-index:1;';
                  tile.prepend(videoEl);
                }
                if (videoEl.srcObject !== stream) {
                  videoEl.srcObject = stream;
                }
                videoEl.style.display = 'block';
                videoEl.play().catch(e => console.warn('[P2P UI] Video play error:', e));
                if (avatar) avatar.style.display = 'none';
              }
              this.updateStageLayout();
            };
            attachVideo();
            setTimeout(attachVideo, 80);
            setTimeout(attachVideo, 300);
          } else if (track.kind === 'audio') {
            if (p) p.isMuted = false;
            let audioEl = document.getElementById(`audio-remote-${identity}`) as HTMLAudioElement | null;
            if (!audioEl) {
              audioEl = document.createElement('audio');
              audioEl.id = `audio-remote-${identity}`;
              audioEl.autoplay = true;
              (audioEl as any).playsInline = true;
              audioEl.setAttribute('playsinline', '');
              document.body.appendChild(audioEl);
            }
            audioEl.srcObject = stream;
            audioEl.play().catch(async (e) => {
              console.warn('[P2P UI] Remote audio autoplay error:', e);
              this.handleAudioPlaybackBanner(true);
            });
          }
          this.renderParticipantsList();
        },
        onChatMessage: (msg) => {
          const existingIdx = this.messages.findIndex(m => m.id === msg.id);
          const fullMsg: ChatMessage = {
            id: msg.id,
            senderName: msg.senderName,
            senderIdentity: msg.senderIdentity || 'remote',
            text: msg.text || '',
            timestamp: msg.timestamp || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            isSelf: false,
            file: msg.file,
          };
          if (existingIdx !== -1) {
            this.messages[existingIdx] = fullMsg;
          } else {
            this.messages.push(fullMsg);
          }
          this.renderChatMessages();
          if (!this.isChatOpen) {
            this.unreadChatCount++;
            this.updateChatBadge();
            this.playChatChime();
            this.showZoomChatNotification(fullMsg);
          } else {
            this.playChatChime();
          }
        },
        onFileTransferProgress: (info: any) => {
          let existing = this.messages.find(m => m.id === info.transferId);
          if (!existing) {
            existing = {
              id: info.transferId,
              senderName: info.senderName,
              senderIdentity: info.senderIdentity,
              text: info.text,
              timestamp: info.timestamp,
              isSelf: false,
              file: {
                name: info.name,
                size: info.size,
                type: '',
                dataUrl: '',
                progress: info.progress,
              } as any,
            };
            this.messages.push(existing);
            this.renderChatMessages();
            if (!this.isChatOpen) {
              this.unreadChatCount++;
              this.updateChatBadge();
            }
          } else {
            if (existing.file) {
              (existing.file as any).progress = info.progress;
            }
            const bar = document.getElementById(`transfer-bar-${info.transferId}`);
            const pct = document.getElementById(`transfer-pct-${info.transferId}`);
            if (bar && pct) {
              bar.style.width = `${info.progress}%`;
              pct.textContent = `${info.progress}%`;
            } else {
              this.renderChatMessages();
            }
          }
        },
        onDataMessage: (data) => {
          if (data.type === 'hand-raise') {
            const p = this.participants.find(part => part.identity === data.identity);
            if (p) {
              p.isHandRaised = data.raised;
              this.renderVideoGrid();
              this.renderParticipantsList();
            }
          } else if (data.type === 'track_state') {
            const p = this.participants.find(part => part.identity === data.identity);
            if (p) {
              if (data.isCamOff !== undefined) {
                p.isCamOff = data.isCamOff;
                const tile = document.getElementById(`tile-${data.identity}`);
                if (tile) {
                  const vid = tile.querySelector('video.remote-cam-video') as HTMLElement | null;
                  const av = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
                  if (vid) vid.style.display = data.isCamOff ? 'none' : 'block';
                  if (av) av.style.display = data.isCamOff ? 'flex' : 'none';
                }
              }
              if (data.isMuted !== undefined) {
                p.isMuted = data.isMuted;
              }
              this.renderVideoGrid();
              this.renderParticipantsList();
            }
          }
        },
      },
    });

    await this.p2pMesh.start(localStream);
    this.renderVideoGrid();
    this.updateControlsState();
    this.updateSelfTile();
    if (this.manager.isMicEnabled) {
      this.startLocalAudioLevelMonitor();
    }
  }


  /**
   * Ensures a remote audio element exists and is properly attached for a participant.
   * Reuses existing elements to prevent audio glitches.
   */
  private ensureRemoteAudioElement(identity: string, track: any, pub: any): void {
    const isScreenAudio = pub?.source === Track.Source.ScreenShareAudio || track?.source === Track.Source.ScreenShareAudio;
    const elementId = isScreenAudio ? `audio-remote-${identity}-screen` : `audio-remote-${identity}`;
    let audioEl = document.getElementById(elementId) as HTMLAudioElement | null;
    if (!audioEl) {
      audioEl = document.createElement('audio');
      audioEl.id = elementId;
      audioEl.autoplay = true;
      audioEl.setAttribute('playsinline', '');
      audioEl.volume = 1.0;
      document.body.appendChild(audioEl);
    }

    // Route to user's selected speaker output device if supported
    const preferredSpeakerId = typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_selected_speaker') : null;
    if (preferredSpeakerId && preferredSpeakerId !== 'default' && typeof (audioEl as any).setSinkId === 'function') {
      (audioEl as any).setSinkId(preferredSpeakerId).catch(() => {});
    }

    // Always re-attach — track.attach is idempotent if already attached
    track.attach(audioEl);

    const isMuted = pub?.isMuted ?? false;
    audioEl.muted = isMuted;
    if (!isMuted) {
      audioEl.play().catch(async (e) => {
        console.warn('[LiveKit] Remote audio autoplay error:', e);
        try {
          await this.manager.getRoom()?.startAudio();
          await audioEl?.play();
        } catch (_) {
          this.handleAudioPlaybackBanner(true);
        }
      });
    }
  }

  private handleAudioPlaybackBanner(show: boolean) {
    let banner = document.getElementById('audio-unlock-banner');
    if (show) {
      if (!banner) {
        banner = document.createElement('div');
        banner.id = 'audio-unlock-banner';
        banner.className = 'audio-unlock-banner';
        banner.innerHTML = '<span>🔊</span> <span>Faceți clic pentru a activa sunetul participanților</span>';
        banner.onclick = async () => {
          try {
            await this.manager.getRoom()?.startAudio();
            banner?.remove();
            document.querySelectorAll('audio[id^="audio-remote-"]').forEach((a: any) => {
              a.muted = false;
              a.play().catch(() => {});
            });
          } catch (_) {}
        };
        document.body.appendChild(banner);
      }
    } else {
      if (banner) banner.remove();
    }
  }

  private updateChatBadge() {
    const chatBtn = document.getElementById('btn-open-chat');
    const mobileMoreBtn = document.getElementById('btn-more-mobile');
    const sheetChatBtn = document.getElementById('sheet-btn-chat');

    if (this.unreadChatCount > 0 && !this.isChatOpen) {
      const badgeText = this.unreadChatCount > 9 ? '9+' : String(this.unreadChatCount);

      // 1. Desktop Chat Button Badge
      if (chatBtn) {
        let badge = chatBtn.querySelector('.chat-unread-badge') as HTMLElement | null;
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'chat-unread-badge';
          chatBtn.appendChild(badge);
        }
        badge.textContent = badgeText;
      }

      // 2. Mobile More Button Badge (Pill notification on "⋯")
      if (mobileMoreBtn) {
        let badge = mobileMoreBtn.querySelector('.chat-unread-badge') as HTMLElement | null;
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'chat-unread-badge';
          mobileMoreBtn.appendChild(badge);
        }
        badge.textContent = badgeText;
      }

      // 3. Mobile Sheet Chat Item Badge
      if (sheetChatBtn) {
        let badge = sheetChatBtn.querySelector('.chat-unread-badge') as HTMLElement | null;
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'chat-unread-badge sheet-chat-badge';
          sheetChatBtn.appendChild(badge);
        }
        badge.textContent = badgeText;
      }
    } else {
      chatBtn?.querySelector('.chat-unread-badge')?.remove();
      mobileMoreBtn?.querySelector('.chat-unread-badge')?.remove();
      sheetChatBtn?.querySelector('.chat-unread-badge')?.remove();
    }
  }

  /**
   * Force full resync of all remote tracks after reconnection.
   * Re-attaches all audio elements and video elements from scratch.
   */
  private resyncAllRemoteTracks(): void {
    const room = this.manager.getRoom();
    if (!room || room.state !== 'connected') return;

    console.info('[LiveKit UI] resyncAllRemoteTracks: syncing', room.remoteParticipants.size, 'remote participants');

    room.remoteParticipants.forEach((rp) => {
      // Ensure participant is in our list
      this.addOrUpdateParticipant(rp.identity, rp.name || 'Participant');

      // Re-attach audio
      const audioPub = rp.getTrackPublication(Track.Source.Microphone);
      if (audioPub && audioPub.track && audioPub.isSubscribed) {
        this.ensureRemoteAudioElement(rp.identity, audioPub.track, audioPub);
      }
      const screenAudioPub = rp.getTrackPublication(Track.Source.ScreenShareAudio);
      if (screenAudioPub && screenAudioPub.track && screenAudioPub.isSubscribed) {
        this.ensureRemoteAudioElement(rp.identity, screenAudioPub.track, screenAudioPub);
      }

      // Re-attach video 
      const videoPub = rp.getTrackPublication(Track.Source.Camera);
      const tile = document.getElementById(`tile-${rp.identity}`);
      if (tile) {
        let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;
        const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;
        if (videoPub && videoPub.track && videoPub.isSubscribed && !videoPub.isMuted) {
          if (!videoEl) {
            videoEl = document.createElement('video');
            videoEl.className = 'remote-cam-video';
            videoEl.autoplay = true;
            videoEl.playsInline = true;
            videoEl.setAttribute('playsinline', '');
            videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:inherit; z-index:1;';
            tile.prepend(videoEl);
          }
          videoPub.track.attach(videoEl);
          videoEl.style.display = 'block';
          if (avatar) avatar.style.display = 'none';
        } else {
          if (videoEl) {
            videoEl.style.display = 'none';
            videoEl.srcObject = null;
          }
          if (avatar) avatar.style.display = 'flex';
        }
      }
    });

    // Try to unlock audio again
    try {
      room.startAudio().catch(() => {});
    } catch (_) {}
  }

  /**
   * Periodic health checker that runs every 3 seconds.
   * Detects dead/ended tracks and attempts automatic recovery.
   * Also ensures all remote audio elements are properly connected.
   */
  private startTrackHealthChecker(): void {
    if (this.trackHealthInterval) {
      clearInterval(this.trackHealthInterval);
    }

    this.trackHealthInterval = setInterval(() => {
      const room = this.manager.getRoom();
      if (!room || room.state !== 'connected') return;

      // Ensure all remote audio elements are alive and playing
      room.remoteParticipants.forEach((rp) => {
        const audioPub = rp.getTrackPublication(Track.Source.Microphone);
        if (audioPub && audioPub.track && audioPub.isSubscribed && !audioPub.isMuted) {
          const audioEl = document.getElementById(`audio-remote-${rp.identity}`) as HTMLAudioElement | null;
          if (!audioEl) {
            this.ensureRemoteAudioElement(rp.identity, audioPub.track, audioPub);
          } else if (audioEl.paused) {
            audioEl.play().catch(() => {});
          }
        }
        const screenAudioPub = rp.getTrackPublication(Track.Source.ScreenShareAudio);
        if (screenAudioPub && screenAudioPub.track && screenAudioPub.isSubscribed && !screenAudioPub.isMuted) {
          const screenAudioEl = document.getElementById(`audio-remote-${rp.identity}-screen`) as HTMLAudioElement | null;
          if (!screenAudioEl) {
            this.ensureRemoteAudioElement(rp.identity, screenAudioPub.track, screenAudioPub);
          } else if (screenAudioEl.paused) {
            screenAudioEl.play().catch(() => {});
          }
        }
      });
    }, 5000);
  }

  private addOrUpdateParticipant(identity: string, name: string) {
    let p = this.participants.find(part => part.identity === identity);
    if (!p) {
      p = { identity, name, isSpeaking: false, isMuted: false, isCamOff: true, role: 'user' };
      this.participants.push(p);
    } else {
      p.name = name;
    }

    if (identity === 'self') {
      p.role = this.role;
    }

    const room = this.manager.getRoom();
    if (room && room.state === 'connected' && identity !== 'self') {
      const rp = room.remoteParticipants.get(identity);
      if (rp) {
        try {
          if (rp.metadata) {
            const meta = typeof rp.metadata === 'string' ? JSON.parse(rp.metadata) : rp.metadata;
            if (meta.role) {
              const r = String(meta.role).toLowerCase();
              p.role = (r === 'admin' ? 'admin' : (r === 'moderator' ? 'moderator' : 'user'));
            }
          }
        } catch (_) {}
        const audioPub = rp.getTrackPublication(Track.Source.Microphone);
        if (audioPub) {
          p.isMuted = audioPub.isMuted || !audioPub.isSubscribed;
        }
        const videoPub = rp.getTrackPublication(Track.Source.Camera);
        if (videoPub) {
          p.isCamOff = videoPub.isMuted || !videoPub.isSubscribed;
        } else {
          p.isCamOff = true;
        }
      }
    }
  }

  private startTimer() {
    this.elapsedSeconds = 0;
    this.timerInterval = setInterval(() => {
      this.elapsedSeconds++;
      const mins = String(Math.floor(this.elapsedSeconds / 60)).padStart(2, '0');
      const secs = String(this.elapsedSeconds % 60).padStart(2, '0');
      const timerEl = document.getElementById('conf-clock');
      if (timerEl) {
        timerEl.innerText = `${mins}:${secs}`;
      }
    }, 1000);
  }

  private renderLayout() {
    let el = document.getElementById('conference-container');
    if (!el) {
      el = document.createElement('div');
      el.id = 'conference-container';
      document.body.appendChild(el);
    }
    if (typeof document !== 'undefined') {
      document.body.classList.add('in-conference');
      document.querySelectorAll('.toast-notification').forEach(el => el.remove());
    }
    this.container = el;
    const cleanRoomTitle = (this.config.roomName || 'Sală Conferință').replace(/\s*\(VPS[^)]*\)/gi, '').trim();
    const isMobileClient = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const clientAppTitle = (window as any).vorbelloDesktop 
      ? `Vorbello Desktop v${APP_VERSION}` 
      : (isMobileClient ? `Vorbello Mobile v${APP_VERSION}` : `Vorbello Web v${APP_VERSION}`);

    this.container.innerHTML = `
      <div class="conference-screen">
        <!-- Header -->
        <header class="conference-header">
          <div class="conf-brand-group" style="display: flex; align-items: center; gap: 0.75rem; min-width: 0; flex-shrink: 1;">
            <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" style="width: 44px; height: 44px; object-fit: contain; flex-shrink: 0; filter: drop-shadow(0 0 12px rgba(56, 189, 248, 0.65));" />
            <div style="display: flex; align-items: center; gap: 0.65rem; min-width: 0;">
              <span class="conf-room-title">${cleanRoomTitle}</span>
              ${this.canInvite ? `
              <button id="btn-header-quick-invite" class="conf-invite-quick-pill" title="Distribuie linkul conferinței" style="margin: 0; white-space: nowrap; flex-shrink: 0;">
                <span>🔗</span> Distribuie
              </button>
              ` : ''}
            </div>
          </div>

          <div class="conf-header-right" style="display: flex; align-items: center; gap: 0.45rem; flex-shrink: 0; flex-wrap: nowrap;">
            <!-- Zoom-Style View Switcher Button (Desktop: in header; Mobile: in sheet) -->
            <button id="btn-toggle-view-mode" title="Comută modul de afișare: Galerie sau Vorbitor Activ" style="display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; color: #f1f5f9; font-size: 0.75rem; font-weight: 600; cursor: pointer; transition: all 0.2s ease;">
              <span id="view-mode-icon">⊞</span>
              <span id="view-mode-text">Galerie</span>
            </button>

            <div class="conf-rec-badge" id="conf-rec-badge" style="display: none;" title="Apasă pentru a opri înregistrarea">
              <span class="rec-dot"></span>
              <span class="rec-label">REC</span>
              <span class="rec-timer" id="rec-timer">00:00</span>
              <span class="rec-stop-badge-hint" id="rec-stop-badge-hint">⏹️ Oprește</span>
            </div>

            <div class="conf-timer">
              <span>⏱️</span> <span id="conf-clock">00:00</span>
            </div>
            <div class="conf-quality-pill" id="conf-status-pill" title="Apasă pentru Telemetrie Rețea & Diagnostic LiveKit SFU" style="cursor: pointer;">
              <span class="conf-quality-dot"></span> Conectat HD
            </div>
            <span id="btn-header-version" title="Panou Versiune Vorbello & Diagnostic Sistem" style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; padding: 3px 9px; border-radius: 8px; font-weight: 700; font-size: 0.72rem; border: 1px solid rgba(56, 189, 248, 0.35); cursor: pointer; transition: all 0.2s; white-space: nowrap;">v${APP_VERSION}</span>
          </div>
        </header>

        <!-- Live Network Telemetry Popover (Secure & Role-Aware) -->
        <div class="conf-telemetry-popover" id="network-telemetry-popover" style="display: none;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 0.5rem;">
            <div style="display: flex; align-items: center; gap: 0.4rem; font-weight: 700; color: #fff; font-size: 0.85rem;">
              <span>🛡️</span> ${this.isAdmin ? 'Diagnostic Sistem & Server (Admin)' : 'Stare Conexiune & Calitate Audio-Video'}
            </div>
            <button id="btn-close-telemetry" style="background: none; border: none; color: #94a3b8; font-size: 1.25rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>
          ${this.isAdmin ? `
            <div class="telemetry-row">
              <span class="telemetry-label">Server VPS LiveKit:</span>
              <span class="telemetry-val" id="telemetry-vps-url" style="color: #38bdf8;">${this.config.livekitUrl || '217.217.233.20:7880'}</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Bitrate Emisie (Upload):</span>
              <span class="telemetry-val" id="telemetry-upload-bitrate" style="color: #38bdf8; font-family: monospace; font-weight: 700;">-- kbps</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Bitrate Recepție (Download):</span>
              <span class="telemetry-val" id="telemetry-download-bitrate" style="color: #38bdf8; font-family: monospace; font-weight: 700;">-- kbps</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Pachete Pierdute:</span>
              <span class="telemetry-val" id="telemetry-loss-val" style="color: #34d399; font-weight: 600;">0.0%</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Limitare Calitate Rețea:</span>
              <span class="telemetry-val" id="telemetry-quality-limit" style="color: #34d399; font-size: 0.75rem;">Fără limitări</span>
            </div>
          ` : ''}
          <div class="telemetry-row">
            <span class="telemetry-label">Stare Conexiune:</span>
            <span class="telemetry-val" id="telemetry-conn-state" style="color: #10b981;">● Conectat Securizat HD</span>
          </div>
          <div class="telemetry-row">
            <span class="telemetry-label">Latență RTT (Ping):</span>
            <span class="telemetry-val" id="telemetry-rtt-val" style="color: #34d399;">< 20 ms (Excelent)</span>
          </div>
          <div class="telemetry-row">
            <span class="telemetry-label">${this.isAdmin ? 'Flux Cameră Video:' : 'Calitate Imagine:'}</span>
            <span class="telemetry-val" id="telemetry-cam-val" style="color: #34d399;">1280x720 HD (Flux Local Activ)</span>
          </div>
          ${this.isAdmin ? `
            <div class="telemetry-row">
              <span class="telemetry-label">Securitate & Criptare:</span>
              <span class="telemetry-val" style="color: #6ee7b7;">End-to-End DTLS-SRTP 256-bit</span>
            </div>
            <div class="telemetry-row">
              <span class="telemetry-label">Codec Audio / Video:</span>
              <span class="telemetry-val">Opus 48kHz HD / VP8</span>
            </div>
          ` : ''}

          ${this.isAdmin ? `
            <div id="telemetry-actions-box" style="margin-top: 0.65rem; padding-top: 0.65rem; border-top: 1px solid rgba(255, 255, 255, 0.08); display: flex; flex-direction: column; gap: 0.45rem;">
              <div id="telemetry-status-help" style="font-size: 0.72rem; color: #cbd5e1; line-height: 1.4;">
                Panou tehnic de control administrator.
              </div>
              <div style="display: flex; gap: 0.45rem; flex-wrap: wrap;">
                <button id="btn-telemetry-retry" class="btn btn-sm" style="flex: 1; min-width: 110px; font-size: 0.75rem; font-weight: 600; padding: 6px 10px; justify-content: center; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.4); color: #38bdf8; border-radius: 8px; cursor: pointer;">
                  🔄 Reîncearcă VPS
                </button>
                <button id="btn-telemetry-demo" class="btn btn-sm" style="flex: 1; min-width: 110px; font-size: 0.75rem; font-weight: 600; padding: 6px 10px; justify-content: center; background: rgba(168, 85, 247, 0.15); border: 1px solid rgba(168, 85, 247, 0.4); color: #c084fc; border-radius: 8px; cursor: pointer;">
                  🧪 Mod Test / Demo
                </button>
              </div>
            </div>
          ` : `
            <div style="margin-top: 0.5rem; padding-top: 0.5rem; border-top: 1px solid rgba(255, 255, 255, 0.08); font-size: 0.72rem; color: #94a3b8; text-align: center;">
              🔒 Comunicație privată securizată pe platforma Vorbello.
            </div>
          `}
        </div>

        <!-- Body Area -->
        <div class="conference-body">
          <main class="stage-area" id="stage-area">
            <!-- Top Filmstrip Carousel Preview Bar (Zoom / Meet Style: active when count > 4 on mobile/tablet or > 6 on desktop) -->
            <div class="top-filmstrip-bar" id="top-filmstrip-bar" style="display: none;">
              <div class="filmstrip-scroll-container" id="filmstrip-scroll-container"></div>
            </div>

            <!-- Dedicated Spotlight Container for Screen Sharing / Pinned Speaker -->
            <div class="spotlight-container" id="spotlight-container" style="display: none;">
              <div class="spotlight-header">
                <div class="spotlight-title">
                  <span class="conf-quality-dot"></span>
                  <span id="spotlight-title-text">Partajare Activă (HD Crisp)</span>
                </div>
                <div style="display: flex; align-items: center; gap: 6px;">
                  <button type="button" class="btn btn-secondary btn-sm" id="btn-toggle-spotlight-size" title="Atingeți sau faceți click pentru a mări ecranul partajat (Ecran Complet / Zoom)">
                    <span>🔍</span> <span id="label-spotlight-size">Mărește</span>
                  </button>
                  <button type="button" class="btn btn-secondary btn-sm" id="btn-switch-spotlight-source" style="display: none; padding: 4px 10px; font-size: 0.75rem;" title="Schimbă fereastra sau ecranul fără să oprești partajarea">
                    <span>🔄</span> Schimbă Sursa
                  </button>
                  <button class="btn btn-danger btn-sm" id="btn-stop-spotlight" style="padding: 4px 10px; font-size: 0.75rem;">
                    Oprește Partajarea
                  </button>
                </div>
              </div>
              <div class="spotlight-viewport" id="spotlight-viewport" title="Click sau atingeți pentru a comuta ecranul complet / zoom">
                <video id="spotlight-video" autoplay playsinline muted></video>
                <div id="spotlight-pinned-card" style="display: none;" class="spotlight-pinned-card">
                  <div class="spotlight-avatar-circle" id="spotlight-avatar-char">M</div>
                  <div class="spotlight-pinned-name" id="spotlight-pinned-name">Mihai Director (Tu)</div>
                  <div class="spotlight-pinned-badge">📌 Participant Fixat pe Scenă</div>
                </div>
              </div>
            </div>

            <!-- Collaborative Whiteboard Container (Full Stage Live Sync) -->
            <div id="whiteboard-container" class="whiteboard-overlay-container" style="display: none;">
              <div class="whiteboard-toolbar">
                <div class="wb-item-title">
                  <span class="wb-toolbar-title">
                    <span>🎨</span> <span>Tablă Albă Live</span>
                  </span>
                </div>
                <div class="wb-item-tools">
                  <button type="button" class="wb-tool-btn active" data-tool="pen" title="Creion / Desen Liber">✏️ <span class="wb-btn-label">Creion</span></button>
                  <button type="button" class="wb-tool-btn" data-tool="eraser" title="Radieră">🧽 <span class="wb-btn-label">Radieră</span></button>
                </div>
                <div class="wb-item-colors">
                  <div class="wb-color-palette">
                    <button type="button" class="wb-color-btn" style="background: #ffffff;" data-color="#ffffff" title="Alb"></button>
                    <button type="button" class="wb-color-btn active" style="background: #38bdf8;" data-color="#38bdf8" title="Sky Blue"></button>
                    <button type="button" class="wb-color-btn" style="background: #ef4444;" data-color="#ef4444" title="Roșu"></button>
                    <button type="button" class="wb-color-btn" style="background: #10b981;" data-color="#10b981" title="Verde"></button>
                    <button type="button" class="wb-color-btn" style="background: #f59e0b;" data-color="#f59e0b" title="Galben"></button>
                    <button type="button" class="wb-color-btn" style="background: #a855f7;" data-color="#a855f7" title="Mov"></button>
                  </div>
                </div>
                <div class="wb-item-actions">
                  <button type="button" class="wb-tool-btn" id="btn-wb-clear" title="Curăță toată tabla">🗑️ <span class="wb-btn-label">Șterge</span></button>
                  <button type="button" class="wb-tool-btn" id="btn-wb-download" title="Descarcă ca imagine PNG">💾 <span class="wb-btn-label">Salvează</span></button>
                  <button type="button" class="btn btn-danger btn-sm" id="btn-wb-close" title="Închide Tabla">✖️ <span class="wb-btn-label">Închide</span></button>
                </div>
              </div>
              <div class="whiteboard-canvas-wrapper" id="wb-canvas-wrapper">
                <canvas id="whiteboard-canvas"></canvas>
              </div>
            </div>

            <!-- Participant Video Grid -->
            <div class="video-grid" id="video-grid"></div>
          </main>

          <!-- Side Panel (Chat & Participants & Polls) -->
          <aside class="conf-side-panel" id="side-panel" style="display: none;">
            <div class="panel-header">
              <h3 class="panel-title" id="panel-title">Chat Cameră</h3>
              <button id="btn-close-panel" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 1.25rem;">&times;</button>
            </div>
            
            <!-- Chat View -->
            <div id="chat-container" style="display: flex; flex-direction: column; height: 100%;">
              <div class="chat-history" id="chat-history"></div>
              <div id="chat-file-preview-strip" style="display: none; padding: 6px 12px; background: rgba(56, 189, 248, 0.12); border-top: 1px solid rgba(56, 189, 248, 0.3); font-size: 0.78rem; align-items: center; justify-content: space-between;">
                <div style="display: flex; align-items: center; gap: 6px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #38bdf8;">
                  <span>📎</span>
                  <span id="chat-file-preview-name" style="font-weight: 600; overflow: hidden; text-overflow: ellipsis; max-width: 170px;"></span>
                  <span id="chat-file-preview-size" style="opacity: 0.8; font-size: 0.72rem;"></span>
                </div>
                <button type="button" id="btn-cancel-file-preview" style="background: none; border: none; color: #f87171; cursor: pointer; font-size: 1.1rem; padding: 0 4px; line-height: 1;" title="Anulează atașamentul">&times;</button>
              </div>
              <div class="chat-input-row" style="display: flex; align-items: center; gap: 6px; width: 100%; box-sizing: border-box; overflow: hidden;">
                <input type="file" id="chat-file-input" style="display: none;" />
                <button type="button" class="chat-attach-btn" id="btn-attach-file" title="Atașează documente, PDF sau fișiere curs (max 25MB)">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l7.88-7.88"/>
                  </svg>
                </button>
                <input type="text" class="chat-input" id="chat-text-input" placeholder="Scrie un mesaj..." style="flex: 1 1 0%; min-width: 0;" />
                <button class="btn btn-primary btn-sm" id="btn-send-chat" style="flex-shrink: 0; white-space: nowrap; padding: 0.45rem 0.8rem;">Trimite</button>
              </div>
            </div>

            <!-- Participants View -->
            <div id="participants-container" style="display: none; flex-direction: column; height: 100%; overflow-y: auto;">
              ${this.canInvite ? `
              <!-- 🔗 Dedicated Invite / Share Card inside Participants (Admin & Host Only) -->
              <div class="invite-participant-card" id="invite-participant-card">
                <div class="invite-header">
                  <div class="invite-title"><span>🔗</span> Invită în Cameră</div>
                  <span class="invite-room-tag">${this.config.roomSlug}</span>
                </div>
                <div class="invite-input-row">
                  <input type="text" class="invite-input-field" id="input-invite-room-url" readonly value="" placeholder="Se generează linkul..." />
                  <button class="btn btn-primary btn-sm" id="btn-copy-invite-link" style="white-space:nowrap; padding: 0.45rem 0.75rem; font-size: 0.75rem;">📋 Copiază</button>
                </div>
                <div style="display: flex; gap: 0.4rem; margin-top: 2px;">
                  <button class="btn btn-secondary btn-sm" id="btn-copy-invite-text" style="flex: 1; font-size: 0.72rem; padding: 0.35rem 0.6rem; display: flex; align-items: center; justify-content: center; gap: 4px;">
                    📨 Copiază Text Invitație
                  </button>
                </div>
              </div>
              ` : ''}

              ${this.isAdmin ? `
              <!-- Global Moderation Actions (Strictly Admin Only) -->
              <div class="participants-global-actions">
                <button class="btn btn-secondary btn-sm" id="btn-mute-all" title="Oprește sau pornește microfonul tuturor participanților">
                  🔇 Mute Toți
                </button>
                <button class="btn btn-secondary btn-sm" id="btn-stop-all-cam" title="Oprește sau pornește camera tuturor participanților">
                  📷 Stop Camere
                </button>
                <button class="btn btn-secondary btn-sm" id="btn-lower-all-hands" title="Coboară toate mâinile ridicate din sală (Zoom / Meet)">
                  ✋ Mâini Jos
                </button>
              </div>
              <div style="font-size: 0.7rem; color: var(--text-muted); padding: 0.25rem 1rem 0.5rem 1rem; border-bottom: 1px solid rgba(255, 255, 255, 0.05);">
                * Acțiunile globale afectează participanții din sală.
              </div>
              ` : ''}

              <!-- 🔍 Dynamic Search & Filter Box for Participants (Zoom & Google Meet Style) -->
              <div class="participants-search-box">
                <div style="position: relative; display: flex; align-items: center; width: 100%;">
                  <span style="position: absolute; left: 10px; font-size: 0.82rem; color: #94a3b8; pointer-events: none;">🔍</span>
                  <input type="text" id="input-search-participants" placeholder="Caută participant..." autocomplete="off" style="width: 100%; box-sizing: border-box; padding: 7px 30px 7px 32px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.14); border-radius: 10px; color: #fff; font-size: 0.8rem; outline: none; transition: border-color 0.2s ease;" />
                  <button type="button" id="btn-clear-search-participants" style="display: none; position: absolute; right: 8px; background: none; border: none; color: #94a3b8; cursor: pointer; font-size: 1.1rem; line-height: 1; padding: 2px 4px;" title="Șterge căutarea">&times;</button>
                </div>
              </div>

              <div id="participants-list"></div>
            </div>

            <!-- Live Polls View in Side Panel -->
            <div id="polls-container" class="polls-drawer-container" style="display: none;">
              <div style="display: flex; justify-content: space-between; align-items: center; padding-bottom: 0.5rem; border-bottom: 1px solid rgba(255,255,255,0.08);">
                <div style="font-weight: 700; font-size: 0.9rem; color: #fff; display: flex; align-items: center; gap: 6px;">
                  <span>📊</span> Sondaje Live
                </div>
                ${(this.isAdmin || this.isModerator) ? `
                <button class="btn btn-primary btn-sm" id="btn-create-poll-open" style="font-size: 0.72rem; padding: 4px 10px;">
                  ➕ Creează Sondaj
                </button>
                ` : ''}
              </div>
              <div id="polls-list-content" style="display: flex; flex-direction: column; gap: 0.75rem;"></div>
            </div>
          </aside>
        </div>

        <!-- Floating Ultra-Premium Ergonomic Controls Dock (Essential Always Visible + On-Click Tools) -->
        <footer class="conference-controls">
          <div class="controls-dock-bar" id="controls-dock-bar">
            <!-- 1. Microfon (Always Visible) -->
            <button class="control-btn ${this.manager.isMicEnabled ? 'active-on' : 'active-off'}" id="btn-toggle-mic" title="Microfon (Mute/Unmute)">
              <span class="btn-icon">${this.manager.isMicEnabled ? ICONS.micOn : ICONS.micOff}</span>
              <span class="btn-label" id="label-mic">${this.manager.isMicEnabled ? 'Microfon' : 'Oprit'}</span>
            </button>

            <!-- 2. Cameră (Always Visible) -->
            <button class="control-btn ${this.manager.isCameraEnabled ? 'active-on' : 'active-off'}" id="btn-toggle-cam" title="Cameră Video (Pornit/Oprit)">
              <span class="btn-icon">${this.manager.isCameraEnabled ? ICONS.camOn : ICONS.camOff}</span>
              <span class="btn-label" id="label-cam">${this.manager.isCameraEnabled ? 'Cameră' : 'Oprită'}</span>
            </button>

            <!-- 3. Partajare Ecran (Always Visible + Hot-Swap Menu) -->
            <div class="screen-share-wrapper" id="screen-share-wrapper">
              <button class="control-btn ${this.manager.isScreenShareEnabled ? 'active-screen' : ''}" id="btn-toggle-screen" title="Partajează Ecranul (Prezentare)">
                <span class="btn-icon">${ICONS.screen}</span>
                <span class="btn-label" id="label-screen">${this.manager.isScreenShareEnabled ? 'Oprește' : 'Partajare'}</span>
              </button>
              <div class="screen-share-active-popover" id="screen-share-active-menu" style="display: none;">
                <button type="button" class="screen-popover-item" id="btn-menu-switch-source">
                  <span class="screen-popover-icon">🔄</span>
                  <div class="screen-popover-text">
                    <strong style="display: block; font-size: 0.8rem; color: #f8fafc;">Schimbă Sursa</strong>
                    <small style="display: block; font-size: 0.68rem; color: #94a3b8;">Alege altă fereastră sau ecran din mers</small>
                  </div>
                </button>
                <button type="button" class="screen-popover-item danger" id="btn-menu-stop-share">
                  <span class="screen-popover-icon">⏹️</span>
                  <div class="screen-popover-text">
                    <strong style="display: block; font-size: 0.8rem; color: #fca5a5;">Oprește Partajarea</strong>
                    <small style="display: block; font-size: 0.68rem; color: #cbd5e1;">Revenire la camera video</small>
                  </div>
                </button>
              </div>
            </div>

            <!-- 4. Chat (Always Visible) -->
            <button class="control-btn" id="btn-open-chat" title="Chat Conferință">
              <span class="btn-icon">${ICONS.chat}</span>
              <span class="btn-label">Chat</span>
              <span class="chat-unread-badge" id="chat-unread-badge" style="display: none;">0</span>
            </button>

            <!-- 5. Participanți (Always Visible) -->
            <button class="control-btn" id="btn-open-participants" title="Listă Participanți">
              <span class="btn-icon">${ICONS.users}</span>
              <span class="btn-label" id="label-participants">Participanți (${this.participants.length})</span>
            </button>

            <!-- Mobile Flip Camera (Front / Rear) - Only visible on mobile touch devices -->
            <button class="control-btn btn-flip-cam-mobile" id="btn-flip-cam" title="Comută Camera (Față / Spate)">
              <span class="btn-icon">🔄</span>
            </button>

            <!-- 6. More Options Popover Trigger ("Celelalte opțiuni la click") -->
            <div class="more-options-wrapper" id="more-options-wrapper">
              <button class="control-btn btn-more-options ${this.isLocalHandRaised ? 'has-active-tool' : ''}" id="btn-more-options" title="Mai multe instrumente și opțiuni">
                <span class="btn-icon">⋯</span>
                <span class="btn-label">Mai multe</span>
                ${this.isLocalHandRaised ? '<span class="dock-hand-dot" id="dock-hand-indicator" title="Mâna este ridicată">✋</span>' : '<span class="dock-hand-dot" id="dock-hand-indicator" style="display: none;" title="Mâna este ridicată">✋</span>'}
              </button>

              <!-- Floating Popover for Secondary / Advanced Tools on Desktop -->
              <div class="more-options-popover" id="more-options-popover" style="display: none;">
                <div class="popover-header">Instrumente & Setări</div>
                
                <div class="popover-grid">
                  <!-- Whiteboard Button -->
                  <button class="popover-item-btn ${this.isWhiteboardOpen ? 'active' : ''}" id="btn-toggle-whiteboard" title="Tablă Colaborativă Interactivă">
                    <span class="popover-item-icon">🎨</span>
                    <span class="popover-item-label" id="label-whiteboard">${this.isWhiteboardOpen ? 'Închide Tablă' : 'Tablă Albă'}</span>
                  </button>

                  <!-- Live Polls Button -->
                  <button class="popover-item-btn" id="btn-open-polls" title="Sondaje Live în Conferință">
                    <span class="popover-item-icon">📊</span>
                    <span class="popover-item-label">Sondaj</span>
                  </button>

                  <!-- Ridică Mâna -->
                  <button class="popover-item-btn ${this.isLocalHandRaised ? 'active-hand' : ''}" id="btn-raise-hand" title="Ridică sau coboară mâna">
                    <span class="popover-item-icon">✋</span>
                    <span class="popover-item-label" id="label-hand">${this.isLocalHandRaised ? 'Coboară Mâna' : 'Ridică Mâna'}</span>
                  </button>

                  <!-- Reacții Live Button -->
                  <button class="popover-item-btn" id="btn-open-reactions" title="Reacții Rapide Live">
                    <span class="popover-item-icon">🎉</span>
                    <span class="popover-item-label">Reacții</span>
                  </button>

                  <!-- Dispozitive & Setări -->
                  <button class="popover-item-btn" id="btn-open-devices" title="Setări Audio & Video">
                    <span class="popover-item-icon">⚙️</span>
                    <span class="popover-item-label">Dispozitive</span>
                  </button>

                  ${(this.isAdmin || this.isModerator) ? `
                  <!-- Network & Realtime Transfer Monitor (Admin) -->
                  <button class="popover-item-btn" id="btn-open-telemetry-monitor" title="Monitor Date & Bitrate în Timp Real">
                    <span class="popover-item-icon">📊</span>
                    <span class="popover-item-label">Monitor Rețea</span>
                  </button>
                  ` : ''}

                  ${this.isHost ? `
                  <!-- Record Button (Host Only) -->
                  <button class="popover-item-btn" id="btn-toggle-record" title="Înregistrare Conferință HD">
                    <span class="popover-item-icon">⏺️</span>
                    <span class="popover-item-label" id="label-record">Înregistrează</span>
                  </button>
                  ` : ''}
                </div>

                <!-- Live Quick Reactions Emoji Bar directly inside the popover! -->
                <div class="popover-reactions-bar">
                  <span class="popover-reactions-title">Trimite Reacție Rapidă:</span>
                  <div class="popover-emojis-row">
                    <button class="btn-reaction-emoji" data-emoji="👏" title="Aplauze">👏</button>
                    <button class="btn-reaction-emoji" data-emoji="👍" title="Bravo">👍</button>
                    <button class="btn-reaction-emoji" data-emoji="❤️" title="Inimă">❤️</button>
                    <button class="btn-reaction-emoji" data-emoji="🎉" title="Felicitări">🎉</button>
                    <button class="btn-reaction-emoji" data-emoji="😂" title="Amuzant">😂</button>
                    <button class="btn-reaction-emoji" data-emoji="😮" title="Uimire">😮</button>
                    <button class="btn-reaction-emoji" data-emoji="🔥" title="Foc">🔥</button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 7. Părăsește Conferința (Always Visible) -->
            <button class="control-btn leave" id="btn-leave-conf" title="Părăsește sala de conferință">
              <span class="btn-icon">${ICONS.leave}</span>
              <span class="btn-label">Părăsește</span>
            </button>
          </div>
        </footer>

        <!-- Mobile Actions Sheet (Zoom Style) -->
        <div class="mobile-more-sheet-overlay" id="mobile-more-sheet-overlay" style="display: none;">
          <div class="mobile-more-sheet" id="mobile-more-sheet">
            <div class="sheet-drag-handle"></div>
            <div class="sheet-header">
              <div class="sheet-title">Opțiuni Sală Conferință</div>
              <button class="sheet-close-btn" id="btn-close-more-sheet">&times;</button>
            </div>

            <!-- Zoom-Style Quick Reactions Bar directly in the sheet -->
            <div class="sheet-quick-reactions-section">
              <div class="sheet-section-title">Trimite Reacție Rapidă:</div>
              <div class="sheet-quick-emojis-row">
                <button class="sheet-quick-emoji" data-emoji="👏" title="Aplauze">👏</button>
                <button class="sheet-quick-emoji" data-emoji="👍" title="Bravo">👍</button>
                <button class="sheet-quick-emoji" data-emoji="❤️" title="Inimă">❤️</button>
                <button class="sheet-quick-emoji" data-emoji="🎉" title="Felicitări">🎉</button>
                <button class="sheet-quick-emoji" data-emoji="😂" title="Amuzant">😂</button>
                <button class="sheet-quick-emoji" data-emoji="😮" title="Uimire">😮</button>
                <button class="sheet-quick-emoji" data-emoji="🔥" title="Foc">🔥</button>
                <button class="sheet-quick-emoji" data-emoji="🚀" title="Rachetă">🚀</button>
              </div>
            </div>

            <div class="sheet-grid">
              <button class="sheet-item-btn" id="sheet-btn-participants">
                <div class="sheet-icon-circle">👥</div>
                <span class="sheet-item-label">Participanți (<span id="sheet-part-count">${this.participants.length}</span>)</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-chat">
                <div class="sheet-icon-circle">💬</div>
                <span class="sheet-item-label">Chat</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-flip-cam">
                <div class="sheet-icon-circle">🔄</div>
                <span class="sheet-item-label">Comută Camera</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-raise-hand">
                <div class="sheet-icon-circle">✋</div>
                <span class="sheet-item-label">${this.isLocalHandRaised ? 'Coboară Mâna' : 'Ridică Mâna'}</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-reactions">
                <div class="sheet-icon-circle">🎉</div>
                <span class="sheet-item-label">Reacții</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-whiteboard">
                <div class="sheet-icon-circle">🎨</div>
                <span class="sheet-item-label">Tablă Albă</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-polls">
                <div class="sheet-icon-circle">📊</div>
                <span class="sheet-item-label">Sondaje</span>
              </button>
              <button class="sheet-item-btn" id="sheet-btn-screen">
                <div class="sheet-icon-circle">💻</div>
                <span class="sheet-item-label">Partajează</span>
              </button>
              ${this.isHost ? `
              <button class="sheet-item-btn" id="sheet-btn-record">
                <div class="sheet-icon-circle">⏺️</div>
                <span class="sheet-item-label">Înregistrare</span>
              </button>
              ` : ''}
              <button class="sheet-item-btn" id="sheet-btn-devices">
                <div class="sheet-icon-circle">⚙️</div>
                <span class="sheet-item-label">Setări</span>
              </button>
              ${(this.isAdmin || this.isModerator) ? `
              <button class="sheet-item-btn" id="sheet-btn-telemetry-monitor">
                <div class="sheet-icon-circle">📊</div>
                <span class="sheet-item-label">Monitor Rețea</span>
              </button>
              ` : ''}
              <button class="sheet-item-btn" id="sheet-btn-view-mode">
                <div class="sheet-icon-circle" id="sheet-icon-view-mode">⊞</div>
                <span class="sheet-item-label" id="sheet-label-view-mode">Mod Galerie / Vorbitor</span>
              </button>
              ${this.canInvite ? `
              <button class="sheet-item-btn sheet-item-invite" id="sheet-btn-invite">
                <div class="sheet-icon-circle" style="background: rgba(99, 102, 241, 0.25); color: #818cf8;">🔗</div>
                <span class="sheet-item-label" style="color: #818cf8; font-weight: 600;">Invită în Cameră (Copiază Link)</span>
              </button>
              ` : ''}
            </div>
          </div>
        </div>

        <!-- Mobile Floating Reactions Dock (triggered by Reacții button) -->
        <div class="mobile-floating-reactions-dock" id="mobile-floating-reactions-dock" style="display: none;">
          <div class="dock-emojis-row">
            <button class="sheet-quick-emoji" data-emoji="👏" title="Aplauze">👏</button>
            <button class="sheet-quick-emoji" data-emoji="👍" title="Bravo">👍</button>
            <button class="sheet-quick-emoji" data-emoji="❤️" title="Inimă">❤️</button>
            <button class="sheet-quick-emoji" data-emoji="🎉" title="Felicitări">🎉</button>
            <button class="sheet-quick-emoji" data-emoji="😂" title="Amuzant">😂</button>
            <button class="sheet-quick-emoji" data-emoji="😮" title="Uimire">😮</button>
            <button class="sheet-quick-emoji" data-emoji="🔥" title="Foc">🔥</button>
            <button class="sheet-quick-emoji" data-emoji="🚀" title="Rachetă">🚀</button>
          </div>
          <button class="dock-close-btn" id="btn-close-reactions-dock" title="Închide">&times;</button>
        </div>

        <!-- Floating Reactions Stream Container (Zoom / Teams Style) -->
        <div class="floating-reactions-stream" id="floating-reactions-stream"></div>

        <!-- Floating Live Poll Popup (When poll is active and drawer is closed) -->
        <div id="live-poll-popup" class="live-poll-card-float" style="display: none;"></div>
      </div>

      <!-- Create Poll Modal -->
      <div class="picker-modal-overlay" id="create-poll-modal" style="display: none; align-items: center; justify-content: center; z-index: 10005;">
        <div style="background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 18px; padding: 1.5rem; width: 440px; max-width: 90vw; display: flex; flex-direction: column; gap: 1rem; box-shadow: 0 20px 50px rgba(0,0,0,0.8);">
          <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.08); padding-bottom: 0.5rem;">
            <div style="font-size: 1rem; font-weight: 700; color: #fff; display: flex; align-items: center; gap: 6px;">
              <span>📊</span> Creează Sondaj Live în Sală
            </div>
            <button id="btn-close-create-poll" style="background: none; border: none; color: #94a3b8; font-size: 1.3rem; cursor: pointer;">&times;</button>
          </div>
          <div>
            <label style="font-size: 0.75rem; color: #cbd5e1; font-weight: 600; display: block; margin-bottom: 4px;">Întrebare:</label>
            <input type="text" id="input-poll-question" class="invite-input-field" style="width: 100%; font-family: inherit;" placeholder="Ex: Sunteți de acord cu planul de lansare?" />
          </div>
          <div>
            <label style="font-size: 0.75rem; color: #cbd5e1; font-weight: 600; display: block; margin-bottom: 4px;">Opțiuni de răspuns:</label>
            <div id="poll-options-inputs-list" style="display: flex; flex-direction: column; gap: 6px;">
              <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 1 (ex: Da, perfect)" />
              <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 2 (ex: Nu, necesită revizuire)" />
            </div>
            <button type="button" id="btn-add-poll-option" class="btn btn-secondary btn-sm" style="margin-top: 6px; font-size: 0.72rem; padding: 3px 8px;">
              ➕ Adaugă altă opțiune
            </button>
          </div>
          <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.5rem;">
            <button type="button" id="btn-cancel-create-poll" class="btn btn-secondary btn-sm">Anulează</button>
            <button type="button" id="btn-submit-launch-poll" class="btn btn-primary btn-sm">🚀 Lansează Sondaj</button>
          </div>
        </div>
      </div>

      <!-- Device Settings Modal (Microphone, Camera, Speaker Selection & VU Meter) -->
      <div class="picker-modal-overlay" id="device-settings-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div class="conf-modal-scrollable-card" style="background: rgba(14, 19, 32, 0.96); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem; width: 500px; max-width: 92vw; max-height: 88vh; max-height: 88dvh; overflow-y: auto; -webkit-overflow-scrolling: touch; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85); display: flex; flex-direction: column; gap: 1.25rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.75rem;">
            <div style="display: flex; align-items: center; gap: 0.6rem;">
              <div style="font-size: 1.4rem;">⚙️</div>
              <div>
                <h3 style="margin: 0; font-size: 1.15rem; font-weight: 700; color: #fff;">Setări Dispozitive Audio & Video</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Selectați microfonul, camera și difuzoarele active</div>
              </div>
            </div>
            <button id="btn-close-device-settings" style="background: none; border: none; color: #94a3b8; font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>

          <!-- Version Banner in Device Settings -->
          <div style="display: flex; align-items: center; justify-content: space-between; background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.25); border-radius: 12px; padding: 0.65rem 1rem;">
            <div style="display: flex; align-items: center; gap: 0.6rem;">
              <span style="font-size: 1.2rem;">🚀</span>
              <div>
                <div style="font-size: 0.85rem; font-weight: 700; color: #38bdf8;">${clientAppTitle}</div>
                <div style="font-size: 0.7rem; color: var(--text-muted);">${APP_BUILD_TAG} • Build ${APP_BUILD_DATE}</div>
              </div>
            </div>
            <span style="font-size: 0.7rem; background: #10b981; color: #fff; padding: 2px 8px; border-radius: 12px; font-weight: 700;">STABIL</span>
          </div>

          <!-- Microphone Selection & Live VU Meter -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🎤 Selectează Microfonul</span>
              <span id="vu-meter-label" style="font-size: 0.75rem; color: #6ee7b7; font-weight: 600;">Semnal audio live</span>
            </label>
            <select class="form-select" id="conf-select-mic" style="width: 100%; margin-bottom: 0.6rem;"></select>
            
            <!-- Live VU Meter Bar -->
            <div style="background: rgba(0, 0, 0, 0.5); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px; height: 14px; overflow: hidden; position: relative;">
              <div id="device-vu-meter-bar" style="height: 100%; width: 0%; background: linear-gradient(90deg, #10b981 0%, #eab308 75%, #ef4444 100%); transition: width 0.06s ease; border-radius: 7px;"></div>
            </div>
            <div style="font-size: 0.7rem; color: var(--text-muted); margin-top: 4px;">Vorbiți în microfon pentru a verifica captura sonoră în timp real.</div>
          </div>

          <!-- Camera Selection -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label">📹 Selectează Camera Web</label>
            <select class="form-select" id="conf-select-cam" style="width: 100%;"></select>
          </div>

          <!-- Video Effects & Background Blur (Roadmap Feature) -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🎭 Efect Cameră & Estompare Fundal</span>
              <span id="label-active-effect" style="font-size: 0.72rem; color: #818cf8; font-weight: 600;">Normal</span>
            </label>
            <div class="effect-selector-grid">
              <button type="button" class="effect-btn active" data-effect="none" title="Fără filtru pe cameră">
                <span style="font-size: 1.1rem;">🚫</span>
                <span>Normal</span>
              </button>
              <button type="button" class="effect-btn" data-effect="blur-soft" title="Estompare fină a fundalului">
                <span style="font-size: 1.1rem;">🌫️</span>
                <span>Blur Fin</span>
              </button>
              <button type="button" class="effect-btn" data-effect="blur-heavy" title="Estompare intensă">
                <span style="font-size: 1.1rem;">🌫️</span>
                <span>Blur Intens</span>
              </button>
              <button type="button" class="effect-btn" data-effect="studio" title="Iluminare studio & claritate înaltă">
                <span style="font-size: 1.1rem;">✨</span>
                <span>Studio HD</span>
              </button>
              <button type="button" class="effect-btn" data-effect="warm" title="Ton ambiental cald / birou">
                <span style="font-size: 1.1rem;">🌆</span>
                <span>Ambient</span>
              </button>
            </div>
          </div>

          <!-- Speaker Output & Test Sound -->
          <div class="form-group" style="margin-bottom: 0;">
            <label class="form-label" style="display: flex; justify-content: space-between; align-items: center;">
              <span>🔊 Ieșire Sunet (Difuzoare / Căști)</span>
              <button id="btn-test-speakers" class="btn btn-secondary btn-sm" style="padding: 2px 8px; font-size: 0.72rem; display: inline-flex; align-items: center; gap: 4px;">
                🔔 Test Sunet
              </button>
            </label>
            <select class="form-select" id="conf-select-speaker" style="width: 100%;"></select>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-primary" id="btn-save-device-settings" style="padding: 0.55rem 1.4rem;">
              Salvează & Aplică
            </button>
          </div>
        </div>
      </div>

      <!-- Rename Participant Modal (Zoom-style) -->
      <div class="rename-modal-overlay" id="rename-modal" style="display: none;">
        <div class="rename-modal-card">
          <div style="font-size: 1.15rem; font-weight: 700; margin-bottom: 0.4rem; color: #fff;">
            Redenumește Participantul
          </div>
          <div style="font-size: 0.8rem; color: var(--text-secondary); margin-bottom: 1.25rem;">
            Introduceți noul nume afișat în conferință pentru acest participant.
          </div>
          <input type="text" id="rename-input" class="chat-input" style="width: 100%; margin-bottom: 1.25rem;" />
          <div style="display: flex; justify-content: flex-end; gap: 0.75rem;">
            <button class="btn btn-secondary btn-sm" id="btn-cancel-rename">Anulează</button>
            <button class="btn btn-primary btn-sm" id="btn-save-rename">Salvează Noul Nume</button>
          </div>
        </div>
      </div>

      <!-- Screen & Window Share Picker Modal (Zoom style) -->
      <div class="picker-modal-overlay" id="screen-picker-modal" style="display: none;">
        <div class="picker-modal-card">
          <div class="picker-modal-header">
            <div>
              <div style="font-size: 1.2rem; font-weight: 700; color: #fff;">Alege ce dorești să partajezi</div>
              <div style="font-size: 0.825rem; color: var(--text-secondary); margin-top: 3px;">
                Selectează o fereastră activă (browser, document, etc.) sau un ecran complet
              </div>
            </div>
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <button class="btn btn-secondary btn-sm" id="btn-refresh-sources" style="padding: 6px 14px; font-size: 0.8rem;" title="Actualizează lista de ferestre">
                🔄 Reîmprospătează
              </button>
              <button id="btn-close-picker" style="background: none; border: none; color: #94a3b8; font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
            </div>
          </div>
          <div class="picker-tip-banner">
            <span style="font-size: 1.15rem;">💡</span>
            <div><strong>Notă Windows:</strong> Ferestrele minimizate în bara de activități nu trimit imagini video. Dacă documentul dorit lipsește, asigurați-vă că este deschis pe ecran, apoi apăsați <strong>🔄 Reîmprospătează</strong>.</div>
          </div>
          <div class="picker-tabs">
            <button class="picker-tab-btn active" id="tab-picker-windows">🪟 Ferestre Aplicații</button>
            <button class="picker-tab-btn" id="tab-picker-screens">🖥️ Ecrane Complete</button>
          </div>
          <div class="picker-grid" id="picker-sources-grid"></div>
          <div class="picker-footer">
            <button class="btn btn-secondary btn-sm" id="btn-cancel-picker">Anulează</button>
            <button class="btn btn-primary btn-sm" id="btn-confirm-picker" disabled>Partajează Fereastra Selectată</button>
          </div>
        </div>
      </div>

      <!-- Custom Vorbello Leave Conference Confirmation Modal -->
      <div class="picker-modal-overlay" id="confirm-leave-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: rgba(14, 19, 32, 0.95); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem 2.2rem; width: 440px; max-width: 90vw; margin: auto; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85), 0 0 35px rgba(239, 68, 68, 0.2); text-align: center; display: flex; flex-direction: column; align-items: center; gap: 1.25rem;">
          
          <div style="position: relative;">
            <div style="width: 72px; height: 72px; border-radius: 50%; background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.35); display: flex; align-items: center; justify-content: center; box-shadow: 0 0 24px rgba(239, 68, 68, 0.25);">
              <img src="${VORBELLO_LOGO}" alt="Vorbello" style="width: 44px; height: 44px; object-fit: contain; filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.5));" />
            </div>
            <div style="position: absolute; bottom: -2px; right: -2px; width: 26px; height: 26px; border-radius: 50%; background: #ef4444; color: #fff; display: flex; align-items: center; justify-content: center; border: 2px solid #0e1320; box-shadow: 0 0 10px rgba(239, 68, 68, 0.6);">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </div>
          </div>

          <div>
            <h3 style="font-size: 1.25rem; font-weight: 700; color: #fff; margin-bottom: 0.45rem; letter-spacing: -0.02em;">Părăsiți Conferința?</h3>
            <p style="font-size: 0.85rem; color: #94a3b8; line-height: 1.5; margin: 0;">
              ${(this.isHost || this.isAdmin) 
                ? 'În calitate de gazdă / organizator, puteți încheia ședința pentru toți participanții sau puteți părăsi doar dumneavoastră.' 
                : 'Sunteți pe cale să vă deconectați din sala de conferință. Puteți reveni oricând din panoul principal.'}
            </p>
          </div>

          <div style="display: flex; flex-direction: column; gap: 0.65rem; width: 100%; margin-top: 0.5rem;">
            ${(this.isHost || this.isAdmin) ? `
              <button class="btn btn-danger" id="btn-end-meeting-for-all" style="width: 100%; padding: 0.8rem 1rem; font-weight: 700; font-size: 0.9rem; display: flex; align-items: center; justify-content: center; gap: 0.65rem; background: linear-gradient(135deg, #dc2626, #b91c1c); border: 1px solid #ef4444; border-radius: 12px; color: #fff; cursor: pointer; box-shadow: 0 4px 15px rgba(220, 38, 38, 0.45);">
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line>
                </svg>
                <span>Încheie Ședința pentru Toți</span>
              </button>
              <button class="btn btn-secondary" id="btn-confirm-leave-modal" style="width: 100%; padding: 0.7rem 1rem; font-size: 0.85rem; display: flex; align-items: center; justify-content: center; gap: 0.65rem; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 12px; color: #cbd5e1; cursor: pointer;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span>Părăsește Doar Tu (Lasă Sala Deschisă)</span>
              </button>
            ` : `
              <button class="btn btn-danger" id="btn-confirm-leave-modal" style="width: 100%; padding: 0.75rem 1rem; font-weight: 700; font-size: 0.9rem; display: flex; align-items: center; justify-content: center; gap: 0.65rem; background: #dc2626; border-radius: 12px; color: #fff; cursor: pointer;">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span>Da, Părăsește Conferința</span>
              </button>
            `}
            <button class="btn btn-secondary" id="btn-cancel-leave-modal" style="width: 100%; padding: 0.65rem 1rem; font-size: 0.825rem; display: flex; align-items: center; justify-content: center; gap: 0.5rem; background: transparent; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 12px; color: #94a3b8; cursor: pointer;">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>Anulează (Rămâi în Conferință)</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Version & Diagnostics Modal -->
      <div class="picker-modal-overlay" id="version-info-modal" style="display: none; align-items: center; justify-content: center; z-index: 10001;">
        <div class="conf-modal-scrollable-card" style="background: rgba(14, 19, 32, 0.98); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(56, 189, 248, 0.4); border-radius: 20px; padding: 2rem; width: 560px; max-width: 92vw; max-height: 88vh; max-height: 88dvh; overflow-y: auto; -webkit-overflow-scrolling: touch; box-shadow: 0 25px 70px rgba(0, 0, 0, 0.9); display: flex; flex-direction: column; gap: 1.25rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.85rem;">
            <div style="display: flex; align-items: center; gap: 0.75rem;">
              <img src="${VORBELLO_LOGO}" alt="Vorbello Logo" style="width: 36px; height: 36px; object-fit: contain; filter: drop-shadow(0 0 10px rgba(56, 189, 248, 0.5));" />
              <div>
                <h3 style="margin: 0; font-size: 1.2rem; font-weight: 700; color: #fff; display: flex; align-items: center; gap: 0.5rem;">
                  <span>Panou Versiune & Sistem</span>
                  <span style="background: linear-gradient(135deg, #10b981, #059669); color: #fff; font-size: 0.7rem; padding: 2px 8px; border-radius: 20px; font-weight: 700;">v${APP_VERSION}</span>
                </h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Platformă Privată de Videoconferință Vorbello</div>
              </div>
            </div>
            <button id="btn-close-version-info" style="background: none; border: none; color: #94a3b8; font-size: 1.6rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>

          <!-- Diagnostic Cards Grid (Clean & Protected) -->
          <div style="display: flex; flex-direction: column; gap: 0.65rem;">
            <div style="display: flex; flex-direction: column; gap: 0.5rem; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.75rem 0.95rem;">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div style="display: flex; align-items: center; gap: 0.6rem;">
                  <span style="font-size: 1.1rem;">🏷️</span>
                  <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 600;">Versiune Software:</span>
                </div>
                <strong style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.35); padding: 2px 8px; border-radius: 6px; font-size: 0.85rem; font-family: monospace;">v${APP_VERSION}</strong>
              </div>
              <div style="color: #38bdf8; font-size: 0.78rem; line-height: 1.45; background: rgba(56, 189, 248, 0.08); border: 1px solid rgba(56, 189, 248, 0.2); border-radius: 6px; padding: 0.5rem 0.75rem;">
                ${APP_BUILD_TAG}
              </div>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">📅</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Data Compilării & Build:</span>
              </div>
              <span style="color: #fff; font-size: 0.825rem; font-weight: 600;">${APP_BUILD_DATE}</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">🛡️</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Securitate & Criptare:</span>
              </div>
              <span style="color: #34d399; font-size: 0.825rem; font-weight: 600;">End-to-End DTLS-SRTP 256-bit</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">📹</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Cameră Video:</span>
              </div>
              <span id="version-modal-cam-name" style="color: #f8fafc; font-size: 0.8rem; font-weight: 600; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Detectare...</span>
            </div>

            <div style="display: flex; justify-content: space-between; align-items: center; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 0.7rem 0.95rem;">
              <div style="display: flex; align-items: center; gap: 0.6rem;">
                <span style="font-size: 1.1rem;">🎤</span>
                <span style="font-size: 0.825rem; color: #cbd5e1; font-weight: 500;">Microfon Audio:</span>
              </div>
              <span id="version-modal-mic-name" style="color: #f8fafc; font-size: 0.8rem; font-weight: 600; max-width: 250px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">Detectare...</span>
            </div>

            ${this.isAdmin ? `
              <div style="margin-top: 0.35rem; padding: 0.75rem 0.95rem; background: rgba(16, 185, 129, 0.08); border: 1px solid rgba(16, 185, 129, 0.3); border-radius: 10px;">
                <div style="font-size: 0.75rem; font-weight: 700; color: #6ee7b7; margin-bottom: 0.35rem;">🔒 Diagnostic Administrator:</div>
                <div style="font-size: 0.74rem; color: #cbd5e1; display: flex; flex-direction: column; gap: 3px;">
                  <div>• Server Semnalizare: <code>app.vorbello.eu/signal.php</code></div>
                  <div>• Conexiune SFU: <code>${this.config.livekitUrl || 'wss://live.vorbello.eu'}</code></div>
                </div>
              </div>
            ` : ''}
          </div>

          <div style="display: flex; justify-content: space-between; align-items: center; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-secondary btn-sm" id="btn-version-open-devices" style="font-size: 0.8rem; padding: 0.5rem 1rem; display: flex; align-items: center; gap: 0.4rem;">
              <span>⚙️</span> Deschide Setări Dispozitive
            </button>
            <button class="btn btn-primary btn-sm" id="btn-version-ok" style="font-size: 0.8rem; padding: 0.5rem 1.4rem;">
              Închide
            </button>
          </div>
        </div>
      </div>

      <!-- Recording Options Modal (Zoom-Style) -->
      <div class="picker-modal-overlay" id="recording-options-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: rgba(14, 19, 32, 0.96); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem; width: 540px; max-width: 92vw; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85); display: flex; flex-direction: column; gap: 1rem;">
          <div style="display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255, 255, 255, 0.08); padding-bottom: 0.75rem;">
            <div style="display: flex; align-items: center; gap: 0.6rem;">
              <span style="font-size: 1.5rem;">🔴</span>
              <div>
                <h3 style="margin: 0; font-size: 1.15rem; font-weight: 700; color: #fff;">Înregistrare Locală Conferință</h3>
                <div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 2px;">Salvare directă pe calculatorul dumneavoastră la 30 FPS</div>
              </div>
            </div>
            <button id="btn-close-rec-options" style="background: none; border: none; color: #94a3b8; font-size: 1.5rem; cursor: pointer; line-height: 1;">&times;</button>
          </div>

          <div style="font-size: 0.825rem; color: #cbd5e1; line-height: 1.45;">
            Selectați calitatea și modul de înregistrare (ambele opțiuni video rulează la <strong>30 FPS fluid</strong>). La finalizare, fișierul se salvează instantaneu în folderul <strong>Videos/Vorbello Recordings</strong> și se descarcă automat.
          </div>

          <!-- Video Composition Layout Selector -->
          <div id="rec-layout-section" style="display: flex; flex-direction: column; gap: 0.5rem; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 0.78rem; font-weight: 700; color: #38bdf8; text-transform: uppercase; letter-spacing: 0.5px;">📐 Compoziție Cadru Video</span>
              <span style="font-size: 0.7rem; color: var(--text-muted);">Ce include imaginea</span>
            </div>
            <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem;" id="rec-layout-selector">
              <button type="button" class="rec-layout-btn selected" id="rec-layout-composite" data-layout="composite">
                <span style="font-size: 1.15rem;">🖼️</span>
                <span style="font-weight: 700; font-size: 0.78rem;">Studio PiP</span>
                <span style="font-size: 0.65rem; color: #94a3b8;">Ecran + Cameră colț</span>
              </button>
              <button type="button" class="rec-layout-btn" id="rec-layout-screen" data-layout="screen_only">
                <span style="font-size: 1.15rem;">🖥️</span>
                <span style="font-weight: 700; font-size: 0.78rem;">Doar Ecran</span>
                <span style="font-size: 0.65rem; color: #94a3b8;">Ecran curat, fără cameră</span>
              </button>
              <button type="button" class="rec-layout-btn" id="rec-layout-cameras" data-layout="cameras_only">
                <span style="font-size: 1.15rem;">👤</span>
                <span style="font-weight: 700; font-size: 0.78rem;">Cameră Curată</span>
                <span style="font-size: 0.65rem; color: #94a3b8;">Doar camera ta (Admin)</span>
              </button>
            </div>
          </div>

          <div class="rec-options-grid">
            <div class="rec-option-card selected" id="rec-card-video-hd" data-type="video-hd">
              <div class="rec-option-icon">🖥️</div>
              <div class="rec-option-title">HD 720p (30 FPS)</div>
              <div style="font-size: 0.68rem; font-weight: 700; color: #38bdf8; background: rgba(56, 189, 248, 0.15); border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 9999px; padding: 2px 8px; display: inline-block; margin-bottom: 6px;">Echilibrat • ~12 MB/min</div>
              <div class="rec-option-desc">1280x720 la 30 fps, video 1.5 Mbps, audio 96 kbps. Text lizibil, mișcare fluidă și fișiere compacte.</div>
            </div>

            <div class="rec-option-card" id="rec-card-video-fhd" data-type="video-fhd">
              <div class="rec-option-icon">🎬</div>
              <div class="rec-option-title">Full HD 1080p (30 FPS)</div>
              <div style="font-size: 0.68rem; font-weight: 700; color: #c084fc; background: rgba(192, 132, 252, 0.15); border: 1px solid rgba(192, 132, 252, 0.3); border-radius: 9999px; padding: 2px 8px; display: inline-block; margin-bottom: 6px;">Claritate Maximă • ~22 MB/min</div>
              <div class="rec-option-desc">1920x1080 la 30 fps, video 2.5 Mbps, audio 128 kbps. Rezoluție nativă Full HD pentru detalii maxime.</div>
            </div>

            <div class="rec-option-card" id="rec-card-audio" data-type="audio" style="grid-column: 1 / -1; display: flex; align-items: center; text-align: left; gap: 0.85rem; padding: 0.75rem 1rem;">
              <div class="rec-option-icon" style="font-size: 1.6rem; margin-bottom: 0;">🎙️</div>
              <div style="flex: 1;">
                <div class="rec-option-title" style="margin-bottom: 2px; font-size: 0.85rem;">Audio HD Exclusiv</div>
                <div class="rec-option-desc" style="font-size: 0.72rem;">Doar vocile tuturor participanților mixate curat la 96 kbps (dimensiune minimă, ~0.7 MB/min).</div>
              </div>
            </div>
          </div>

          <!-- Format Container Selector (WebM vs MP4) -->
          <div style="display: flex; flex-direction: column; gap: 0.5rem; background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem;">
            <div style="display: flex; justify-content: space-between; align-items: center;">
              <span style="font-size: 0.78rem; font-weight: 700; color: #38bdf8; text-transform: uppercase; letter-spacing: 0.5px;">📦 Format Fișier (Container)</span>
              <span style="font-size: 0.7rem; color: var(--text-muted);">Extensie salvare</span>
            </div>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem;" id="rec-container-selector">
              <button type="button" class="rec-container-btn selected" id="rec-container-webm" data-container="webm">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-size: 1.05rem;">⚡</span>
                  <span style="font-weight: 700; font-size: 0.8rem; color: #a5b4fc;">WebM (.webm)</span>
                </div>
                <div style="font-size: 0.65rem; color: #cbd5e1;">Recomandat • Sunet Opus HD • Fără lag</div>
              </button>
              <button type="button" class="rec-container-btn" id="rec-container-mp4" data-container="mp4">
                <div style="display: flex; align-items: center; gap: 6px;">
                  <span style="font-size: 1.05rem;">🎬</span>
                  <span style="font-weight: 700; font-size: 0.8rem; color: #fff;">MP4 (.mp4)</span>
                </div>
                <div style="font-size: 0.65rem; color: #94a3b8;">Format H.264 / AAC • Compatibilitate largă</div>
              </button>
            </div>
          </div>

          <div style="display: flex; justify-content: flex-end; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-secondary btn-sm" id="btn-cancel-recording" style="padding: 0.5rem 1.2rem;">Anulează</button>
            <button class="btn btn-primary btn-sm" id="btn-confirm-start-recording" style="background: linear-gradient(135deg, #ef4444, #dc2626); border-color: #ef4444; padding: 0.5rem 1.5rem; font-weight: 700;">🔴 Începe Înregistrarea</button>
          </div>
        </div>
      </div>

      <!-- Recording Success Modal -->
      <div class="picker-modal-overlay" id="recording-success-modal" style="display: none; align-items: center; justify-content: center; z-index: 10000;">
        <div style="background: rgba(14, 19, 32, 0.96); backdrop-filter: blur(28px); -webkit-backdrop-filter: blur(28px); border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 20px; padding: 2rem; width: 480px; max-width: 90vw; box-shadow: 0 25px 60px rgba(0, 0, 0, 0.85); display: flex; flex-direction: column; gap: 1rem; text-align: center;">
          <div style="font-size: 2.8rem; line-height: 1;">🎉</div>
          <h3 style="margin: 0; font-size: 1.2rem; font-weight: 700; color: #fff;">Înregistrare Salvată cu Succes!</h3>
          <div id="rec-success-filename" style="font-weight: 700; color: #38bdf8; font-size: 0.88rem; word-break: break-all; background: rgba(56, 189, 248, 0.1); padding: 0.5rem; border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.25);"></div>
          <div id="rec-success-details" style="font-size: 0.8rem; color: #94a3b8; line-height: 1.4;"></div>

          <div style="display: flex; justify-content: center; gap: 0.75rem; border-top: 1px solid rgba(255, 255, 255, 0.08); padding-top: 1rem; margin-top: 0.25rem;">
            <button class="btn btn-secondary btn-sm" id="btn-open-rec-folder" style="display: none; padding: 0.5rem 1.2rem;">📂 Deschide Dosarul</button>
            <button class="btn btn-primary btn-sm" id="btn-close-rec-success" style="padding: 0.5rem 1.5rem;">Am înțeles</button>
          </div>
        </div>
      </div>
    `;

    this.attachListeners();
    this.attachPickerListeners();
    this.attachDeviceSettingsListeners();
    this.attachVersionModalListeners();
    this.attachTileActionListeners();
  }

  private adaptVideoElementFit(videoEl: HTMLVideoElement, tile: HTMLElement) {
    if (!videoEl || !tile) return;
    const checkFit = () => {
      const vw = videoEl.videoWidth;
      const vh = videoEl.videoHeight;
      if (vw > 0 && vh > 0) {
        const isPortrait = vh > vw * 1.05; // 9:16 portrait video from smartphone
        const manualFit = tile.getAttribute('data-fit-mode');
        if (manualFit === 'cover') {
          videoEl.classList.remove('fit-contain', 'is-portrait');
          videoEl.style.objectFit = 'cover';
        } else if (manualFit === 'contain' || isPortrait) {
          // Automatic 9:16 smartphone framing: whole person visible, no head crop, no zoom!
          videoEl.classList.add('fit-contain', 'is-portrait');
          videoEl.style.objectFit = 'contain';
          tile.style.backgroundColor = '#070b14';
        } else {
          videoEl.classList.remove('fit-contain', 'is-portrait');
          videoEl.style.objectFit = 'cover';
        }
      }
    };

    videoEl.addEventListener('loadedmetadata', checkFit);
    videoEl.addEventListener('resize', checkFit);
    checkFit();
  }

  private renderVideoGrid() {
    const grid = document.getElementById('video-grid');
    if (!grid) return;

    // Do not show participants who are currently in the waiting lobby on the main stage:
    const activeParticipants = this.participants.filter(p => !this.waitingParticipants.some(wp => wp.identity === p.identity));
    const count = activeParticipants.length;
    grid.className = `video-grid count-${count > 6 ? 'many' : count}`;

    const currentIdentities = new Set(activeParticipants.map(p => p.identity));

    // 1. Remove stale tiles for departed or waiting participants
    grid.querySelectorAll('.video-tile').forEach(tile => {
      const id = tile.id.replace('tile-', '');
      if (!currentIdentities.has(id)) {
        const vid = tile.querySelector('video') as HTMLVideoElement | null;
        if (vid && vid.srcObject) vid.srcObject = null;
        tile.remove();
      }
    });

    // 2. Incrementally add or update each active participant tile
    activeParticipants.forEach(p => {
      const isSelf = p.identity === 'self';
      const isMuted = isSelf ? !this.manager.isMicEnabled : !!p.isMuted;
      const isCamOff = isSelf ? !this.manager.isCameraEnabled : !!p.isCamOff;
      const isPinned = this.pinnedIdentity === p.identity;
      const isSpeaking = !!p.isSpeaking && !isMuted;
      const isHandRaised = isSelf ? this.isLocalHandRaised : !!p.isHandRaised;

      const speakerIconText = isMuted ? '🔇' : (isSpeaking ? '🔊' : '👤');
      const speakerIconColor = isMuted ? '#f87171' : (isSpeaking ? '#34d399' : 'rgba(255,255,255,0.6)');

      let tile = document.getElementById(`tile-${p.identity}`);
      if (!tile) {
        tile = document.createElement('div');
        tile.id = `tile-${p.identity}`;
        tile.className = `video-tile ${isSpeaking ? 'speaking' : ''} ${isPinned ? 'is-pinned-tile' : ''} ${isCamOff ? 'has-cam-off' : ''}`;
        if (isPinned) {
          tile.style.border = '2px solid #6366f1';
          tile.style.boxShadow = '0 0 15px rgba(99,102,241,0.4)';
        }
        const isPartAdmin = p.role === 'admin' || (isSelf && this.isAdmin);
        const isPartMod = !isPartAdmin && (p.role === 'moderator' || (isSelf && this.isModerator));
        const roleBadgeHtml = isPartAdmin 
          ? `<span class="role-badge role-admin">👑 Admin</span>`
          : (isPartMod ? `<span class="role-badge role-mod">🛡️ Mod</span>` : '');

        tile.innerHTML = `
          <div class="tile-hand-badge" id="hand-badge-${p.identity}" style="${isHandRaised ? '' : 'display: none;'}">${ICONS.hand}</div>
          <div class="avatar-placeholder" style="${isCamOff ? '' : 'display: none;'}">${p.name.charAt(0)}</div>
          <div class="tile-badge">
            <span class="speaker-indicator-icon" style="color: ${speakerIconColor}; font-size: 0.72rem;">${speakerIconText}</span>
            <span class="participant-name-text">${p.name}</span>
            ${roleBadgeHtml}
            <span class="pinned-badge" style="background: #6366f1; color: #fff; font-size: 0.65rem; padding: 1px 5px; border-radius: 4px; margin-left: 4px; font-weight: 700; display: ${isPinned ? 'inline-block' : 'none'};">📌</span>
          </div>

          <!-- 3-Dots Action Menu Trigger (Visible for all participants) -->
          <div class="tile-actions-wrapper">
            <button class="tile-action-trigger" data-id="${p.identity}" title="Opțiuni participant">
              ${ICONS.moreVert}
            </button>
          </div>
        `;
        grid.appendChild(tile);
      } else {
        // Tile already exists - update properties in-place without destroying DOM:
        tile.classList.toggle('speaking', isSpeaking);
        tile.classList.toggle('is-pinned-tile', isPinned);
        tile.classList.toggle('has-cam-off', isCamOff);
        tile.style.border = isPinned ? '2px solid #6366f1' : '';
        tile.style.boxShadow = isPinned ? '0 0 15px rgba(99,102,241,0.4)' : '';

        // Remove legacy bulky audio indicator and old dropdowns if still in DOM
        tile.querySelector('.tile-audio-indicator')?.remove();
        tile.querySelector('.tile-dropdown')?.remove();

        // Update hand badge
        let handBadge = tile.querySelector('.tile-hand-badge') as HTMLElement | null;
        if (!handBadge && isHandRaised) {
          handBadge = document.createElement('div');
          handBadge.className = 'tile-hand-badge';
          handBadge.id = `hand-badge-${p.identity}`;
          handBadge.innerHTML = ICONS.hand;
          tile.prepend(handBadge);
        } else if (handBadge && (!handBadge.querySelector('svg') || handBadge.innerHTML.includes('✋'))) {
          handBadge.innerHTML = ICONS.hand;
        }
        if (handBadge) {
          handBadge.style.display = isHandRaised ? 'flex' : 'none';
        }

        const nameEl = tile.querySelector('.participant-name-text');
        if (nameEl && nameEl.textContent !== p.name) nameEl.textContent = p.name;

        const speakerIcon = tile.querySelector('.speaker-indicator-icon') as HTMLElement | null;
        if (speakerIcon) {
          speakerIcon.textContent = speakerIconText;
          speakerIcon.style.color = speakerIconColor;
        }

        const pinnedBadge = tile.querySelector('.pinned-badge') as HTMLElement | null;
        if (pinnedBadge) pinnedBadge.style.display = isPinned ? 'inline-block' : 'none';

        const isPartAdmin = p.role === 'admin' || (isSelf && this.isAdmin);
        const isPartMod = !isPartAdmin && (p.role === 'moderator' || (isSelf && this.isModerator));
        let roleBadge = tile.querySelector('.role-badge') as HTMLElement | null;
        if (isPartAdmin || isPartMod) {
          if (!roleBadge) {
            roleBadge = document.createElement('span');
            const badgeWrapper = tile.querySelector('.tile-badge');
            if (badgeWrapper) badgeWrapper.appendChild(roleBadge);
          }
          roleBadge.className = `role-badge ${isPartAdmin ? 'role-admin' : 'role-mod'}`;
          roleBadge.removeAttribute('style');
          roleBadge.textContent = isPartAdmin ? '👑 Admin' : '🛡️ Mod';
        } else if (roleBadge) {
          roleBadge.remove();
        }

        // Ensure 3-dots action menu trigger is always present
        if (!tile.querySelector('.tile-actions-wrapper')) {
          const actionsWrapper = document.createElement('div');
          actionsWrapper.className = 'tile-actions-wrapper';
          actionsWrapper.innerHTML = `
            <button class="tile-action-trigger" data-id="${p.identity}" title="Opțiuni participant">
              ${ICONS.moreVert}
            </button>
          `;
          tile.appendChild(actionsWrapper);
        }
      }
    });

    // 3. Connect remote video and audio tracks seamlessly without recreation
    if (this.isP2PMode) return;
    const room = this.manager.getRoom();
    if (room && room.state === 'connected') {
      room.remoteParticipants.forEach(remotePart => {
        const p = this.participants.find(part => part.identity === remotePart.identity);

        // --- AUDIO TRACK SYNCHRONIZATION ---
        const audioPub = remotePart.getTrackPublication(Track.Source.Microphone);
        if (audioPub && audioPub.track) {
          let audioEl = document.getElementById(`audio-remote-${remotePart.identity}`) as HTMLAudioElement | null;
          if (!audioEl) {
            audioEl = document.createElement('audio');
            audioEl.id = `audio-remote-${remotePart.identity}`;
            audioEl.autoplay = true;
            audioEl.setAttribute('playsinline', '');
            audioEl.volume = 1.0;
            document.body.appendChild(audioEl);
          }
          if (!audioPub.track.attachedElements.includes(audioEl)) {
            audioPub.track.attach(audioEl);
          }
          const isAudioMuted = !!audioPub.isMuted || !!p?.isMuted;
          audioEl.muted = isAudioMuted;
          // NEVER pause audioEl! Pausing kills autoplay permissions on mobile devices.
          if (audioEl.paused) {
            audioEl.play().catch(() => {});
          }
        }

        // --- VIDEO TRACK SYNCHRONIZATION ---
        const tile = document.getElementById(`tile-${remotePart.identity}`);
        if (!tile) return;
        const avatar = tile.querySelector('.avatar-placeholder') as HTMLElement | null;

        const videoPub = remotePart.getTrackPublication(Track.Source.Camera);
        let videoEl = tile.querySelector('video.remote-cam-video') as HTMLVideoElement | null;

        if (videoPub && videoPub.track) {
          if (!videoEl) {
            videoEl = document.createElement('video');
            videoEl.className = 'remote-cam-video';
            videoEl.autoplay = true;
            videoEl.playsInline = true;
            videoEl.muted = true;
            videoEl.setAttribute('muted', '');
            videoEl.setAttribute('playsinline', '');
            videoEl.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; border-radius:inherit; z-index:1;';
            tile.prepend(videoEl);
            videoPub.track.attach(videoEl);
          } else {
            if (!videoPub.track.attachedElements.includes(videoEl) || !videoEl.srcObject) {
              videoPub.track.attach(videoEl);
            }
          }
          this.adaptVideoElementFit(videoEl, tile);
          videoEl.muted = true;
          videoEl.setAttribute('muted', '');

          const isMuted = !!videoPub.isMuted || !videoPub.isSubscribed || !!p?.isCamOff;
          videoEl.style.display = isMuted ? 'none' : 'block';
          if (avatar) avatar.style.display = isMuted ? 'flex' : 'none';
          if (!isMuted && videoEl.paused) {
            videoEl.play().catch(() => {});
          }

          videoEl.onloadedmetadata = () => {
            if (videoEl) this.adaptVideoElementFit(videoEl, tile);
            if (!videoPub.isMuted && videoPub.isSubscribed && !p?.isCamOff) {
              if (videoEl) videoEl.style.display = 'block';
              if (avatar) avatar.style.display = 'none';
              videoEl?.play().catch(() => {});
            }
          };
        } else {
          if (videoEl) {
            videoEl.style.display = 'none';
          }
          if (avatar) avatar.style.display = 'flex';
        }
      });
    }

    // 4. Update local self tile video
    this.updateSelfTile();

    // 5. Update production waiting banner
    this.updateProdWaitingBanner();

    // 6. Update top filmstrip carousel previews (Zoom / Meet Style)
    this.renderFilmstripPreviews();
  }

  private shouldUseFilmstrip(): boolean {
    const count = this.participants.length;
    // Strict universal rule for Desktop, Web & Mobile:
    // Up to 4 participants (1, 2, 3, 4) display in clean, proportional Gallery Grid.
    // Anything above 4 (> 4 participants: 5, 6, 15, 20, 50+) ALWAYS enters Spotlight Mode:
    // - On Desktop: Hero on stage (left), other participants in right column (220px).
    // - On Mobile: Hero on stage (full), other participants in bottom floating row.
    if (count > 4) {
      return true;
    }
    // Up to 4 participants: respect manual view mode toggle if user clicked it:
    if (this.userExplicitlyChangedViewMode) {
      return this.currentViewMode === 'speaker';
    }
    return false;
  }

  private renderFilmstripPreviews() {
    const filmstripBar = document.getElementById('top-filmstrip-bar');
    const container = document.getElementById('filmstrip-scroll-container');
    if (!filmstripBar || !container) return;

    // Per user direct requirement: NEVER show miniatures at the top!
    // Miniatures live exclusively in video-grid AT THE BOTTOM!
    filmstripBar.style.display = 'none';
    container.innerHTML = '';
  }



  private updateProdWaitingBanner() {
    // Permanently disabled per user request: floating waiting banner removed from stage.
    // Sharing is now handled seamlessly via the dedicated Invite card in Participants.
  }

  private attachTileActionListeners() {
    // 1. Single global floating dropdown attached directly to document.body
    // Immune to any parent transform, backdrop-filter, or overflow:hidden
    document.getElementById('global-tile-dropdown')?.remove();
    const globalDropdown = document.createElement('div');
    globalDropdown.id = 'global-tile-dropdown';
    globalDropdown.className = 'tile-dropdown';
    document.body.appendChild(globalDropdown);

      // Delegated click listener for actions inside the global dropdown:
      globalDropdown.addEventListener('click', async (e) => {
        const target = e.target as HTMLElement;

        // 1. Rename
        const renameBtn = target.closest('.action-rename') as HTMLElement | null;
        if (renameBtn) {
          e.stopPropagation();
          const id = renameBtn.getAttribute('data-id');
          if (id) this.openRenameModal(id);
          this.closeAllDropdowns();
          return;
        }

        // 2. Pin / Spotlight
        const pinBtn = target.closest('.action-pin') as HTMLElement | null;
        if (pinBtn) {
          e.stopPropagation();
          if (pinBtn.hasAttribute('disabled') || pinBtn.classList.contains('disabled') || (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin)) {
            this.showFloatingToast('🔒 Scena a fost fixată de Administrator și nu poate fi modificată.');
            this.closeAllDropdowns();
            return;
          }
          const id = pinBtn.getAttribute('data-id');
          const newId = this.pinnedIdentity === id ? null : id;
          this.setPinState(newId, true);
          this.closeAllDropdowns();
          return;
        }

        // 3. Toggle Fit
        const toggleFitBtn = target.closest('.action-toggle-fit') as HTMLElement | null;
        if (toggleFitBtn) {
          e.stopPropagation();
          const id = toggleFitBtn.getAttribute('data-id');
          const tileEl = document.getElementById(`tile-${id}`);
          if (tileEl) {
            const vid = tileEl.querySelector('video') as HTMLVideoElement | null;
            const currentFit = tileEl.getAttribute('data-fit-mode') || (vid?.classList.contains('fit-contain') ? 'contain' : 'cover');
            const newFit = currentFit === 'contain' ? 'cover' : 'contain';
            tileEl.setAttribute('data-fit-mode', newFit);
            if (vid) {
              this.adaptVideoElementFit(vid, tileEl);
            }
            this.showFloatingToast(newFit === 'contain' ? '📐 Încadrare completă (Fără tăiere / Decupare 9:16)' : '🔍 Umplere completă a cadrului (Zoom / Crop)');
          }
          this.closeAllDropdowns();
          return;
        }

        // 4. Lower Hand
        const lowerHandBtn = target.closest('.action-lower-hand') as HTMLElement | null;
        if (lowerHandBtn) {
          e.stopPropagation();
          const id = lowerHandBtn.getAttribute('data-id');
          if (id === 'self') {
            this.isLocalHandRaised = false;
            const selfP = this.participants.find(p => p.identity === 'self');
            if (selfP) selfP.isHandRaised = false;
            this.manager.sendHandRaise(false);
            this.updateControlsState();
          } else if (id) {
            const p = this.participants.find(part => part.identity === id);
            if (p) {
              p.isHandRaised = false;
              this.manager.sendDataMessage({
                type: 'hand_raise',
                identity: id,
                name: p.name,
                isRaised: false,
              });
            }
          }
          this.renderVideoGrid();
          this.renderParticipantsList();
          this.closeAllDropdowns();
          return;
        }

        // 5. Mute Toggle
        const muteBtn = target.closest('.action-mute') as HTMLElement | null;
        if (muteBtn) {
          e.stopPropagation();
          const id = muteBtn.getAttribute('data-id');
          if (id === 'self') {
            this.manager.enableMicrophone(!this.manager.isMicEnabled).then(() => {
              this.updateControlsState();
              this.renderVideoGrid();
            });
          } else if (id) {
            if (!this.isAdmin && !this.isModerator) {
              const audioEl = document.getElementById(`audio-remote-${id}`) as HTMLAudioElement | null;
              if (audioEl) {
                audioEl.muted = !audioEl.muted;
                const p = this.participants.find(part => part.identity === id);
                if (p) p.isMuted = audioEl.muted;
                this.showFloatingToast(audioEl.muted ? '🔇 Ai oprit sunetul pentru acest participant.' : '🔊 Sunetul a fost reactivat.');
                this.renderVideoGrid();
              }
              this.closeAllDropdowns();
              return;
            }
            const p = this.participants.find(part => part.identity === id);
            if (p) {
              p.isMuted = !p.isMuted;
              this.manager.sendDataMessage({
                type: 'moderation',
                action: p.isMuted ? 'mute-user' : 'unmute-user',
                targetIdentity: id,
              });
              this.renderParticipantsList();
              this.renderVideoGrid();
            }
          }
          this.closeAllDropdowns();
          return;
        }

        // 6. Camera Toggle
        const camBtn = target.closest('.action-cam') as HTMLElement | null;
        if (camBtn) {
          e.stopPropagation();
          const id = camBtn.getAttribute('data-id');
          if (id === 'self') {
            this.manager.enableCamera(!this.manager.isCameraEnabled).then(() => {
              this.updateControlsState();
              this.updateSelfTile();
              this.renderVideoGrid();
            });
          } else if (id) {
            if (!this.isAdmin && !this.isModerator) {
              this.showFloatingToast('⚠️ Doar Administratorul și Moderatorii pot opri camera altor participanți.');
              this.closeAllDropdowns();
              return;
            }
            const p = this.participants.find(part => part.identity === id);
            if (p) {
              p.isCamOff = !p.isCamOff;
              this.manager.sendDataMessage({
                type: 'moderation',
                action: p.isCamOff ? 'stop-cam-user' : 'start-cam-user',
                targetIdentity: id,
              });
              this.renderParticipantsList();
              this.renderVideoGrid();
            }
          }
          this.closeAllDropdowns();
          return;
        }

        // 7. Kick
        const kickBtn = target.closest('.action-kick') as HTMLElement | null;
        if (kickBtn) {
          e.stopPropagation();
          if (!this.isAdmin) {
            this.showFloatingToast('⚠️ Doar Administratorul poate elimina participanți din conferință.');
            this.closeAllDropdowns();
            return;
          }
          const id = kickBtn.getAttribute('data-id');
          if (id) {
            this.kickParticipant(id);
          }
          this.closeAllDropdowns();
          return;
        }
      });

    // 2. Remove previous document click listener if any to prevent duplicate calls
    if ((this as any)._globalTileClickListener) {
      document.removeEventListener('click', (this as any)._globalTileClickListener);
      (this as any)._globalTileClickListener = null;
    }

    // Master document-level delegated listener for 3-dots trigger and tile clicks
    const docClickListener = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // 1. More options 3-dots trigger clicked:
      const trigger = target.closest('.tile-action-trigger') as HTMLElement | null;
      if (trigger) {
        e.stopPropagation();
        e.preventDefault();
        const id = trigger.getAttribute('data-id');
        let gd = document.getElementById('global-tile-dropdown') as HTMLElement | null;
        if (!gd) {
          gd = document.createElement('div');
          gd.id = 'global-tile-dropdown';
          gd.className = 'tile-dropdown';
          document.body.appendChild(gd);
        }
        if (!id) return;

        const isAlreadyOpen = gd.classList.contains('show') && gd.getAttribute('data-target-id') === id;
        if (isAlreadyOpen) {
          this.closeAllDropdowns();
          return;
        }

        const isSelf = id === 'self';
        const p = isSelf
          ? { identity: 'self', name: `${this.manager.displayName || this.config.displayName} (Tu)`, role: 'admin' as const, isMuted: !this.manager.isMicEnabled, isCamOff: !this.manager.isCameraEnabled, isHandRaised: this.isLocalHandRaised }
          : (this.participants.find(part => part.identity === id) || {
              identity: id,
              name: trigger.closest('.video-tile')?.querySelector('.participant-name-text')?.textContent || 'Participant',
              role: 'user' as const,
              isMuted: false,
              isCamOff: false,
              isHandRaised: false
            });

        const isMuted = isSelf ? !this.manager.isMicEnabled : !!p.isMuted;
        const isCamOff = isSelf ? !this.manager.isCameraEnabled : !!p.isCamOff;
        const isHandRaised = isSelf ? this.isLocalHandRaised : !!p.isHandRaised;
        const canRename = isSelf || this.isAdmin || this.isModerator;
        const canToggleMic = isSelf || this.isAdmin || this.isModerator;
        const canToggleCam = isSelf || this.isAdmin || this.isModerator;
        const canLowerHand = isSelf || this.isAdmin || this.isModerator;
        const canModerateUser = !isSelf && (this.isAdmin || this.isModerator);

        let pinHtml = '';
        if (this.isStageLockedByAdmin && !this.isAdmin && !this.isHost) {
          pinHtml = `
            <button class="tile-dropdown-item action-pin disabled" data-id="${p.identity}" disabled style="opacity: 0.65; cursor: not-allowed;" title="Scena a fost fixată de Administrator">
              <span>🔒</span> <span>Scenă Fixată de Admin</span>
            </button>
          `;
        } else if (this.isAdmin || this.isHost) {
          pinHtml = `
            <button class="tile-dropdown-item action-pin" data-id="${p.identity}">
              <span>${this.pinnedIdentity === p.identity ? '✖️' : '👑'}</span> <span>${this.pinnedIdentity === p.identity ? 'Anulează Evidențierea pe Scenă' : 'Evidențiază pe Scenă (pentru toți)'}</span>
            </button>
          `;
        } else {
          pinHtml = `
            <button class="tile-dropdown-item action-pin" data-id="${p.identity}">
              <span>📌</span> <span>${this.pinnedIdentity === p.identity ? 'Anulează Fixarea pentru mine' : 'Fixează pentru mine'}</span>
            </button>
          `;
        }

        gd.setAttribute('data-target-id', p.identity);
        gd.innerHTML = `
          ${pinHtml}
          ${canRename ? `
            <button class="tile-dropdown-item action-rename" data-id="${p.identity}">
              ${ICONS.pencil} <span>Redenumește</span>
            </button>
          ` : ''}
          ${canToggleMic ? `
            <button class="tile-dropdown-item action-mute" data-id="${p.identity}">
              ${isMuted ? ICONS.micOn : ICONS.micOff} <span>${isMuted ? 'Activează Microfon' : 'Oprește Microfon'}</span>
            </button>
          ` : ''}
          ${canToggleCam ? `
            <button class="tile-dropdown-item action-cam" data-id="${p.identity}">
              ${isCamOff ? ICONS.camOn : ICONS.camOff} <span>${isCamOff ? 'Activează Cameră' : 'Oprește Camera'}</span>
            </button>
          ` : ''}
          <button class="tile-dropdown-item action-toggle-fit" data-id="${p.identity}" title="Comută între încadrare completă 9:16 și decupare">
            <span>📐</span> <span>Comută Încadrare (Fit / Fill)</span>
          </button>
          ${(isHandRaised && canLowerHand) ? `
            <button class="tile-dropdown-item action-lower-hand" data-id="${p.identity}">
              <span>✋ Coboară Mâna</span>
            </button>
          ` : ''}
          ${(canModerateUser && this.isAdmin) ? `
            <button class="tile-dropdown-item danger action-kick" data-id="${p.identity}">
              ${ICONS.trash} <span>Elimină din Cameră</span>
            </button>
          ` : ''}
        `;

        gd.style.display = 'flex';
        gd.classList.add('show');

        // Position flawlessly using viewport coordinates
        const rect = trigger.getBoundingClientRect();
        gd.style.position = 'fixed';
        gd.style.zIndex = '999999';
        gd.style.right = `${Math.max(10, window.innerWidth - rect.right)}px`;
        gd.style.left = 'auto';

        const menuHeight = gd.offsetHeight || 260;
        if (rect.bottom + menuHeight > window.innerHeight - 70) {
          gd.style.top = 'auto';
          gd.style.bottom = `${Math.max(10, window.innerHeight - rect.top + 6)}px`;
        } else {
          gd.style.top = `${rect.bottom + 6}px`;
          gd.style.bottom = 'auto';
        }

        // Also mark the tile as dropdown-open for hover styling
        document.querySelectorAll('.video-tile.dropdown-open').forEach(t => t.classList.remove('dropdown-open'));
        trigger.closest('.video-tile')?.classList.add('dropdown-open');
        return;
      }

      // 2. Click inside the global dropdown itself - handled by dropdown listener
      if (target.closest('#global-tile-dropdown')) {
        return;
      }

      // 3. Tile clicked outside 3-dots on touch devices: first tap reveals controls
      const clickedTile = target.closest('.video-tile') as HTMLElement | null;
      if (clickedTile && !target.closest('.tile-actions-wrapper')) {
        const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
        if (isTouchDevice) {
          if (!clickedTile.classList.contains('tile-touch-active')) {
            document.querySelectorAll('.video-tile.tile-touch-active').forEach(t => t.classList.remove('tile-touch-active'));
            clickedTile.classList.add('tile-touch-active');
            setTimeout(() => {
              clickedTile.classList.remove('tile-touch-active');
            }, 3500);
            return;
          }
        }
      }

      // 4. Clicked anywhere else: close dropdowns and clear touch states
      this.closeAllDropdowns();
      if (!target.closest('.video-tile')) {
        document.querySelectorAll('.video-tile.tile-touch-active').forEach(t => t.classList.remove('tile-touch-active'));
      }
    };
    (this as any)._globalTileClickListener = docClickListener;
    document.addEventListener('click', docClickListener);

    // Double click on tile toggles fit
    document.addEventListener('dblclick', (e) => {
      const tileEl = (e.target as HTMLElement).closest('.video-tile') as HTMLElement | null;
      if (!tileEl) return;
      const vid = tileEl.querySelector('video') as HTMLVideoElement | null;
      if (!vid) return;
      const currentFit = tileEl.getAttribute('data-fit-mode') || (vid.classList.contains('fit-contain') ? 'contain' : 'cover');
      const newFit = currentFit === 'contain' ? 'cover' : 'contain';
      tileEl.setAttribute('data-fit-mode', newFit);
      this.adaptVideoElementFit(vid, tileEl);
      this.showFloatingToast(newFit === 'contain' ? '📐 Încadrare completă (Fără tăiere / Decupare 9:16)' : '🔍 Umplere completă a cadrului (Zoom / Crop)');
    });

    window.addEventListener('resize', () => this.closeAllDropdowns(), { passive: true });
  }

  private closeAllDropdowns() {
    const gd = document.getElementById('global-tile-dropdown');
    if (gd) {
      gd.classList.remove('show');
      gd.style.display = 'none';
      gd.removeAttribute('data-target-id');
    }
    document.querySelectorAll('.video-tile.dropdown-open').forEach(d => {
      d.classList.remove('dropdown-open');
    });
  }

  private openRenameModal(identity: string) {
    const isSelf = identity === 'self';
    const currentName = isSelf
      ? (this.manager.displayName || this.config.displayName || 'Participant')
      : (this.participants.find(part => part.identity === identity)?.name || '');
    if (!currentName && !isSelf) return;

    const modal = document.getElementById('rename-modal');
    const input = document.getElementById('rename-input') as HTMLInputElement;
    const saveBtn = document.getElementById('btn-save-rename');
    const cancelBtn = document.getElementById('btn-cancel-rename');

    if (modal && input) {
      input.value = currentName.replace(' (Tu)', '').trim();
      modal.style.display = 'flex';
      input.focus();

      const onSave = () => {
        const newName = input.value.trim();
        if (newName) {
          if (isSelf) {
            this.config.displayName = newName;
            this.manager.displayName = newName;
            try { localStorage.setItem('vorbello_display_name', newName); } catch (_) {}
            const selfP = this.participants.find(part => part.identity === 'self');
            if (selfP) selfP.name = `${newName} (Tu)`;
            this.manager.sendDataMessage({
              type: 'moderation',
              action: 'rename-user',
              targetIdentity: this.manager.getRoom()?.localParticipant?.identity || 'self',
              newName: newName,
            }).catch(() => {});
          } else {
            const p = this.participants.find(part => part.identity === identity);
            if (p) p.name = newName;
            this.manager.sendDataMessage({
              type: 'moderation',
              action: 'rename-user',
              targetIdentity: identity,
              newName: newName,
            }).catch(() => {});
          }
          this.renderVideoGrid();
          this.renderParticipantsList();
        }
        cleanupModal();
      };

      const onCancel = () => {
        cleanupModal();
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Enter') onSave();
        if (e.key === 'Escape') onCancel();
      };

      const cleanupModal = () => {
        modal.style.display = 'none';
        saveBtn?.removeEventListener('click', onSave);
        cancelBtn?.removeEventListener('click', onCancel);
        input.removeEventListener('keydown', onKeyDown);
      };

      saveBtn?.addEventListener('click', onSave);
      cancelBtn?.addEventListener('click', onCancel);
      input.addEventListener('keydown', onKeyDown);
    }
  }

  private renderChatMessages() {
    const container = document.getElementById('chat-history');
    if (!container) return;

    container.innerHTML = this.messages.map(m => {
      let fileBlock = '';
      if (m.file && m.file.dataUrl) {
        const ext = m.file.name.split('.').pop()?.toLowerCase() || '';
        let fileIcon = '📄';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) fileIcon = '🖼️';
        else if (['pdf'].includes(ext)) fileIcon = '📕';
        else if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) fileIcon = '📝';
        else if (['xls', 'xlsx', 'csv'].includes(ext)) fileIcon = '📊';
        else if (['ppt', 'pptx', 'key'].includes(ext)) fileIcon = '📽️';
        else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) fileIcon = '📦';
        else if (['mp3', 'wav', 'm4a', 'ogg'].includes(ext)) fileIcon = '🎵';
        else if (['mp4', 'webm', 'mov'].includes(ext)) fileIcon = '🎬';

        const sizeFormatted = m.file.size > 1024 * 1024
          ? (m.file.size / (1024 * 1024)).toFixed(1) + ' MB'
          : Math.max(1, Math.round(m.file.size / 1024)) + ' KB';

        const isImg = m.file.type?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);

        fileBlock = `
          <div class="chat-file-card" style="margin-top: 6px; background: rgba(0,0,0,0.25); border: 1px solid rgba(255,255,255,0.12); border-radius: 8px; padding: 8px; display: flex; flex-direction: column; gap: 6px;">
            ${isImg ? `
              <div style="max-height: 140px; overflow: hidden; border-radius: 6px; cursor: pointer; background: rgba(0,0,0,0.3);" onclick="window.vorbelloDownloadFile('${m.id}')" title="Atingeți pentru descărcare">
                <img src="${m.file.dataUrl}" alt="${m.file.name}" style="width: 100%; height: auto; object-fit: contain; display: block;" />
              </div>
            ` : ''}
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 6px; overflow: hidden; min-width: 0;">
                <span style="font-size: 1.25rem; flex-shrink: 0;">${fileIcon}</span>
                <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  <div style="font-weight: 600; font-size: 0.8rem; color: #f8fafc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${m.file.name}">${m.file.name}</div>
                  <div style="font-size: 0.68rem; color: var(--text-muted);">${sizeFormatted}</div>
                </div>
              </div>
              <button type="button" onclick="window.vorbelloDownloadFile('${m.id}')" class="btn btn-secondary btn-sm" style="padding: 3px 8px; font-size: 0.72rem; flex-shrink: 0; text-decoration: none; display: inline-flex; align-items: center; gap: 4px; border-radius: 6px; cursor: pointer;" title="Descarcă fișierul pe dispozitiv">
                ⬇️ Descarcă
              </button>
            </div>
          </div>
        `;
      } else if (m.file && !m.file.dataUrl) {
        const ext = m.file.name.split('.').pop()?.toLowerCase() || '';
        let fileIcon = '📄';
        if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) fileIcon = '🖼️';
        else if (['pdf'].includes(ext)) fileIcon = '📕';
        else if (['doc', 'docx', 'odt', 'rtf'].includes(ext)) fileIcon = '📝';
        else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) fileIcon = '📦';

        const sizeFormatted = m.file.size > 1024 * 1024
          ? (m.file.size / (1024 * 1024)).toFixed(1) + ' MB'
          : Math.max(1, Math.round(m.file.size / 1024)) + ' KB';

        const prog = (m.file as any).progress || 0;

        fileBlock = `
          <div class="chat-file-card" style="margin-top: 6px; background: rgba(0,0,0,0.25); border: 1px solid rgba(56,189,248,0.3); border-radius: 8px; padding: 8px; display: flex; flex-direction: column; gap: 6px;">
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px;">
              <div style="display: flex; align-items: center; gap: 6px; overflow: hidden; min-width: 0;">
                <span style="font-size: 1.25rem; flex-shrink: 0;">${fileIcon}</span>
                <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
                  <div style="font-weight: 600; font-size: 0.8rem; color: #f8fafc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="${m.file.name}">${m.file.name}</div>
                  <div style="font-size: 0.68rem; color: #38bdf8;">${sizeFormatted} • Se recepționează (<span id="transfer-pct-${m.id}">${prog}%</span>)...</div>
                </div>
              </div>
              <div style="font-size: 0.72rem; color: #38bdf8; display: flex; align-items: center; gap: 4px;">
                <span style="display: inline-block; width: 12px; height: 12px; border: 2px solid #38bdf8; border-top-color: transparent; border-radius: 50%; animation: spin 0.8s linear infinite;"></span>
              </div>
            </div>
            <div style="width: 100%; height: 4px; background: rgba(255,255,255,0.08); border-radius: 2px; overflow: hidden;">
              <div id="transfer-bar-${m.id}" style="width: ${prog}%; height: 100%; background: linear-gradient(90deg, #38bdf8, #818cf8); transition: width 0.15s ease;"></div>
            </div>
          </div>
        `;
      }

      return `
        <div class="chat-bubble ${m.isSelf ? 'self' : ''}">
          <div class="chat-sender">${m.senderName} • ${m.timestamp}</div>
          ${m.text ? `<div>${m.text}</div>` : ''}
          ${fileBlock}
        </div>
      `;
    }).join('');

    container.scrollTop = container.scrollHeight;
  }

  private renderParticipantsList() {
    const container = document.getElementById('participants-list');
    if (!container) return;

    const waitingSectionHtml = ((this.isAdmin || this.isModerator) && this.waitingParticipants.length > 0) ? `
      <div class="waiting-room-panel-card" style="background: rgba(245, 158, 11, 0.08); border: 1px solid rgba(245, 158, 11, 0.35); border-radius: 10px; padding: 10px; margin-bottom: 12px;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
          <span style="font-weight: 700; font-size: 0.8rem; color: #fbbf24; display: flex; align-items: center; gap: 6px;">
            ⏳ În Sala de Așteptare (${this.waitingParticipants.length})
          </span>
          <button class="btn btn-primary btn-sm btn-admit-all-panel" style="font-size: 0.7rem; padding: 2px 8px; border-radius: 6px;">
            Admite pe toți
          </button>
        </div>
        <div style="display: flex; flex-direction: column; gap: 6px;">
          ${this.waitingParticipants.map(wp => `
            <div style="display: flex; align-items: center; justify-content: space-between; background: rgba(0,0,0,0.25); padding: 6px 8px; border-radius: 6px;">
              <div style="font-size: 0.8rem; font-weight: 600; color: #f8fafc; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 125px;" title="${wp.name}">
                ${wp.name}
              </div>
              <div style="display: flex; gap: 4px;">
                <button class="btn btn-primary btn-sm btn-admit-user-panel" data-id="${wp.identity}" style="padding: 2px 7px; font-size: 0.72rem; border-radius: 4px;">Admite</button>
                <button class="btn btn-secondary btn-sm btn-reject-user-panel" data-id="${wp.identity}" style="padding: 2px 7px; font-size: 0.72rem; border-radius: 4px; color: #f87171;">Respinge</button>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    ` : '';

    // Sort participants: self first, then raised hands float to the top (Zoom & Meet style), then admins/mods, then alphabetical
    const sortedParticipants = [...this.participants].sort((a, b) => {
      if (a.identity === 'self') return -1;
      if (b.identity === 'self') return 1;
      const aRaised = (a.isHandRaised ? 1 : 0);
      const bRaised = (b.isHandRaised ? 1 : 0);
      if (aRaised !== bRaised) return bRaised - aRaised;
      const aAdmin = a.role === 'admin' ? 2 : (a.role === 'moderator' ? 1 : 0);
      const bAdmin = b.role === 'admin' ? 2 : (b.role === 'moderator' ? 1 : 0);
      if (aAdmin !== bAdmin) return bAdmin - aAdmin;
      return a.name.localeCompare(b.name);
    });

    const query = this.participantSearchQuery.trim().toLowerCase();
    const filteredParticipants = query
      ? sortedParticipants.filter(p => {
          const nameMatch = p.name.toLowerCase().includes(query);
          const isSelfTu = p.identity === 'self' && ('tu'.includes(query) || 'self'.includes(query));
          const roleMatch = (p.role && p.role.toLowerCase().includes(query)) || (this.isAdmin && p.identity === 'self' && 'administrator'.includes(query));
          return nameMatch || isSelfTu || roleMatch;
        })
      : sortedParticipants;

    const panelTitle = document.getElementById('panel-title');
    if (panelTitle && this.isParticipantsOpen) {
      if (query) {
        panelTitle.innerText = `Participanți (${filteredParticipants.length}/${this.participants.length})`;
      } else {
        panelTitle.innerText = `Participanți Conectați (${this.participants.length})`;
      }
    }

    if (query && filteredParticipants.length === 0) {
      container.innerHTML = waitingSectionHtml + `
        <div style="padding: 2.2rem 1rem; text-align: center; color: var(--text-muted); font-size: 0.85rem;">
          <div style="font-size: 1.8rem; margin-bottom: 0.4rem; opacity: 0.7;">🔍</div>
          <div style="color: #cbd5e1; font-weight: 600;">Niciun participant găsit</div>
          <div style="font-size: 0.75rem; margin-top: 0.3rem; color: #94a3b8;">Nu am găsit rezultate pentru „<span style="color: #38bdf8;">${this.escapeHtml(this.participantSearchQuery.trim())}</span>”</div>
        </div>
      `;
      return;
    }

    container.innerHTML = waitingSectionHtml + filteredParticipants.map(p => {
      const isSelf = p.identity === 'self';
      const isPartAdmin = p.role === 'admin' || (isSelf && this.isAdmin);
      const isPartMod = !isPartAdmin && (p.role === 'moderator' || (isSelf && this.isModerator));
      const roleLabel = isPartAdmin ? '👑 Administrator' : (isPartMod ? '🛡️ Moderator' : 'Participant');
      const subtitle = isSelf 
        ? (this.isAdmin ? 'Tu (👑 Administrator / Gazdă)' : (this.isModerator ? 'Tu (🛡️ Moderator)' : 'Tu (Participant)'))
        : roleLabel;

      return `
        <div class="participant-item" id="part-item-${p.identity}">
          <div style="display: flex; align-items: center; gap: 0.6rem;">
            <div style="width: 32px; height: 32px; border-radius: 50%; background: ${isPartAdmin ? 'linear-gradient(135deg, #f59e0b, #d97706)' : (isPartMod ? 'linear-gradient(135deg, #38bdf8, #0284c7)' : 'var(--accent-gradient)')}; display: flex; align-items: center; justify-content: center; font-size: 0.8rem; font-weight: 700; color: #fff;">
              ${p.name.charAt(0)}
            </div>
            <div>
              <div style="font-weight: 600; font-size: 0.85rem; color: #f8fafc; display: flex; align-items: center; gap: 6px;">
                <span>${p.name}</span>
                ${(isSelf ? this.isLocalHandRaised : p.isHandRaised) ? '<span class="participant-hand-badge" title="Mână Ridicată">✋ Mână</span>' : ''}
              </div>
              <div style="font-size: 0.7rem; color: ${isPartAdmin ? '#fbbf24' : (isPartMod ? '#38bdf8' : 'var(--text-muted)')}; font-weight: ${isPartAdmin || isPartMod ? '600' : '400'};">${subtitle}</div>
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 5px; flex-shrink: 0;">
            ${(this.isAdmin || this.isModerator) && p.isHandRaised && !isSelf ? `
              <button class="btn btn-secondary btn-sm btn-lower-user-hand" data-id="${p.identity}" title="Coboară mâna participantului" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(245, 158, 11, 0.2); border: 1px solid rgba(245, 158, 11, 0.5); color: #fbbf24; font-size: 0.85rem;">
                ✋
              </button>
            ` : ''}
            ${(this.isAdmin || this.isHost || this.isModerator) ? `
              <button class="btn btn-secondary btn-sm btn-pin-user" data-id="${p.identity}" title="${this.pinnedIdentity === p.identity ? 'Anulează Fixarea' : 'Fixează pe Scenă'}" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; ${this.pinnedIdentity === p.identity ? 'background: #6366f1; border: 1px solid #818cf8; color: #fff;' : 'background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #cbd5e1;'}">
                ${ICONS.pin}
              </button>
            ` : ''}
            ${this.isAdmin ? `
              <button class="btn btn-secondary btn-sm btn-rename-user" data-id="${p.identity}" title="Redenumește participant" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #cbd5e1;">
                ${ICONS.pencil}
              </button>
            ` : ''}
            ${!isSelf ? `
              ${(this.isAdmin || this.isModerator) ? `
                <button class="btn btn-secondary btn-sm btn-mute-user" data-id="${p.identity}" title="${p.isMuted ? 'Activează Microfon' : 'Oprește Microfon'}" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; ${p.isMuted ? 'background: rgba(239, 68, 68, 0.2); border: 1px solid rgba(239, 68, 68, 0.45); color: #f87171;' : 'background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.12); color: #cbd5e1;'}">
                  ${p.isMuted ? ICONS.micOffSm : ICONS.micSm}
                </button>
              ` : `
                <span style="font-size: 0.72rem; color: ${p.isMuted ? '#f87171' : '#34d399'}; padding: 3px 6px; background: rgba(255,255,255,0.05); border-radius: 6px;">
                  ${p.isMuted ? '🔇' : '🎤'}
                </span>
              `}
              ${this.isAdmin ? `
                <button class="btn btn-secondary btn-sm btn-wait-user" data-id="${p.identity}" title="Trimite în sala de așteptare" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(245, 158, 11, 0.15); border: 1px solid rgba(245, 158, 11, 0.35); color: #fbbf24;">
                  ${ICONS.hourglass}
                </button>
                <button class="btn btn-danger btn-sm btn-kick-user" data-id="${p.identity}" title="Elimină din conferință" style="width: 28px; height: 28px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 7px; background: rgba(239, 68, 68, 0.18); border: 1px solid rgba(239, 68, 68, 0.4); color: #f87171;">
                  ${ICONS.userX}
                </button>
              ` : ''}
            ` : '<span style="font-size: 0.75rem; color: var(--status-active); font-weight: 600; margin-left: 4px;">● Tu</span>'}
          </div>
        </div>
      `;
    }).join('');

    if (!query && this.participants.length === 1 && this.config.appMode === 'production' && (this.isAdmin || this.isModerator)) {
      container.innerHTML += `
        <div style="padding: 1.25rem 1rem; text-align: center; color: var(--text-muted); font-size: 0.8rem; border: 1px dashed rgba(56, 189, 248, 0.35); background: rgba(56, 189, 248, 0.05); border-radius: 14px; margin: 1.25rem 0.85rem 0.75rem 0.85rem; box-sizing: border-box;">
          <div style="font-size: 1.4rem; margin-bottom: 0.3rem;">👥</div>
          <div style="color: #f1f5f9; font-weight: 600; font-size: 0.82rem;">Ești singur în sală</div>
          <div style="margin-top: 0.25rem; line-height: 1.35; font-size: 0.73rem;">Distribuie linkul conferinței către invitați pentru a se alătura în timp real.</div>
          <button class="btn btn-primary btn-sm btn-empty-copy-link" style="margin-top: 0.65rem; font-size: 0.75rem; padding: 0.35rem 0.9rem; border-radius: 8px;">
            📋 Copiază Linkul Camerei
          </button>
        </div>
      `;
    }

    // Empty state copy link listener
    container.querySelector('.btn-empty-copy-link')?.addEventListener('click', (e) => {
      this.copyInviteLink(e.currentTarget as HTMLElement);
    });

    // Sidebar moderation controls
    container.querySelectorAll('.btn-lower-user-hand').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const p = this.participants.find(part => part.identity === id);
        if (p) {
          p.isHandRaised = false;
          await this.manager.sendDataMessage({
            type: 'hand_raise',
            identity: id,
            name: p.name,
            isRaised: false,
          });
          this.renderVideoGrid();
          this.renderParticipantsList();
        }
      });
    });
    container.querySelectorAll('.btn-pin-user').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const newId = this.pinnedIdentity === id ? null : id;
        this.setPinState(newId, true);
      });
    });

    container.querySelectorAll('.btn-rename-user').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        this.openRenameModal(id!);
      });
    });

    container.querySelectorAll('.btn-mute-user').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        const p = this.participants.find(part => part.identity === id);
        if (p && id) {
          p.isMuted = !p.isMuted;
          await this.manager.sendDataMessage({
            type: 'moderation',
            action: p.isMuted ? 'mute-user' : 'unmute-user',
            targetIdentity: id,
          });
          this.renderVideoGrid();
          this.renderParticipantsList();
        }
      });
    });

    container.querySelectorAll('.btn-kick-user').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) {
          await this.kickParticipant(id);
        }
      });
    });

    container.querySelectorAll('.btn-admit-user-panel').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) this.admitWaitingParticipant(id);
      });
    });

    container.querySelectorAll('.btn-reject-user-panel').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) this.rejectWaitingParticipant(id);
      });
    });

    container.querySelector('.btn-admit-all-panel')?.addEventListener('click', () => {
      this.admitAllWaitingParticipants();
    });

    container.querySelectorAll('.btn-wait-user').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute('data-id');
        if (id) await this.moveParticipantToWaitingRoom(id);
      });
    });
  }

  private updateControlsState() {
    // Mic button
    const micBtn = document.getElementById('btn-toggle-mic');
    const micLabel = document.getElementById('label-mic');
    if (micBtn && micLabel) {
      micBtn.className = `control-btn ${this.manager.isMicEnabled ? 'active-on' : 'active-off'}`;
      micLabel.innerText = this.manager.isMicEnabled ? 'Microfon Pornit' : 'Microfon Oprit';
      const icon = micBtn.querySelector('.btn-icon') as HTMLElement | null;
      if (icon) icon.innerHTML = this.manager.isMicEnabled ? ICONS.micOn : ICONS.micOff;
    }

    // Cam button
    const camBtn = document.getElementById('btn-toggle-cam');
    const camLabel = document.getElementById('label-cam');
    if (camBtn && camLabel) {
      camBtn.className = `control-btn ${this.manager.isCameraEnabled ? 'active-on' : 'active-off'}`;
      camLabel.innerText = this.manager.isCameraEnabled ? 'Cameră Pornită' : 'Cameră Oprită';
      const icon = camBtn.querySelector('.btn-icon') as HTMLElement | null;
      if (icon) icon.innerHTML = this.manager.isCameraEnabled ? ICONS.camOn : ICONS.camOff;
    }

    // Screen button
    const screenBtn = document.getElementById('btn-toggle-screen');
    const screenLabel = document.getElementById('label-screen');
    const isScreenSharing = this.manager.isScreenShareEnabled || (this.manager.getRoom()?.localParticipant?.isScreenShareEnabled ?? false);
    if (screenBtn) {
      screenBtn.className = `control-btn ${isScreenSharing ? 'active-screen' : ''}`;
    }
    if (screenLabel) {
      screenLabel.innerText = isScreenSharing ? 'Oprește Partajarea' : 'Partajează Ecran';
    }

    // Hand button (Opțiunea 1)
    const handBtn = document.getElementById('btn-raise-hand');
    const handLabel = document.getElementById('label-hand');
    const moreBtn = document.getElementById('btn-more-options');
    const handDot = document.getElementById('dock-hand-indicator');
    if (handBtn && handLabel) {
      if (this.isLocalHandRaised) {
        handBtn.classList.add('active-hand');
        handLabel.innerText = 'Coboară Mâna';
      } else {
        handBtn.classList.remove('active-hand');
        handLabel.innerText = 'Ridică Mâna';
      }
    }
    if (this.isLocalHandRaised) {
      moreBtn?.classList.add('has-hand-raised');
      if (handDot) handDot.style.display = 'inline-block';
    } else {
      moreBtn?.classList.remove('has-hand-raised');
      if (handDot) handDot.style.display = 'none';
    }

    // Participants count in button
    const partBtn = document.getElementById('btn-open-participants');
    if (partBtn) {
      const label = partBtn.querySelector('span:last-child') as HTMLElement | null;
      if (label) label.innerText = `Participanți (${this.participants.length})`;
    }

    // Sync view mode button text and icon
    const stageArea = document.getElementById('stage-area');
    const isSpotlightOn = stageArea?.classList.contains('has-spotlight') ?? this.shouldUseFilmstrip();
    const isMany = this.participants.length > 4;
    const viewIcon = document.getElementById('view-mode-icon');
    const viewText = document.getElementById('view-mode-text');
    const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
    const sheetViewText = document.getElementById('sheet-label-view-mode');

    if (isMany) {
      const isPinned = !!this.pinnedIdentity;
      if (viewIcon) viewIcon.innerText = isPinned ? '📌' : '👤';
      if (viewText) viewText.innerText = isPinned ? 'Fixat Admin' : 'Vorbitor Activ';
      if (sheetViewIcon) sheetViewIcon.innerText = isPinned ? '📌' : '👤';
      if (sheetViewText) sheetViewText.innerText = isPinned ? 'Focus: Fixat Admin' : 'Focus: Vorbitor Activ';
    } else {
      if (viewIcon) viewIcon.innerText = isSpotlightOn ? '⊞' : '👤';
      if (viewText) viewText.innerText = isSpotlightOn ? 'Galerie' : 'Vorbitor Activ';
      if (sheetViewIcon) sheetViewIcon.innerText = isSpotlightOn ? '⊞' : '👤';
      if (sheetViewText) sheetViewText.innerText = isSpotlightOn ? 'Mod: Galerie' : 'Mod: Vorbitor';
    }
  }

  private updateSelfTile() {
    const selfTile = document.getElementById('tile-self');
    if (!selfTile) return;

    let existingVideo = selfTile.querySelector('video.self-cam-video') as HTMLVideoElement | null;
    const avatar = selfTile.querySelector('.avatar-placeholder') as HTMLElement | null;

    if (this.manager.isCameraEnabled) {
      const room = this.manager.getRoom();
      const localCamPub = room?.localParticipant?.getTrackPublication(Track.Source.Camera);
      const fallbackStream = this.manager.getLocalCameraStream();

      if (!existingVideo) {
        existingVideo = document.createElement('video');
        existingVideo.className = 'self-cam-video';
        existingVideo.autoplay = true;
        existingVideo.muted = true;
        existingVideo.setAttribute('muted', '');
        existingVideo.playsInline = true;
        existingVideo.setAttribute('playsinline', '');
        existingVideo.setAttribute('webkit-playsinline', '');
        existingVideo.style.cssText = 'position:absolute; inset:0; width:100%; height:100%; object-fit:cover; border-radius:inherit; z-index:1; display:block;';
        selfTile.prepend(existingVideo);

        // Mobile / Tablet touch unlock for media autoplay (both video & audio)
        if (typeof window !== 'undefined' && !(window as any)._vorbelloTouchUnlockAttached) {
          (window as any)._vorbelloTouchUnlockAttached = true;
          const unlock = async () => {
            try {
              if (this.manager.getRoom() && !this.manager.getRoom()?.canPlaybackAudio) {
                await this.manager.getRoom()?.startAudio();
              }
            } catch (_) {}
            document.querySelectorAll('video').forEach(v => {
              if (v.srcObject && v.paused) v.play().catch(() => {});
            });
            document.querySelectorAll('audio').forEach(a => {
              if (a.paused && !a.muted) a.play().catch(() => {});
            });
          };
          ['touchstart', 'touchend', 'pointerdown', 'click'].forEach(evt => {
            window.addEventListener(evt, unlock, { passive: true });
          });
        }
      }

      // Mirror selfie camera (user), do NOT mirror rear environment camera!
      existingVideo.style.transform = this.manager.currentFacingMode === 'environment' ? 'none' : 'scaleX(-1)';
      this.applyVideoEffectToElement(existingVideo);

      const showSelfIfReady = () => {
        if (!existingVideo) return;
        const hasLiveTrack = existingVideo.srcObject instanceof MediaStream &&
          existingVideo.srcObject.getVideoTracks().some(t => t.readyState === 'live');
        if (this.manager.isCameraEnabled && hasLiveTrack) {
          existingVideo.style.display = 'block';
          if (avatar) avatar.style.display = 'none';
        } else {
          existingVideo.style.display = 'none';
          if (avatar) avatar.style.display = 'flex';
        }
      };

      existingVideo.onloadedmetadata = () => {
        showSelfIfReady();
        existingVideo?.play().catch(() => {});
      };
      existingVideo.onloadeddata = showSelfIfReady;
      existingVideo.onplaying = showSelfIfReady;
      existingVideo.onresize = showSelfIfReady;

      const activeLocalStream = fallbackStream || this.manager.getLocalCameraStream();
      if (localCamPub && localCamPub.track) {
        if (!localCamPub.track.attachedElements.includes(existingVideo) || !existingVideo.srcObject) {
          localCamPub.track.attach(existingVideo);
        }
        existingVideo.play().catch(() => {});
        showSelfIfReady();
      } else if (activeLocalStream) {
        if (existingVideo.srcObject !== activeLocalStream) {
          existingVideo.srcObject = activeLocalStream;
        }
        existingVideo.play().catch(() => {});
        showSelfIfReady();
      } else {
        existingVideo.style.display = 'none';
        if (avatar) avatar.style.display = 'flex';
      }
    } else {
      if (existingVideo) {
        existingVideo.style.display = 'none';
      }
      if (avatar) avatar.style.display = 'flex';
    }
  }

  public async updateTelemetryPopover(isConnected?: boolean, statusDetail?: string): Promise<void> {
    const isConn = isConnected !== undefined ? isConnected : (this.manager.getRoom()?.state === 'connected' || this.isP2PMode);
    const vpsUrlEl = document.getElementById('telemetry-vps-url');
    const connStateEl = document.getElementById('telemetry-conn-state');
    const rttEl = document.getElementById('telemetry-rtt-val');
    const camValEl = document.getElementById('telemetry-cam-val');
    const upEl = document.getElementById('telemetry-upload-bitrate');
    const downEl = document.getElementById('telemetry-download-bitrate');
    const lossEl = document.getElementById('telemetry-loss-val');
    const limitEl = document.getElementById('telemetry-quality-limit');

    if (vpsUrlEl) {
      if (this.isP2PMode) {
        vpsUrlEl.textContent = 'app.vorbello.eu (Mesh P2P WebRTC Direct)';
        vpsUrlEl.style.color = '#34d399';
      } else {
        vpsUrlEl.textContent = this.config.livekitUrl || '217.217.233.20:7880';
        vpsUrlEl.style.color = '#38bdf8';
      }
    }

    if (connStateEl) {
      if (this.isP2PMode) {
        connStateEl.style.color = '#10b981';
        connStateEl.textContent = '● Conectat Direct (P2P WebRTC Mesh)';
      } else if (isConn) {
        connStateEl.style.color = '#10b981';
        connStateEl.textContent = '● Conectat Stabil (WebRTC SFU)';
      } else {
        connStateEl.style.color = '#eab308';
        connStateEl.textContent = statusDetail || '🟡 În Așteptare Server VPS (Port 7880)';
      }
    }

    // Query real-time statistics from WebRTC peer connection
    let realStats = null;
    try {
      realStats = await this.manager.getRealtimeStats();
    } catch (_) {}

    if (rttEl) {
      const rttVal = realStats?.rttMs || 18;
      if (this.isP2PMode) {
        rttEl.textContent = `< ${rttVal} ms (P2P Direct)`;
        rttEl.style.color = '#34d399';
      } else if (isConn) {
        rttEl.textContent = `${rttVal} ms (${rttVal < 35 ? 'Excelent' : (rttVal < 100 ? 'Bun' : 'Ridicată')})`;
        rttEl.style.color = rttVal < 50 ? '#34d399' : (rttVal < 120 ? '#fbbf24' : '#f87171');
      } else {
        rttEl.textContent = '-- ms (Fără conexiune la port 7880)';
        rttEl.style.color = '#94a3b8';
      }
    }

    if (camValEl) {
      if (realStats && realStats.outboundResolution) {
        camValEl.textContent = `${realStats.outboundResolution} (${realStats.outboundFps || 30} FPS HD)`;
        camValEl.style.color = '#34d399';
      } else {
        camValEl.textContent = this.manager.isCameraEnabled ? '1280x720 HD (30 FPS)' : 'Oprită';
        camValEl.style.color = this.manager.isCameraEnabled ? '#34d399' : '#f87171';
      }
    }

    if (upEl && realStats) {
      upEl.textContent = `${realStats.outboundBitrateKbps.toLocaleString()} kbps`;
      upEl.style.color = realStats.outboundBitrateKbps > 500 ? '#38bdf8' : (realStats.outboundBitrateKbps > 0 ? '#34d399' : '#94a3b8');
    }

    if (downEl && realStats) {
      downEl.textContent = `${realStats.inboundBitrateKbps.toLocaleString()} kbps`;
      downEl.style.color = realStats.inboundBitrateKbps > 500 ? '#38bdf8' : (realStats.inboundBitrateKbps > 0 ? '#34d399' : '#94a3b8');
    }

    if (lossEl && realStats) {
      lossEl.textContent = `${realStats.packetLossPercent}%`;
      lossEl.style.color = realStats.packetLossPercent === 0 ? '#34d399' : (realStats.packetLossPercent < 2 ? '#fbbf24' : '#f87171');
    }

    if (limitEl && realStats) {
      if (realStats.qualityLimitation === 'bandwidth') {
        limitEl.textContent = '⚠️ Limitare Rețea (Barieră Lățime Bandă)';
        limitEl.style.color = '#f87171';
      } else if (realStats.qualityLimitation === 'cpu') {
        limitEl.textContent = '⚠️ Limitare CPU Dispozitiv';
        limitEl.style.color = '#fbbf24';
      } else {
        limitEl.textContent = '✅ Fără limitări (Capacitate optimă)';
        limitEl.style.color = '#34d399';
      }
    }

    const helpEl = document.getElementById('telemetry-status-help');
    if (helpEl) {
      if (this.isP2PMode) {
        helpEl.innerHTML = `<span style="color: #34d399;">✅ Conexiune directă activă între dispozitive (P2P WebRTC prin <code>app.vorbello.eu</code>). Nu depinde de VPS.</span>`;
      } else if (isConn) {
        helpEl.innerHTML = `<span style="color: #34d399;">✅ Conexiune activă la serverul SFU (${this.config.livekitUrl || 'live.vorbello.eu'}). Transfer date optimizat.</span>`;
      } else {
        helpEl.innerHTML = `<span style="color: #fbbf24;">⚠️ Serverul LiveKit (<code>${this.config.livekitUrl || 'wss://live.vorbello.eu'}</code>) nu răspunde. Asigurați-vă că este pornit VPS-ul sau comutați în Mod Test.</span>`;
      }
    }
  }

  private startTelemetryMonitoring(): void {
    this.stopTelemetryMonitoring();
    this.updateTelemetryPopover();
    this.telemetryInterval = setInterval(() => {
      const popover = document.getElementById('network-telemetry-popover');
      if (popover && popover.style.display !== 'none') {
        this.updateTelemetryPopover();
      } else {
        this.stopTelemetryMonitoring();
      }
    }, 1000);
  }

  private stopTelemetryMonitoring(): void {
    if (this.telemetryInterval) {
      clearInterval(this.telemetryInterval);
      this.telemetryInterval = null;
    }
  }

  public activateInteractiveDemoMode(): void {
    const pill = document.getElementById('conf-status-pill');
    if (pill) {
      pill.innerHTML = `<span class="conf-quality-dot" style="background: #10b981;"></span> Mod Test / Demo Activ`;
    }
    this.updateTelemetryPopover(true, 'Mod Demo Local Activ');
    this.updateSelfTile();

    this.addOrUpdateParticipant('part-ana', 'Ana Popescu (Moderator)');
    this.addOrUpdateParticipant('part-mihai', 'Mihai Ionescu (Membru)');
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();

    this.messages.push({
      id: 'msg-' + Date.now(),
      senderName: 'Ana Popescu',
      senderIdentity: 'part-ana',
      text: 'Modul test / demo Vorbello este activ! Puteți testa camera, microfonul, partajarea de ecran și reacțiile.',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      isSelf: false,
    });
    this.renderChatMessages();
    this.showFloatingToast('🧪 Modul test / demo Vorbello a fost activat!');
  }

  public async retryVpsConnection(): Promise<void> {
    const pill = document.getElementById('conf-status-pill');
    if (pill) {
      pill.innerHTML = `<span class="conf-quality-dot" style="background: #38bdf8;"></span> Se conectează la VPS...`;
    }
    this.showFloatingToast(`Se reîncearcă conexiunea VPS (${this.config.livekitUrl})...`);
    try {
      await this.manager.connect(this.config.livekitUrl, this.config.token);
      if (pill) {
        pill.innerHTML = `<span class="conf-quality-dot" style="background: var(--status-active);"></span> Server VPS Conectat HD`;
      }
      this.updateTelemetryPopover(true, '● Conectat Stabil (WebRTC SFU)');
      this.updateSelfTile();
      this.renderVideoGrid();
      this.showFloatingToast('✅ Conexiune stabilită cu succes la serverul VPS!');
    } catch (err) {
      console.warn('[Vorbello Conference] Retry VPS connection failed:', err);
      if (pill) {
        pill.innerHTML = `<span class="conf-quality-dot" style="background: #eab308;"></span> În Așteptare Server VPS (Port 7880)`;
      }
      this.updateTelemetryPopover(false, 'În Așteptare Server VPS (Port 7880)');
      this.showFloatingToast('⚠️ Serverul VPS nu răspunde la wss://live.vorbello.eu. Puteți activa Mod Test.');
    }
  }

  public getRoomInviteUrl(): string {
    const origin = typeof window !== 'undefined' && window.location.origin && window.location.origin.startsWith('http')
      ? window.location.origin
      : 'https://app.vorbello.eu';
    return `${origin}/?room=${encodeURIComponent(this.config.roomSlug)}`;
  }

  public copyInviteLink(btnEl?: HTMLElement | null): void {
    if (!this.canInvite) {
      this.showFloatingToast('⚠️ Doar Gazda/Administratorul sălii are permisiunea de a distribui invitații.');
      return;
    }
    const url = this.getRoomInviteUrl();
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
    if (btnEl) {
      const original = btnEl.innerHTML;
      btnEl.innerHTML = '✅ Link Copiat!';
      setTimeout(() => { btnEl.innerHTML = original; }, 2200);
    }
    this.showFloatingToast('🔗 Linkul conferinței a fost copiat în clipboard!');
  }

  public copyInviteText(btnEl?: HTMLElement | null): void {
    if (!this.canInvite) {
      this.showFloatingToast('⚠️ Doar Gazda/Administratorul sălii are permisiunea de a distribui invitații.');
      return;
    }
    const url = this.getRoomInviteUrl();
    const organizer = this.config.roomSettings?.creatorName || this.config.displayName || 'Gazda Conferinței';
    const pin = (this.config.roomSettings as any)?.pinCode ? `\n🔑 Cod PIN Acces: ${(this.config.roomSettings as any).pinCode}` : '';
    const text = [
      `Invitație la Videoconferință • Vorbello`,
      ``,
      `Ați fost invitat(ă) să participați la conferința audio-video:`,
      `📌 Eveniment / Sală: ${this.config.roomName}`,
      `👤 Organizator: ${organizer}${pin}`,
      ``,
      `🔗 Conectare Directă (clic pe link pentru acces):`,
      `${url}`,
      ``,
      `ℹ️ Vă puteți conecta instantaneu direct din browserul web (PC, tabletă sau telefon mobil, fără instalare) sau prin aplicația desktop Vorbello.`,
    ].join('\n');

    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    if (btnEl) {
      const original = btnEl.innerHTML;
      btnEl.innerHTML = '✅ Text Copiat!';
      setTimeout(() => { btnEl.innerHTML = original; }, 2200);
    }
    this.showFloatingToast('📨 Textul complet al invitației a fost copiat!');
  }

  public applyVideoEffect(effect: string): void {
    this.selectedVideoEffect = effect;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('vorbello_video_effect', effect);
    }
    const selfVideo = document.querySelector('video.self-cam-video') as HTMLVideoElement | null;
    if (selfVideo) {
      this.applyVideoEffectToElement(selfVideo);
    }
  }

  public applyVideoEffectToElement(videoEl: HTMLVideoElement): void {
    videoEl.classList.remove('video-filter-none', 'video-filter-blur-soft', 'video-filter-blur-heavy', 'video-filter-studio', 'video-filter-warm');
    if (this.selectedVideoEffect && this.selectedVideoEffect !== 'none') {
      videoEl.classList.add(`video-filter-${this.selectedVideoEffect}`);
    }
  }

  public async setPinState(identity: string | null, broadcast: boolean = true): Promise<void> {
    // If scene was locked by Admin/Host, regular attendees cannot unpin or override it
    if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
      this.showFloatingToast('🔒 Scena este fixată de către Administrator.');
      return;
    }

    const canBroadcast = broadcast && (this.isAdmin || this.isHost);

    if (identity) {
      this.pinnedIdentity = identity;
      this.currentViewMode = 'speaker';
    } else {
      this.pinnedIdentity = null;
      // When unpinning: if 4 or fewer participants, return cleanly to Galerie!
      if (this.participants.length <= 4) {
        this.currentViewMode = 'gallery';
      } else {
        this.currentViewMode = 'speaker';
      }
      this.currentStageSpeakerId = null;
      this.lastStageRenderedSpeakerId = null;
      this.candidateSpeakerId = null;
    }
    this.userExplicitlyChangedViewMode = true;

    const isGal = this.currentViewMode === 'gallery';
    const viewIcon = document.getElementById('view-mode-icon');
    const viewText = document.getElementById('view-mode-text');
    const sheetViewIcon = document.getElementById('sheet-icon-view-mode');
    const sheetViewText = document.getElementById('sheet-label-view-mode');
    if (viewIcon) viewIcon.innerText = isGal ? '⊞' : (this.pinnedIdentity ? '📌' : '👤');
    if (viewText) viewText.innerText = isGal ? 'Galerie' : (this.pinnedIdentity ? 'Fixat' : 'Vorbitor Activ');
    if (sheetViewIcon) sheetViewIcon.innerText = isGal ? '⊞' : (this.pinnedIdentity ? '📌' : '👤');
    if (sheetViewText) sheetViewText.innerText = isGal ? 'Mod: Galerie' : (this.pinnedIdentity ? 'Mod: Fixat' : 'Mod: Vorbitor');

    this.updateStageLayout();
    this.renderVideoGrid();
    this.renderParticipantsList();

    const p = this.participants.find(part => part.identity === identity) || (identity === 'self' ? { name: `${this.manager.displayName || this.config.displayName} (Tu)` } : null);
    if (identity && p) {
      if (canBroadcast) {
        this.showFloatingToast(`👑 ${p.name} a fost evidențiat pe scenă pentru toți participanții`);
      } else {
        this.showFloatingToast(`📌 ${p.name} a fost fixat pe ecranul tău`);
      }
    } else if (!identity) {
      if (canBroadcast) {
        this.showFloatingToast(`⊞ Mod Galerie restabilit pentru toți participanții`);
      } else {
        this.showFloatingToast(`⊞ Ai anulat fixarea locală`);
      }
    }

    if (canBroadcast) {
      await this.broadcastAdminLayoutSync();
    }
  }

  public async broadcastAdminLayoutSync(): Promise<void> {
    try {
      const room = this.manager.getRoom();
      const myLiveKitId = room?.localParticipant?.identity || 'self';
      const broadcastPinnedId = this.pinnedIdentity === 'self' 
        ? myLiveKitId 
        : (this.pinnedIdentity || null);
      
      const pinnedPart = this.pinnedIdentity
        ? (this.pinnedIdentity === 'self'
            ? { name: `${this.manager.displayName || this.config.displayName} (Admin)` }
            : this.participants.find(p => p.identity === this.pinnedIdentity))
        : null;

      const isLocked = Boolean(this.pinnedIdentity);
      this.isStageLockedByAdmin = isLocked;

      await this.manager.sendDataMessage({
        type: 'admin_layout_sync',
        senderIdentity: myLiveKitId,
        isPrimaryHost: Boolean(this.isHost),
        viewMode: this.currentViewMode,
        pinnedIdentity: broadcastPinnedId,
        pinnedName: pinnedPart?.name || '',
        adminName: this.config.displayName,
        isStageLocked: isLocked,
        timestamp: Date.now(),
      });
      console.info('[ConferenceUI] Broadcasted admin layout sync to room:', this.currentViewMode, broadcastPinnedId, 'isStageLocked:', isLocked);
    } catch (err) {
      console.warn('[ConferenceUI] Failed to broadcast admin layout sync:', err);
    }
  }

  private updateStageLayout() {
    const stageArea = document.getElementById('stage-area');
    const spotlightContainer = document.getElementById('spotlight-container');
    const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement;
    const spotlightPinnedCard = document.getElementById('spotlight-pinned-card');
    const spotlightTitle = document.getElementById('spotlight-title-text');
    const spotlightBtn = document.getElementById('btn-stop-spotlight');

    if (!stageArea || !spotlightContainer || !spotlightVideo) return;

    const room = this.manager.getRoom();
    const localScreenPub = (room && room.state === 'connected')
      ? room.localParticipant.getTrackPublication(Track.Source.ScreenShare)
      : null;
    const hasLocalPubTrack = !!(localScreenPub && localScreenPub.track);
    const screenStream = this.manager.getLocalScreenStream();
    const isLocalSharing = this.manager.isScreenShareEnabled || (room?.localParticipant?.isScreenShareEnabled ?? false) || hasLocalPubTrack || !!(screenStream && screenStream.active);

    // Check if any remote participant is ACTUALLY sharing screen:
    // Requires authoritative LiveKit flag (isScreenShareEnabled), subscribed track, unmuted, and live MediaStreamTrack.
    let remoteScreenTrack: RemoteTrack | null = null;
    let remoteScreenSharerName = '';
    if (room && room.state === 'connected') {
      for (const rp of room.remoteParticipants.values()) {
        if (!rp.isScreenShareEnabled) continue;
        const screenPub = rp.getTrackPublication(Track.Source.ScreenShare);
        if (screenPub) {
          if (!screenPub.isSubscribed) {
            console.info(`[ConferenceUI] Auto-subscribing remote screen share from ${rp.identity}`);
            screenPub.setSubscribed(true);
          }
          const tr = screenPub.track;
          if (tr) {
            const mst = (tr as any).mediaStreamTrack as MediaStreamTrack | undefined;
            const isAlive = !screenPub.isMuted && !tr.isMuted && !!mst && mst.readyState === 'live' && mst.enabled;
            if (isAlive) {
              remoteScreenTrack = tr;
              remoteScreenSharerName = rp.name || 'Participant';
              break;
            }
          }
        }
      }
    }

    const pinnedParticipant = this.pinnedIdentity
      ? (this.pinnedIdentity === 'self'
          ? (this.participants.find(p => p.identity === 'self') || { identity: 'self', name: `${this.manager.displayName || 'Tu'} (Tu)` })
          : this.participants.find(p => p.identity === this.pinnedIdentity))
      : null;

    // Only clear spotlight video when there is NEITHER a screen share NOR a pinned camera on stage.
    // Preserving srcObject when pinned eliminates the black flash/flicker whenever participants join or leave!
    if (!remoteScreenTrack && !isLocalSharing && !pinnedParticipant) {
      if (this.lastAttachedRemoteTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
        this.lastAttachedRemoteTrack = null;
      }
      if (spotlightVideo && spotlightVideo.srcObject) {
        spotlightVideo.srcObject = null;
        spotlightVideo.pause();
      }
    }

    const banner = document.getElementById('prod-waiting-banner');
    if (banner && (isLocalSharing || remoteScreenTrack || pinnedParticipant)) {
      banner.style.display = 'none';
    }

    const switchSpotlightBtn = document.getElementById('btn-switch-spotlight-source');

    if (isLocalSharing) {
      if (this.lastAttachedRemoteTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
        this.lastAttachedRemoteTrack = null;
      }
      // 1. Local screen sharing spotlight mode (I am actively presenting!)
      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.add('is-screen-share');
      spotlightContainer.style.display = 'flex';
      if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
      spotlightVideo.style.display = 'block';
      spotlightVideo.style.transform = 'none';
      spotlightVideo.muted = true;
      spotlightVideo.setAttribute('muted', '');
      spotlightVideo.setAttribute('playsinline', '');

      if (spotlightTitle) {
        const hasAudio = (this.manager.getLocalScreenStream()?.getAudioTracks().length ?? 0) > 0;
        const audioBadge = hasAudio ? ' • 🔊 Cu Sunet' : ' • 🔇 Fără Sunet';
        spotlightTitle.innerText = `Partajare: ${this.selectedSourceName || 'Ecran Activ'} (HD)${audioBadge}`;
      }
      if (spotlightBtn) {
        spotlightBtn.style.display = 'inline-flex';
        spotlightBtn.innerText = 'Oprește Partajarea';
        spotlightBtn.className = 'btn btn-danger btn-sm';
      }
      if (switchSpotlightBtn) {
        switchSpotlightBtn.style.display = 'inline-flex';
      }

      if (screenStream) {
        if (spotlightVideo.srcObject !== screenStream) {
          spotlightVideo.srcObject = screenStream;
        }
      } else if (localScreenPub && localScreenPub.track) {
        try {
          localScreenPub.track.attach(spotlightVideo);
        } catch (_) {}
      }
      spotlightVideo.muted = true;
      spotlightVideo.playsInline = true;
      spotlightVideo.setAttribute('muted', '');
      spotlightVideo.setAttribute('playsinline', '');
      spotlightVideo.onloadedmetadata = () => {
        spotlightVideo.play().catch(() => {});
      };
      spotlightVideo.play().catch(e => {
        if (e.name !== 'AbortError') console.warn('Spotlight local play error:', e);
      });
    } else if (remoteScreenTrack) {
      if (switchSpotlightBtn) switchSpotlightBtn.style.display = 'none';
      // 2. Remote screen sharing spotlight mode (Someone else is presenting)
      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.add('is-screen-share');
      spotlightContainer.style.display = 'flex';
      if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
      spotlightVideo.style.display = 'block';
      spotlightVideo.style.transform = 'none';
      spotlightVideo.muted = true;
      spotlightVideo.playsInline = true;
      spotlightVideo.setAttribute('muted', '');
      spotlightVideo.setAttribute('playsinline', '');

      if (spotlightTitle) {
        let remoteHasAudio = false;
        if (room && room.state === 'connected') {
          for (const rp of room.remoteParticipants.values()) {
            if (rp.isScreenShareEnabled) {
              const sAudio = rp.getTrackPublication(Track.Source.ScreenShareAudio);
              if (sAudio && sAudio.isSubscribed && !sAudio.isMuted) {
                remoteHasAudio = true;
                break;
              }
            }
          }
        }
        const audioBadge = remoteHasAudio ? ' • 🔊 Cu Sunet' : ' • 🔇 Fără Sunet';
        spotlightTitle.innerText = `🖥️ Ecran Partajat de ${remoteScreenSharerName}${audioBadge}`;
      }
      if (spotlightBtn) {
        spotlightBtn.style.display = 'none';
      }

      if (this.lastAttachedRemoteTrack && this.lastAttachedRemoteTrack !== remoteScreenTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
      }
      this.lastAttachedRemoteTrack = remoteScreenTrack;

      if (!remoteScreenTrack.attachedElements.includes(spotlightVideo) || !spotlightVideo.srcObject) {
        try {
          remoteScreenTrack.attach(spotlightVideo);
          console.info('[ConferenceUI] Attached remote screen share track cleanly to spotlight');
        } catch (e) {
          console.warn('[ConferenceUI] Spotlight remote attach error:', e);
        }
      }
      spotlightVideo.play().catch(e => {
        if (e.name !== 'AbortError') console.warn('Spotlight remote play error:', e);
      });
    } else if (pinnedParticipant) {
      // 1. Pinned participant spotlight mode (ALWAYS takes precedence over default gallery!)
      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.remove('is-screen-share');
      spotlightContainer.style.display = 'flex';

      if (spotlightTitle) {
        spotlightTitle.innerText = `📌 Fixat pe Scenă: ${pinnedParticipant.name}`;
      }
      if (spotlightBtn) {
        if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
          spotlightBtn.style.display = 'inline-flex';
          spotlightBtn.innerText = '🔒 Fixat de Admin';
          spotlightBtn.className = 'btn btn-secondary btn-sm';
          spotlightBtn.setAttribute('title', 'Scena este fixată de Administrator');
          spotlightBtn.style.opacity = '0.85';
          spotlightBtn.style.cursor = 'default';
        } else {
          spotlightBtn.style.display = 'inline-flex';
          spotlightBtn.innerText = '✕ Anulează Fixarea';
          spotlightBtn.className = 'btn btn-secondary btn-sm';
          spotlightBtn.setAttribute('title', 'Anulează fixarea pe scenă');
          spotlightBtn.style.opacity = '1';
          spotlightBtn.style.cursor = 'pointer';
        }
      }

      const isSelf = pinnedParticipant.identity === 'self';
      let activeStream: MediaStream | null = null;
      let activeTrack: RemoteTrack | null = null;

      if (isSelf) {
        if (this.manager.isCameraEnabled) {
          const room = this.manager.getRoom();
          const localPub = room?.localParticipant?.getTrackPublication(Track.Source.Camera);
          if (localPub && localPub.track && !localPub.isMuted) {
            activeTrack = localPub.track as any;
          }
          activeStream = this.manager.getLocalCameraStream();
          if (!activeStream && localPub && localPub.track && (localPub.track as any).mediaStreamTrack) {
            activeStream = new MediaStream([(localPub.track as any).mediaStreamTrack]);
          }
        }
      } else {
        const room = this.manager.getRoom();
        if (room && room.state === 'connected') {
          const remotePart = room.remoteParticipants.get(pinnedParticipant.identity);
          if (remotePart) {
            const videoPub = remotePart.getTrackPublication(Track.Source.Camera);
            if (videoPub && videoPub.track && !videoPub.isMuted) {
              activeTrack = videoPub.track;
              if (videoPub.track.mediaStream) {
                activeStream = videoPub.track.mediaStream;
              } else if ((videoPub.track as any).mediaStreamTrack) {
                activeStream = new MediaStream([(videoPub.track as any).mediaStreamTrack]);
              }
            }
          }
        }
      }

      // Mirror spotlight video only if local camera and front-facing
      if (isSelf) {
        spotlightVideo.style.transform = this.manager.currentFacingMode === 'environment' ? 'none' : 'scaleX(-1)';
      } else {
        spotlightVideo.style.transform = 'none';
      }

      const isDifferentTarget = this.lastStageRenderedSpeakerId !== pinnedParticipant.identity;

      if (activeTrack || activeStream) {
        if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
        spotlightVideo.style.display = 'block';
        spotlightVideo.muted = true;
        spotlightVideo.setAttribute('muted', '');
        spotlightVideo.setAttribute('playsinline', '');

        if (isDifferentTarget || !spotlightVideo.srcObject) {
          if (this.lastAttachedRemoteTrack) {
            try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
            this.lastAttachedRemoteTrack = null;
          }
          // Reset srcObject to force Chromium to switch rendering pipeline cleanly
          spotlightVideo.pause();
          spotlightVideo.srcObject = null;

          if (isSelf) {
            this.lastAttachedRemoteTrack = null;
            if (activeStream) {
              spotlightVideo.srcObject = activeStream;
            } else if (activeTrack) {
              activeTrack.attach(spotlightVideo);
            }
          } else {
            if (activeTrack) {
              this.lastAttachedRemoteTrack = activeTrack;
              activeTrack.attach(spotlightVideo);
            } else if (activeStream) {
              this.lastAttachedRemoteTrack = null;
              spotlightVideo.srcObject = activeStream;
            }
          }
          this.lastStageRenderedSpeakerId = pinnedParticipant.identity;
          spotlightVideo.play().catch(e => {
            if (e.name !== 'AbortError') console.warn('Spotlight pin play error:', e);
          });
        }
      } else {
        if (this.lastAttachedRemoteTrack) {
          try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
          this.lastAttachedRemoteTrack = null;
        }
        spotlightVideo.style.display = 'none';
        if (spotlightVideo.srcObject) {
          spotlightVideo.pause();
          spotlightVideo.srcObject = null;
        }
        this.lastStageRenderedSpeakerId = null;
        if (spotlightPinnedCard) {
          spotlightPinnedCard.style.display = 'flex';
          const avatarChar = document.getElementById('spotlight-avatar-char');
          const pinnedName = document.getElementById('spotlight-pinned-name');
          if (avatarChar) avatarChar.innerText = pinnedParticipant.name.charAt(0);
          if (pinnedName) pinnedName.innerText = pinnedParticipant.name;
        }
      }
    } else if (this.shouldUseFilmstrip() && this.participants.length > 1) {
      // 2. Automatic Side-by-Side Spotlight Mode (Desktop with 5+ participants, or speaker mode)
      // Hero active speaker on main stage, all participant miniatures stacked in right-hand column
      let targetSpeakerId: string | null = this.pinnedIdentity;
      if (!targetSpeakerId && this.currentStageSpeakerId) {
        targetSpeakerId = this.currentStageSpeakerId;
      }
      if (!targetSpeakerId && this.activeSpeakerIdentity) {
        targetSpeakerId = this.activeSpeakerIdentity;
      }
      if (!targetSpeakerId) {
        const adminP = this.participants.find(p => p.role === 'admin') || this.participants.find(p => p.identity === 'self');
        targetSpeakerId = adminP ? adminP.identity : (this.participants[0]?.identity ?? 'self');
      }
      const speakerPart = this.participants.find(p => p.identity === targetSpeakerId) || this.participants[0];

      stageArea.classList.add('has-spotlight');
      spotlightContainer.classList.remove('is-screen-share');
      spotlightContainer.style.display = 'flex';

      if (spotlightTitle) {
        spotlightTitle.innerText = this.pinnedIdentity
          ? `📌 Fixat pe Scenă: ${speakerPart.name}`
          : `👤 Vorbitor Activ: ${speakerPart.name}`;
      }
      if (spotlightBtn) {
        spotlightBtn.style.display = this.pinnedIdentity ? 'inline-block' : 'none';
        spotlightBtn.innerText = 'Anulează Fixarea';
      }

      const isSelf = speakerPart.identity === 'self';
      let activeStream: MediaStream | null = null;
      let activeTrack: RemoteTrack | null = null;

      if (isSelf) {
        if (this.manager.isCameraEnabled) {
          activeStream = this.manager.getLocalCameraStream();
        }
      } else {
        const room = this.manager.getRoom();
        if (room && room.state === 'connected') {
          const remotePart = room.remoteParticipants.get(speakerPart.identity);
          if (remotePart) {
            const videoPub = remotePart.getTrackPublication(Track.Source.Camera);
            if (videoPub && videoPub.track && !videoPub.isMuted) {
              activeTrack = videoPub.track;
              if (videoPub.track.mediaStream) {
                activeStream = videoPub.track.mediaStream;
              } else if ((videoPub.track as any).mediaStreamTrack) {
                activeStream = new MediaStream([(videoPub.track as any).mediaStreamTrack]);
              }
            }
          }
        }
      }

      // Avoid reattaching or restarting the video element if it's already showing this speaker/track
      const isSameSpeaker = this.lastStageRenderedSpeakerId === targetSpeakerId;
      if (activeTrack || activeStream) {
        if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
        spotlightVideo.style.display = 'block';
        spotlightVideo.style.transform = (isSelf && this.manager.currentFacingMode !== 'environment') ? 'scaleX(-1)' : 'none';
        spotlightVideo.muted = true;
        spotlightVideo.setAttribute('muted', '');
        spotlightVideo.setAttribute('playsinline', '');

        if (!isSameSpeaker || spotlightVideo.srcObject !== (activeTrack?.mediaStream || activeStream)) {
          if (this.lastAttachedRemoteTrack && this.lastAttachedRemoteTrack !== activeTrack) {
            try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
            this.lastAttachedRemoteTrack = null;
          }
          if (activeTrack) {
            this.lastAttachedRemoteTrack = activeTrack;
            activeTrack.attach(spotlightVideo);
          } else if (activeStream && spotlightVideo.srcObject !== activeStream) {
            this.lastAttachedRemoteTrack = null;
            spotlightVideo.srcObject = activeStream;
          }
        }
        spotlightVideo.play().catch(e => {
          if (e.name !== 'AbortError') console.warn('Spotlight speaker play error:', e);
        });
        this.lastStageRenderedSpeakerId = targetSpeakerId;
      } else {
        if (this.lastAttachedRemoteTrack) {
          try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
          this.lastAttachedRemoteTrack = null;
        }
        spotlightVideo.style.display = 'none';
        if (spotlightVideo.srcObject) spotlightVideo.srcObject = null;
        if (spotlightPinnedCard) {
          spotlightPinnedCard.style.display = 'flex';
          const avatarChar = document.getElementById('spotlight-avatar-char');
          const pinnedName = document.getElementById('spotlight-pinned-name');
          if (avatarChar) avatarChar.innerText = speakerPart.name.charAt(0);
          if (pinnedName) pinnedName.innerText = speakerPart.name;
        }
        this.lastStageRenderedSpeakerId = targetSpeakerId;
      }
    } else {
      // 4. Standard Gallery Grid Mode (Default for 1..4 participants):
      // Cleanly split screens across the viewport (1 = full, 2 = 50/50, 3 = 2+1, 4 = 2x2)
      if (this.lastAttachedRemoteTrack) {
        try { this.lastAttachedRemoteTrack.detach(spotlightVideo); } catch (_) {}
        this.lastAttachedRemoteTrack = null;
      }
      stageArea.classList.remove('has-spotlight');
      spotlightContainer.classList.remove('is-screen-share');
      spotlightContainer.style.display = 'none';
      if (spotlightPinnedCard) spotlightPinnedCard.style.display = 'none';
      if (spotlightVideo.srcObject) spotlightVideo.srcObject = null;
    }

    this.renderVideoGrid();
    this.updateControlsState();
  }

  private handleScreenShareState() {
    this.updateStageLayout();
    this.updateControlsState();
  }

  // Screen and Window Picker Logic
  private async triggerScreenShareFlow() {
    if (this.isScreenShareFlowInProgress) {
      console.warn('[ConferenceUI] Screen share flow already in progress, ignoring duplicate trigger');
      return;
    }
    this.isScreenShareFlowInProgress = true;

    try {
      if (this.manager.isScreenShareEnabled) {
        await this.manager.enableScreenShare(false);
        this.handleScreenShareState();
        return;
      }

      const isMobile = typeof navigator !== 'undefined' && /android|iphone|ipad|ipod/i.test(navigator.userAgent);
      if (isMobile) {
        this.showFloatingToast('📱 Pe telefoane mobile (Android / iOS), Google și Apple nu permit capturarea ecranului din browser. În browser puteți viziona ecranul partajat de colegi de pe PC.', 6000);
        return;
      }

      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.getShareSources === 'function') {
        const modal = document.getElementById('screen-picker-modal');
        const tabWindows = document.getElementById('tab-picker-windows');
        const tabScreens = document.getElementById('tab-picker-screens');
        const grid = document.getElementById('picker-sources-grid');
        const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;

        this.selectedSourceId = null;
        this.selectedSourceName = '';
        this.currentPickerTab = 'windows';

        if (modal) modal.style.display = 'flex';
        tabWindows?.classList.add('active');
        tabScreens?.classList.remove('active');

        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.innerText = 'Partajează Fereastra Selectată';
        }

        if (grid) {
          grid.innerHTML = `
            <div style="grid-column: 1 / -1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3rem; gap: 0.85rem; color: #94a3b8;">
              <div class="conf-connecting-spinner" style="width: 36px; height: 36px; border-width: 3px;"></div>
              <div style="font-weight: 600; font-size: 0.95rem;">Se încarcă ferestrele și ecranele active...</div>
            </div>
          `;
        }

        try {
          const sources = await desktopApi.getShareSources();
          this.availableSources = sources || [];
          this.renderPickerSources();
        } catch (err) {
          console.warn('Desktop capturer error, falling back to direct media:', err);
          if (modal) modal.style.display = 'none';
          await this.manager.enableScreenShare(true);
          this.handleScreenShareState();
        }
      } else {
        // Browser / PWA Mode: Immediately trigger browser native window/screen picker with 0ms lag
        this.pinnedIdentity = null;
        this.showFloatingToast('🖥️ Alegeți fereastra sau ecranul pe care doriți să îl partajați...', 3000);
        const success = await this.manager.enableScreenShare(true);
        if (success) {
          this.showFloatingToast('✅ Partajarea ecranului este activă!', 2500);
          this.handleScreenShareState();
          setTimeout(() => {
            this.updateStageLayout();
          }, 100);
          setTimeout(() => this.updateStageLayout(), 400);
          setTimeout(() => this.updateStageLayout(), 1000);
        } else {
          this.showFloatingToast('ℹ️ Partajarea ecranului a fost anulată.');
          this.handleScreenShareState();
        }
      }
    } finally {
      this.isScreenShareFlowInProgress = false;
    }
  }

  public async switchScreenShareSource() {
    try {
      this.showFloatingToast('🔄 Selectați noua fereastră sau ecran...', 3000);

      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.getShareSources === 'function') {
        // Desktop app: re-open picker modal in switch mode
        const modal = document.getElementById('screen-picker-modal');
        const tabWindows = document.getElementById('tab-picker-windows');
        const tabScreens = document.getElementById('tab-picker-screens');
        const grid = document.getElementById('picker-sources-grid');
        const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;

        this.selectedSourceId = null;
        this.selectedSourceName = '';
        this.currentPickerTab = 'windows';

        if (modal) modal.style.display = 'flex';
        tabWindows?.classList.add('active');
        tabScreens?.classList.remove('active');

        if (confirmBtn) {
          confirmBtn.disabled = true;
          confirmBtn.innerText = 'Schimbă cu Sursa Selectată';
        }

        if (grid) {
          grid.innerHTML = `
            <div style="grid-column: 1 / -1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 3rem; gap: 0.85rem; color: #94a3b8;">
              <div class="conf-connecting-spinner" style="width: 36px; height: 36px; border-width: 3px;"></div>
              <div style="font-weight: 600; font-size: 0.95rem;">Se încarcă ferestrele și ecranele active...</div>
            </div>
          `;
        }

        try {
          const sources = await desktopApi.getShareSources();
          this.availableSources = sources || [];
          this.renderPickerSources();
        } catch (err) {
          console.warn('Desktop capturer error, falling back to direct media:', err);
          if (modal) modal.style.display = 'none';
        }
        return;
      }

      // Web / Browser mode: Hot-swap via getDisplayMedia
      if (typeof navigator !== 'undefined' && navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function') {
        let swapController: any = null;
        try {
          if (typeof (window as any).CaptureController !== 'undefined') swapController = new (window as any).CaptureController();
        } catch (_) {}
        const swapOpts: any = {
          video: {
            width: { ideal: 1920, max: 1920 },
            height: { ideal: 1080, max: 1080 },
            frameRate: { ideal: 30, max: 60 }
          },
          audio: {
            autoGainControl: false,
            echoCancellation: false,
            noiseSuppression: false,
          },
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'include',
          systemAudio: 'include'
        };
        if (swapController) swapOpts.controller = swapController;
        let stream: MediaStream;
        try {
          stream = await navigator.mediaDevices.getDisplayMedia(swapOpts);
        } catch (swapErr) {
          try {
            stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true });
          } catch (_) {
            stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
          }
        }
        if (swapController && typeof swapController.setFocusBehavior === 'function') {
          try { swapController.setFocusBehavior('no-focus-change'); } catch (_) {}
        }

        const newTrack = stream.getVideoTracks()[0];
        const newAudioTrack = stream.getAudioTracks()[0] || null;
        if (!newTrack) {
          this.showFloatingToast('⚠️ Nicio pistă video detectată în noul flux.');
          return;
        }

        // Disarm and STOP old stream tracks so background tabs (e.g. YouTube) immediately terminate audio capture!
        const oldStream = this.manager.getLocalScreenStream();
        if (oldStream) {
          oldStream.getTracks().forEach(t => {
            t.onended = null;
            try { t.stop(); } catch (_) {}
          });
        }

        const success = await this.manager.replaceScreenShareTrack(newTrack, newAudioTrack);
        if (success) {
          this.manager.setLocalScreenStream(stream);
          const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
          if (spotlightVideo) {
            spotlightVideo.muted = true;
            spotlightVideo.playsInline = true;
            spotlightVideo.setAttribute('muted', '');
            spotlightVideo.setAttribute('playsinline', '');
            spotlightVideo.srcObject = stream;
            spotlightVideo.play().catch(() => {});
          }
          this.selectedSourceName = 'Ecran / Fereastră';
          this.showFloatingToast('✅ Sursa de partajare a fost schimbată din mers!', 2500);
        } else {
          this.showFloatingToast('⚠️ Comutarea sursei nu a reușit.');
        }
        this.updateStageLayout();
        setTimeout(() => this.updateStageLayout(), 200);
      }
    } catch (err: any) {
      if (err.name !== 'NotAllowedError' && err.name !== 'AbortError') {
        console.warn('[ConferenceUI] switchScreenShareSource error:', err);
        this.showFloatingToast('⚠️ Schimbarea sursei a fost anulată.');
      }
    }
  }

  private renderPickerSources() {
    const grid = document.getElementById('picker-sources-grid');
    const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;
    if (!grid) return;

    const filtered = this.availableSources.filter(s => {
      // Exclude Vorbello window itself from window list to avoid recursion
      if (s.name.includes('Vorbello') && !s.isScreen) return false;
      return this.currentPickerTab === 'windows' ? !s.isScreen : s.isScreen;
    });

    if (filtered.length === 0) {
      grid.innerHTML = `
        <div style="grid-column: 1 / -1; text-align: center; color: var(--text-muted); padding: 3rem 1rem;">
          Nu s-au găsit ${this.currentPickerTab === 'windows' ? 'ferestre deschise' : 'ecrane disponibile'}. Deschideți/restaurați fereastra dorită și apăsați butonul <strong>🔄 Reîmprospătează</strong> de mai sus.
        </div>
      `;
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerText = this.currentPickerTab === 'windows' ? 'Partajează Fereastra Selectată' : 'Partajează Ecranul Selectat';
      }
      return;
    }

    grid.innerHTML = filtered.map(s => {
      const isSelected = this.selectedSourceId === s.id;
      const safeName = s.name.replace(/"/g, '&quot;');
      const icon = s.isScreen ? '🖥️' : '🪟';
      return `
        <div class="picker-card ${isSelected ? 'selected' : ''}" data-id="${s.id}" data-name="${safeName}">
          <div class="picker-thumb-wrapper">
            ${s.thumbnail && s.thumbnail.length > 30 ? `
              <img src="${s.thumbnail}" class="picker-thumb" alt="${safeName}" onerror="this.style.display='none'; if(this.nextElementSibling) this.nextElementSibling.style.display='flex';" />
              <div style="font-size: 2.2rem; display: none; align-items: center; justify-content: center; width: 100%; height: 100%;">${icon}</div>
            ` : `<div style="font-size: 2.2rem; display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;">${icon}</div>`}
          </div>
          <div class="picker-title" title="${safeName}">${s.name}</div>
        </div>
      `;
    }).join('');

    grid.querySelectorAll('.picker-card').forEach(card => {
      card.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute('data-id');
        const name = target.getAttribute('data-name') || (this.currentPickerTab === 'windows' ? 'Fereastră' : 'Ecran');
        this.selectedSourceId = id;
        this.selectedSourceName = name;
        grid.querySelectorAll('.picker-card').forEach(c => c.classList.remove('selected'));
        target.classList.add('selected');
        if (confirmBtn) {
          confirmBtn.disabled = false;
          confirmBtn.innerText = `Partajează: ${name.slice(0, 24)}${name.length > 24 ? '...' : ''}`;
        }
      });

      // Double-click to immediately share
      card.addEventListener('dblclick', async (e) => {
        const target = e.currentTarget as HTMLElement;
        const id = target.getAttribute('data-id');
        const name = target.getAttribute('data-name') || (this.currentPickerTab === 'windows' ? 'Fereastră' : 'Ecran');
        this.selectedSourceId = id;
        this.selectedSourceName = name;
        await this.confirmScreenPicker();
      });
    });
  }

  private attachPickerListeners() {
    const modal = document.getElementById('screen-picker-modal');
    const tabWindows = document.getElementById('tab-picker-windows');
    const tabScreens = document.getElementById('tab-picker-screens');
    const refreshBtn = document.getElementById('btn-refresh-sources') as HTMLButtonElement | null;
    const confirmBtn = document.getElementById('btn-confirm-picker') as HTMLButtonElement | null;
    const cancelBtn = document.getElementById('btn-cancel-picker');
    const closeBtn = document.getElementById('btn-close-picker');

    tabWindows?.addEventListener('click', () => {
      this.currentPickerTab = 'windows';
      tabWindows.classList.add('active');
      tabScreens?.classList.remove('active');
      this.selectedSourceId = null;
      this.selectedSourceName = '';
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerText = 'Partajează Fereastra Selectată';
      }
      this.renderPickerSources();
    });

    tabScreens?.addEventListener('click', () => {
      this.currentPickerTab = 'screens';
      tabScreens.classList.add('active');
      tabWindows?.classList.remove('active');
      this.selectedSourceId = null;
      this.selectedSourceName = '';
      if (confirmBtn) {
        confirmBtn.disabled = true;
        confirmBtn.innerText = 'Partajează Ecranul Selectat';
      }
      this.renderPickerSources();
    });

    refreshBtn?.addEventListener('click', async () => {
      if (!refreshBtn) return;
      refreshBtn.disabled = true;
      refreshBtn.innerText = 'Se încarcă...';
      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.getShareSources === 'function') {
        try {
          this.availableSources = await desktopApi.getShareSources();
          this.renderPickerSources();
        } catch (e) {
          console.warn('Failed to refresh share sources:', e);
        }
      }
      refreshBtn.disabled = false;
      refreshBtn.innerText = '🔄 Reîmprospătează';
    });

    const closePicker = async () => {
      if (modal) modal.style.display = 'none';
      const desktopApi = (window as any).vorbelloDesktop;
      if (desktopApi && typeof desktopApi.cancelShareSource === 'function') {
        await desktopApi.cancelShareSource();
      } else if (desktopApi && typeof desktopApi.selectShareSource === 'function') {
        await desktopApi.selectShareSource(null);
      }
    };

    cancelBtn?.addEventListener('click', closePicker);
    closeBtn?.addEventListener('click', closePicker);

    confirmBtn?.addEventListener('click', async () => {
      await this.confirmScreenPicker();
    });
  }

  private async confirmScreenPicker() {
    const modal = document.getElementById('screen-picker-modal');
    if (!this.selectedSourceId) return;

    if (modal) modal.style.display = 'none';

    const desktopApi = (window as any).vorbelloDesktop;
    if (desktopApi && typeof desktopApi.prepareShareSource === 'function') {
      await desktopApi.prepareShareSource(this.selectedSourceId);
    } else if (desktopApi && typeof desktopApi.selectShareSource === 'function') {
      await desktopApi.selectShareSource(this.selectedSourceId);
    }

    const titleEl = document.getElementById('spotlight-title-text');
    if (titleEl && this.selectedSourceName) {
      titleEl.innerText = `Partajare: ${this.selectedSourceName.slice(0, 32)} (60 FPS HD)`;
    }

    // If screen share is already active, hot-swap the source without interrupting presentation
    if (this.manager.isScreenShareEnabled) {
      try {
        let stream: MediaStream;
        try {
          stream = await (navigator.mediaDevices as any).getDisplayMedia({
            video: {
              width: { ideal: 1920 },
              height: { ideal: 1080 },
              frameRate: { ideal: 30 }
            },
            audio: {
              autoGainControl: false,
              echoCancellation: false,
              noiseSuppression: false,
            },
            systemAudio: 'include'
          });
        } catch (_) {
          try {
            stream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: true });
          } catch (__) {
            stream = await (navigator.mediaDevices as any).getDisplayMedia({ video: true, audio: false });
          }
        }

        const newTrack = stream?.getVideoTracks()[0];
        const newAudioTrack = stream?.getAudioTracks()[0] || null;
        if (newTrack) {
          // Disarm and STOP old stream tracks
          const oldStream = this.manager.getLocalScreenStream();
          if (oldStream) {
            oldStream.getTracks().forEach(t => {
              t.onended = null;
              try { t.stop(); } catch (_) {}
            });
          }

          const swapped = await this.manager.replaceScreenShareTrack(newTrack, newAudioTrack);
          if (swapped) {
            this.manager.setLocalScreenStream(stream);
            const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
            if (spotlightVideo) {
              spotlightVideo.muted = true;
              spotlightVideo.playsInline = true;
              spotlightVideo.setAttribute('muted', '');
              spotlightVideo.setAttribute('playsinline', '');
              spotlightVideo.srcObject = stream;
              spotlightVideo.play().catch(() => {});
            }
            this.showFloatingToast(`✅ Sursa a fost schimbată: ${this.selectedSourceName.slice(0, 24)}`, 2500);
          } else {
            this.showFloatingToast('⚠️ Comutarea sursei nu a reușit.');
          }
        }
      } catch (err) {
        console.warn('[ConferenceUI] Hot-swap screen source error:', err);
      }
      this.handleScreenShareState();
      this.updateStageLayout();
      return;
    }

    // Starting screen share fresh
    this.pinnedIdentity = null;
    const success = await this.manager.enableScreenShare(true);
    if (success) {
      this.handleScreenShareState();
    } else {
      console.warn('Screen share failed or was aborted.');
      this.handleScreenShareState();
    }
  }

  private attachListeners() {
    // Mic toggle (Instant, responsive toggle)
    const micBtn = document.getElementById('btn-toggle-mic');
    micBtn?.addEventListener('click', async () => {
      const targetState = !this.manager.isMicEnabled;
      this.manager.isMicEnabled = targetState;
      if (!targetState) {
        this.userExplicitlyMuted = true;
      }
      this.updateControlsState();

      const selfP = this.participants.find(p => p.identity === 'self');
      if (selfP) selfP.isMuted = !targetState;
      this.setParticipantSpeaking('self', false);

      if (this.p2pMesh && this.p2pMesh.localStream) {
        this.p2pMesh.localStream.getAudioTracks().forEach(t => t.enabled = targetState);
      }

      try {
        await this.manager.enableMicrophone(targetState);
        if (targetState) {
          this.startLocalAudioLevelMonitor();
        }
      } catch (e) {
        console.warn('[ConferenceUI] Mic toggle error:', e);
      } finally {
        this.updateControlsState();
        this.setParticipantSpeaking('self', false);
      }
    });

    // Camera toggle (Instant, responsive toggle)
    const camBtn = document.getElementById('btn-toggle-cam');
    camBtn?.addEventListener('click', async () => {
      const targetState = !this.manager.isCameraEnabled;
      this.manager.isCameraEnabled = targetState;
      this.updateControlsState();
      this.updateSelfTile();

      const selfP = this.participants.find(p => p.identity === 'self');
      if (selfP) selfP.isCamOff = !targetState;

      if (this.p2pMesh && this.p2pMesh.localStream) {
        this.p2pMesh.localStream.getVideoTracks().forEach(t => t.enabled = targetState);
      }

      try {
        const ok = await this.manager.enableCamera(targetState);
        if (targetState && !ok) {
          this.manager.isCameraEnabled = false;
          if (selfP) selfP.isCamOff = true;
          this.showFloatingToast('⚠️ Camera nu a putut fi pornită (posibil ocupată de altă aplicație sau permisiune refuzată)');
        }
      } catch (e) {
        console.warn('[ConferenceUI] Cam toggle error:', e);
        if (targetState) {
          this.manager.isCameraEnabled = false;
          if (selfP) selfP.isCamOff = true;
          this.showFloatingToast('⚠️ Eroare la pornirea camerei (posibil ocupată de altă aplicație)');
        }
      } finally {
        this.updateControlsState();
        this.updateSelfTile();
        this.updateStageLayout();
      }
    });

    // Screen share toggle & on-the-fly hot-swap popover
    const screenBtn = document.getElementById('btn-toggle-screen');
    const screenActiveMenu = document.getElementById('screen-share-active-menu');

    screenBtn?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (this.manager.isScreenShareEnabled) {
        // If already sharing, toggle popover menu for switching sources or stopping
        if (screenActiveMenu) {
          const isShown = screenActiveMenu.style.display !== 'none';
          screenActiveMenu.style.display = isShown ? 'none' : 'flex';
        }
      } else {
        if (screenActiveMenu) screenActiveMenu.style.display = 'none';
        await this.triggerScreenShareFlow();
      }
    });

    // Close screen share popover on outside click
    document.addEventListener('click', (e) => {
      if (screenActiveMenu && !screenActiveMenu.contains(e.target as Node) && e.target !== screenBtn) {
        screenActiveMenu.style.display = 'none';
      }
    });

    // Menu Item: Schimbă Sursa
    document.getElementById('btn-menu-switch-source')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (screenActiveMenu) screenActiveMenu.style.display = 'none';
      await this.switchScreenShareSource();
    });

    // Menu Item: Oprește Partajarea
    document.getElementById('btn-menu-stop-share')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      if (screenActiveMenu) screenActiveMenu.style.display = 'none';
      await this.manager.enableScreenShare(false);
      this.handleScreenShareState();
    });

    // Spotlight Header Button: Schimbă Sursa
    document.getElementById('btn-switch-spotlight-source')?.addEventListener('click', async () => {
      await this.switchScreenShareSource();
    });

    // Global chat file download handler (Android APK Native Bridge + Web fallback)
    (window as any).vorbelloDownloadFile = (msgId: string) => {
      const msg = this.messages.find(m => m.id === msgId);
      if (!msg || !msg.file || !msg.file.dataUrl) {
        this.showFloatingToast('⚠️ Fișierul nu mai este disponibil.');
        return;
      }
      const dataUrl = msg.file.dataUrl;
      const fileName = msg.file.name || 'document';
      const mimeType = msg.file.type || 'application/octet-stream';

      // 1. Android APK Native Bridge
      if ((window as any).VorbelloNativeBridge && typeof (window as any).VorbelloNativeBridge.downloadBase64File === 'function') {
        try {
          (window as any).VorbelloNativeBridge.downloadBase64File(dataUrl, fileName, mimeType);
          this.showFloatingToast(`⬇️ Descărcare salvată în folderul Descărcări: ${fileName}`);
          return;
        } catch (err) {
          console.warn('[Chat] VorbelloNativeBridge error, falling back:', err);
        }
      }

      // 2. Desktop & Mobile Web / PWA Download via real Blob ObjectURL (Chrome on Android does not block Blob URLs)
      try {
        const blob = this.dataUrlToBlob(dataUrl, mimeType);
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          if (link.parentNode) link.parentNode.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        }, 4000);
        this.showFloatingToast(`⬇️ Fișier descărcat: ${fileName}`);
      } catch (err) {
        console.error('[Chat] Download error:', err);
        window.open(dataUrl, '_blank');
      }
    };

    // Raise Hand Toggle (Opțiunea 1)
    const handBtn = document.getElementById('btn-raise-hand');
    handBtn?.addEventListener('click', async () => {
      this.isLocalHandRaised = !this.isLocalHandRaised;
      const selfP = this.participants.find(p => p.identity === 'self');
      if (selfP) selfP.isHandRaised = this.isLocalHandRaised;

      this.updateControlsState();
      this.renderVideoGrid();
      this.renderParticipantsList();

      if (this.isP2PMode && this.p2pMesh) {
        this.p2pMesh.broadcastDataMessage({ type: 'hand-raise', identity: 'self', raised: this.isLocalHandRaised });
      } else {
        await this.manager.sendHandRaise(this.isLocalHandRaised);
      }
      if (this.isLocalHandRaised) {
        this.showFloatingToast('✋ Ai ridicat mâna! Toți participanții au fost notificați.');
      } else {
        this.showFloatingToast('✋ Ai coborât mâna.');
      }
    });

    // Live Reactions Popover (Opțiunea 2)
    const reactionsBtn = document.getElementById('btn-open-reactions');
    const reactionsPopover = document.getElementById('reactions-popover');
    reactionsBtn?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (reactionsPopover) {
        const isShown = reactionsPopover.style.display !== 'none';
        reactionsPopover.style.display = isShown ? 'none' : 'flex';
      }
    });

    // Close reactions popover on outside click
    document.addEventListener('click', (e) => {
      if (reactionsPopover && !reactionsPopover.contains(e.target as Node) && e.target !== reactionsBtn) {
        reactionsPopover.style.display = 'none';
      }
    });

    // Emoji reaction buttons
    reactionsPopover?.querySelectorAll('.btn-reaction-emoji').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const emoji = (btn as HTMLElement).getAttribute('data-emoji') || '👍';
        if (reactionsPopover) reactionsPopover.style.display = 'none';

        // Trigger local visual feedback immediately
        this.triggerFloatingReaction(emoji, 'self', `${this.config.displayName} (Tu)`);

        // Broadcast to all participants via LiveKit Data Channel
        await this.manager.sendReaction(emoji);
      });
    });

    // Stop / Unpin button inside spotlight header
    document.getElementById('btn-stop-spotlight')?.addEventListener('click', async () => {
      if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
        this.showFloatingToast('🔒 Doar Administratorul poate anula fixarea scenei.');
        return;
      }
      if (this.manager.isScreenShareEnabled) {
        await this.manager.enableScreenShare(false);
      } else if (this.pinnedIdentity) {
        await this.setPinState(null, true);
      }
    });

    // Toggle Spotlight Size (Zoom & Google Meet Mobile Style: contain vs cover on mobile, maximize on desktop)
    const toggleSpotlightSize = () => {
      const container = document.getElementById('spotlight-container');
      const label = document.getElementById('label-spotlight-size');
      if (!container) return;
      if (window.innerWidth <= 768) {
        // Mobile Zoom & Meet behavior: Toggle between contain (Fit 16:9 full view) and cover (Fill screen)
        const isFill = container.classList.toggle('is-fill-mode');
        if (label) {
          label.innerText = isFill ? 'Potrivește' : 'Umple';
        }
        this.showFloatingToast(isFill ? '🔍 Umplere ecran (Zoom In / Decupat)' : '⤡ Potrivire ecran complet (Zoom Out / 16:9 Original)');
      } else {
        const isMax = container.classList.toggle('is-maximized');
        if (label) {
          label.innerText = isMax ? 'Micșorează' : 'Mărește';
        }
        this.showFloatingToast(isMax ? '🔍 Ecran mărit la maximum (Full View)' : '⤡ Revenire la aspect standard');
      }
    };

    document.getElementById('btn-toggle-spotlight-size')?.addEventListener('click', toggleSpotlightSize);
    document.getElementById('spotlight-viewport')?.addEventListener('dblclick', toggleSpotlightSize);

    // Double-tap gesture on mobile touch screens (Zoom & Google Meet gesture)
    let lastSpotlightTapTime = 0;
    document.getElementById('spotlight-viewport')?.addEventListener('touchend', (e) => {
      const now = Date.now();
      if (now - lastSpotlightTapTime < 350 && now - lastSpotlightTapTime > 0) {
        e.preventDefault();
        toggleSpotlightSize();
      }
      lastSpotlightTapTime = now;
    });

    // Zoom-Style View Switcher (Galerie vs Vorbitor Activ / Focus)
    const viewBtn = document.getElementById('btn-toggle-view-mode');
    viewBtn?.addEventListener('click', async () => {
      if (!this.isAdmin && !this.isHost && this.isStageLockedByAdmin) {
        this.showFloatingToast('🔒 Scena este fixată de Administrator (Mod Spotlight).');
        return;
      }
      if (this.participants.length > 4) {
        // Above 4 participants: Toggle focus between Active Speaker and Pinned Admin without breaking stage layout
        if (this.pinnedIdentity) {
          this.pinnedIdentity = null;
          this.currentViewMode = 'speaker';
          this.currentStageSpeakerId = null;
          this.lastStageRenderedSpeakerId = null;
          this.showFloatingToast('👤 Focus automat pe Vorbitorul Activ.');
        } else {
          const adminP = this.participants.find(p => p.role === 'admin') || this.participants.find(p => p.identity === 'self');
          this.pinnedIdentity = adminP?.identity || 'self';
          this.currentViewMode = 'speaker';
          this.showFloatingToast('📌 Scenă fixată pe Administrator / Prezentator.');
        }
      } else {
        this.userExplicitlyChangedViewMode = true;
        const stageArea = document.getElementById('stage-area');
        const isCurrentlySpotlight = stageArea?.classList.contains('has-spotlight') ?? (this.currentViewMode === 'speaker');
        if (isCurrentlySpotlight) {
          this.currentViewMode = 'gallery';
          this.pinnedIdentity = null;
          this.showFloatingToast('⊞ Mod Galerie activat.');
        } else {
          this.currentViewMode = 'speaker';
          this.pinnedIdentity = null;
          this.currentStageSpeakerId = null;
          this.lastStageRenderedSpeakerId = null;
          this.showFloatingToast('👤 Mod Vorbitor Activ activat (Ecran Mare).');
        }
      }
      this.updateStageLayout();
      this.renderVideoGrid();
      this.updateControlsState();
      if (this.isAdmin || this.isHost) {
        await this.broadcastAdminLayoutSync();
      }
    });

    // Global Moderation: Lower All Hands (Zoom & Google Meet feature)
    const lowerAllHandsBtn = document.getElementById('btn-lower-all-hands');
    lowerAllHandsBtn?.addEventListener('click', async () => {
      this.isLocalHandRaised = false;
      this.participants.forEach(p => { p.isHandRaised = false; });
      this.showFloatingToast('✋ Ați coborât toate mâinile ridicate din sală.');
      this.renderVideoGrid();
      this.renderParticipantsList();
      this.updateControlsState();
      try {
        await this.manager.sendLowerAllHands();
      } catch (err) {
        console.warn('Failed to send lower all hands:', err);
      }
    });

    // Global Moderation: Toggle Microphone for All Non-Admins
    const muteAllBtn = document.getElementById('btn-mute-all');
    muteAllBtn?.addEventListener('click', async () => {
      this.isAllMuted = !this.isAllMuted;
      this.participants.forEach(p => {
        if (p.identity !== 'self') {
          p.isMuted = this.isAllMuted;
        }
      });
      if (muteAllBtn) {
        muteAllBtn.innerText = this.isAllMuted ? '🔊 Unmute Toți' : '🔇 Mute Toți';
        muteAllBtn.className = `btn btn-sm ${this.isAllMuted ? 'btn-primary' : 'btn-secondary'}`;
      }

      // Immediately silence or restore all remote audio elements locally for 0ms delay:
      document.querySelectorAll('audio[id^="audio-remote-"]').forEach((a: any) => {
        a.muted = this.isAllMuted;
      });

      const myId = this.manager.getRoom()?.localParticipant?.identity || 'self';
      await this.manager.sendDataMessage({
        type: 'moderation',
        action: this.isAllMuted ? 'mute-all' : 'unmute-all',
        senderIdentity: myId
      });

      if (this.isP2PMode && this.p2pMesh) {
        this.p2pMesh.broadcastDataMessage({
          type: 'moderation',
          action: this.isAllMuted ? 'mute-all' : 'unmute-all',
          senderIdentity: myId
        });
      }

      this.renderVideoGrid();
      this.renderParticipantsList();
    });

    // Global Moderation: Toggle Camera for All Non-Admins
    const stopCamAllBtn = document.getElementById('btn-stop-all-cam');
    stopCamAllBtn?.addEventListener('click', async () => {
      this.isAllCamOff = !this.isAllCamOff;
      this.participants.forEach(p => {
        if (p.identity !== 'self') {
          p.isCamOff = this.isAllCamOff;
        }
      });
      if (stopCamAllBtn) {
        stopCamAllBtn.innerText = this.isAllCamOff ? '📹 Start Camere' : '📷 Stop Camere';
        stopCamAllBtn.className = `btn btn-sm ${this.isAllCamOff ? 'btn-primary' : 'btn-secondary'}`;
      }

      // Immediately toggle remote video elements locally:
      if (this.isAllCamOff) {
        document.querySelectorAll('video.remote-cam-video').forEach((v: any) => {
          v.style.display = 'none';
        });
        document.querySelectorAll('.video-tile:not(#tile-self) .avatar-placeholder').forEach((av: any) => {
          av.style.display = 'flex';
        });
      }

      const myId = this.manager.getRoom()?.localParticipant?.identity || 'self';
      await this.manager.sendDataMessage({
        type: 'moderation',
        action: this.isAllCamOff ? 'stop-cam-all' : 'start-cam-all',
        senderIdentity: myId
      });

      if (this.isP2PMode && this.p2pMesh) {
        this.p2pMesh.broadcastDataMessage({
          type: 'moderation',
          action: this.isAllCamOff ? 'stop-cam-all' : 'start-cam-all',
          senderIdentity: myId
        });
      }

      this.renderVideoGrid();
      this.renderParticipantsList();
    });

    // Chat drawer
    document.getElementById('btn-open-chat')?.addEventListener('click', () => {
      this.isChatOpen = !this.isChatOpen;
      this.isParticipantsOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    // Participants drawer
    document.getElementById('btn-open-participants')?.addEventListener('click', () => {
      this.isParticipantsOpen = !this.isParticipantsOpen;
      this.isChatOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    // Close drawer
    document.getElementById('btn-close-panel')?.addEventListener('click', () => {
      this.isChatOpen = false;
      this.isParticipantsOpen = false;
      this.isPollsOpen = false;
      this.participantSearchQuery = '';
      const sInput = document.getElementById('input-search-participants') as HTMLInputElement | null;
      if (sInput) sInput.value = '';
      const cBtn = document.getElementById('btn-clear-search-participants');
      if (cBtn) cBtn.style.display = 'none';
      this.toggleDrawer();
    });

    // Participants Search & Filter (Zoom & Google Meet Style)
    const searchInput = document.getElementById('input-search-participants') as HTMLInputElement | null;
    const clearSearchBtn = document.getElementById('btn-clear-search-participants');
    searchInput?.addEventListener('input', () => {
      this.participantSearchQuery = searchInput.value || '';
      if (clearSearchBtn) {
        clearSearchBtn.style.display = this.participantSearchQuery ? 'block' : 'none';
      }
      this.renderParticipantsList();
    });
    clearSearchBtn?.addEventListener('click', () => {
      if (searchInput) {
        searchInput.value = '';
        this.participantSearchQuery = '';
        clearSearchBtn.style.display = 'none';
        searchInput.focus();
        this.renderParticipantsList();
      }
    });

    // Quick Invite in Header
    document.getElementById('btn-header-quick-invite')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.isParticipantsOpen = true;
      this.isChatOpen = false;
      this.toggleDrawer();
      const inviteInput = document.getElementById('input-invite-room-url') as HTMLInputElement | null;
      if (inviteInput) inviteInput.value = this.getRoomInviteUrl();
      this.copyInviteLink(document.getElementById('btn-header-quick-invite'));
    });

    // Copy link in invite card inside participants
    document.getElementById('btn-copy-invite-link')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.copyInviteLink(document.getElementById('btn-copy-invite-link'));
    });

    // Copy full invite text in invite card
    document.getElementById('btn-copy-invite-text')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.copyInviteText(document.getElementById('btn-copy-invite-text'));
    });

    // Mobile More Sheet & Desktop More Options Popover Handlers
    const moreSheetOverlay = document.getElementById('mobile-more-sheet-overlay');
    const openMoreSheet = () => {
      if (moreSheetOverlay) moreSheetOverlay.style.display = 'flex';
      const countEl = document.getElementById('sheet-part-count');
      if (countEl) countEl.textContent = String(this.participants.length);
    };
    const closeMoreSheet = () => {
      if (moreSheetOverlay) moreSheetOverlay.style.display = 'none';
    };

    const moreBtn = document.getElementById('btn-more-options');
    const morePopover = document.getElementById('more-options-popover');

    const toggleMoreOptions = (e: MouseEvent) => {
      e.stopPropagation();
      if (window.innerWidth <= 768) {
        openMoreSheet();
        return;
      }
      if (morePopover) {
        const isShown = morePopover.style.display !== 'none';
        morePopover.style.display = isShown ? 'none' : 'flex';
      }
    };

    moreBtn?.addEventListener('click', toggleMoreOptions);
    document.getElementById('btn-more-mobile')?.addEventListener('click', openMoreSheet);
    document.getElementById('btn-close-more-sheet')?.addEventListener('click', closeMoreSheet);
    moreSheetOverlay?.addEventListener('click', (e) => {
      if (e.target === moreSheetOverlay) closeMoreSheet();
    });

    // Close desktop more-options popover on outside click
    document.addEventListener('click', (e) => {
      if (morePopover && !morePopover.contains(e.target as Node) && e.target !== moreBtn && !moreBtn?.contains(e.target as Node)) {
        morePopover.style.display = 'none';
      }
    });

    // Close popover when an item inside is clicked
    morePopover?.querySelectorAll('.popover-item-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (morePopover) morePopover.style.display = 'none';
      });
    });

    // Popover quick reactions emojis
    morePopover?.querySelectorAll('.btn-reaction-emoji').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const emoji = (btn as HTMLElement).getAttribute('data-emoji') || '👏';
        if (morePopover) morePopover.style.display = 'none';
        this.triggerFloatingReaction(emoji, 'self', `${this.config.displayName} (Tu)`);
        await this.manager.sendReaction(emoji);
        this.showFloatingToast(`Reacție trimisă: ${emoji}`, 1600);
      });
    });

    // Mobile Camera Flip
    document.getElementById('btn-flip-cam')?.addEventListener('click', async () => {
      const btn = document.getElementById('btn-flip-cam');
      if (btn) btn.style.transform = 'rotate(180deg)';
      setTimeout(() => { if (btn) btn.style.transform = 'none'; }, 300);
      try {
        await this.manager.flipCamera();
        if (this.isP2PMode && this.p2pMesh) {
          const newCamStream = this.manager.getLocalCameraStream();
          if (newCamStream) {
            this.p2pMesh.updateLocalStream(newCamStream);
          }
        }
        this.updateSelfTile();
        const modeLabel = this.manager.currentFacingMode === 'user' ? 'Față (Selfie)' : 'Spate (Principală)';
        this.showFloatingToast(`🔄 Camera a fost comutată: ${modeLabel}`);
      } catch (flipErr) {
        console.warn('[ConferenceUI] flipCamera error:', flipErr);
      }
    });

    document.getElementById('sheet-btn-flip-cam')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-flip-cam')?.click();
    });

    // Sheet button forwards
    document.getElementById('sheet-btn-participants')?.addEventListener('click', () => {
      closeMoreSheet();
      this.isParticipantsOpen = true;
      this.isChatOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    document.getElementById('sheet-btn-chat')?.addEventListener('click', () => {
      closeMoreSheet();
      this.isChatOpen = true;
      this.isParticipantsOpen = false;
      this.isPollsOpen = false;
      this.toggleDrawer();
    });

    document.getElementById('sheet-btn-raise-hand')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-raise-hand')?.click();
    });

    // Mobile Reactions: Open Dedicated Floating Reactions Dock on Stage
    document.getElementById('sheet-btn-reactions')?.addEventListener('click', () => {
      closeMoreSheet();
      const dock = document.getElementById('mobile-floating-reactions-dock');
      if (dock) {
        dock.style.display = dock.style.display === 'none' ? 'flex' : 'none';
      }
    });

    document.getElementById('btn-close-reactions-dock')?.addEventListener('click', () => {
      const dock = document.getElementById('mobile-floating-reactions-dock');
      if (dock) dock.style.display = 'none';
    });

    // Mobile Quick Emojis (both in sheet and in floating dock)
    document.querySelectorAll('.sheet-quick-emoji').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const emoji = (btn as HTMLElement).getAttribute('data-emoji') || '👏';
        this.triggerFloatingReaction(emoji, 'self', `${this.config.displayName} (Tu)`);
        await this.manager.sendReaction(emoji);
        this.showFloatingToast(`Reacție trimisă: ${emoji}`, 1600);
        if (btn.closest('#mobile-more-sheet')) {
          closeMoreSheet();
        }
      });
    });

    document.getElementById('sheet-btn-whiteboard')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-toggle-whiteboard')?.click();
    });

    document.getElementById('sheet-btn-polls')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-open-polls')?.click();
    });

    document.getElementById('sheet-btn-screen')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-toggle-screen')?.click();
    });

    document.getElementById('sheet-btn-record')?.addEventListener('click', () => {
      closeMoreSheet();
      if (!this.isHost) {
        this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a înregistra.');
        return;
      }
      if (this.isRecording || this.roomRecordingActive) {
        this.stopRecording();
      } else {
        this.openRecordingOptionsModal();
      }
    });

    document.getElementById('sheet-btn-devices')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-open-devices')?.click();
    });

    document.getElementById('sheet-btn-view-mode')?.addEventListener('click', () => {
      closeMoreSheet();
      document.getElementById('btn-toggle-view-mode')?.click();
    });

    document.getElementById('sheet-btn-invite')?.addEventListener('click', (e) => {
      e.stopPropagation();
      closeMoreSheet();
      this.copyInviteLink(document.getElementById('btn-header-quick-invite'));
    });

    // Live Network Telemetry Popover
    const statusPill = document.getElementById('conf-status-pill');
    const telemetryPopover = document.getElementById('network-telemetry-popover');
    statusPill?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (telemetryPopover) {
        const isShown = telemetryPopover.style.display !== 'none';
        telemetryPopover.style.display = isShown ? 'none' : 'flex';
        if (!isShown) {
          this.startTelemetryMonitoring();
        } else {
          this.stopTelemetryMonitoring();
        }
      }
    });

    document.getElementById('btn-open-telemetry-monitor')?.addEventListener('click', (e) => {
      e.stopPropagation();
      const popover = document.getElementById('more-options-popover');
      if (popover) popover.style.display = 'none';
      if (telemetryPopover) {
        telemetryPopover.style.display = 'flex';
        this.startTelemetryMonitoring();
      }
    });

    document.getElementById('sheet-btn-telemetry-monitor')?.addEventListener('click', (e) => {
      e.stopPropagation();
      closeMoreSheet();
      if (telemetryPopover) {
        telemetryPopover.style.display = 'flex';
        this.startTelemetryMonitoring();
      }
    });

    document.getElementById('btn-close-telemetry')?.addEventListener('click', (e) => {
      e.stopPropagation();
      if (telemetryPopover) {
        telemetryPopover.style.display = 'none';
        this.stopTelemetryMonitoring();
      }
    });
    document.addEventListener('click', (e) => {
      if (telemetryPopover && telemetryPopover.style.display !== 'none' && !telemetryPopover.contains(e.target as Node) && e.target !== statusPill) {
        telemetryPopover.style.display = 'none';
        this.stopTelemetryMonitoring();
      }
    });

    document.getElementById('btn-telemetry-retry')?.addEventListener('click', async (e) => {
      e.stopPropagation();
      await this.retryVpsConnection();
    });

    document.getElementById('btn-telemetry-demo')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.activateInteractiveDemoMode();
    });

    // Send chat message and/or file
    const sendHandler = async () => {
      const input = document.getElementById('chat-text-input') as HTMLInputElement;
      const text = (input?.value || '').trim();
      const fileToSend = this.pendingFileAttachment;

      if (!text && !fileToSend) return;

      if (input) input.value = '';
      this.pendingFileAttachment = null;
      const previewStrip = document.getElementById('chat-file-preview-strip');
      if (previewStrip) previewStrip.style.display = 'none';
      const fileInput = document.getElementById('chat-file-input') as HTMLInputElement | null;
      if (fileInput) fileInput.value = '';

      if (this.isP2PMode && this.p2pMesh) {
        const timestamp = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const p2pMsgId = 'msg-' + Date.now();
        const p2pMsg: ChatMessage = {
          id: p2pMsgId,
          senderName: this.config.displayName,
          senderIdentity: 'self',
          text,
          timestamp,
          isSelf: true,
          file: fileToSend || undefined,
        };
        if (fileToSend) {
          this.p2pMesh.sendFileMessage(fileToSend, text, p2pMsgId);
        } else {
          this.p2pMesh.broadcastDataMessage({ type: 'chat', message: p2pMsg });
        }
        this.messages.push(p2pMsg);
        this.renderChatMessages();
        return;
      }

      let msg: ChatMessage | null = null;
      if (fileToSend) {
        msg = await this.manager.sendFileMessage(fileToSend, text);
      } else {
        msg = await this.manager.sendChatMessage(text);
      }

      if (msg) {
        this.messages.push(msg);
        this.renderChatMessages();

        if (this.participants.some(p => p.identity === 'part-ana')) {
          setTimeout(() => {
            this.messages.push({
              id: `msg-rep-${Date.now()}`,
              senderName: 'Ana Popescu',
              senderIdentity: 'part-ana',
              text: fileToSend ? 'Mulțumesc pentru fișier! S-a recepționat fără probleme.' : 'Am recepționat mesajul tău! Calitatea conexiunii este excelentă.',
              timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
              isSelf: false,
            });
            this.renderChatMessages();
          }, 800);
        }
      }
    };

    document.getElementById('btn-send-chat')?.addEventListener('click', sendHandler);
    document.getElementById('chat-text-input')?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        sendHandler();
      }
    });

    const attachBtn = document.getElementById('btn-attach-file');
    const fileInput = document.getElementById('chat-file-input') as HTMLInputElement | null;
    const previewStrip = document.getElementById('chat-file-preview-strip');
    const previewName = document.getElementById('chat-file-preview-name');
    const previewSize = document.getElementById('chat-file-preview-size');
    const cancelPreviewBtn = document.getElementById('btn-cancel-file-preview');

    attachBtn?.addEventListener('click', () => {
      fileInput?.click();
    });

    fileInput?.addEventListener('change', () => {
      const file = fileInput.files?.[0];
      if (!file) return;

      const MAX_SIZE = 25 * 1024 * 1024; // 25 MB
      if (file.size > MAX_SIZE) {
        this.showFloatingToast('⚠️ Fișierul depășește limita maximă de 25MB pentru conferință.');
        fileInput.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string;
        this.pendingFileAttachment = {
          name: file.name,
          size: file.size,
          type: file.type || 'application/octet-stream',
          dataUrl,
        };

        if (previewStrip && previewName && previewSize) {
          previewName.innerText = file.name;
          const sz = file.size > 1024 * 1024 
            ? (file.size / (1024 * 1024)).toFixed(1) + ' MB'
            : Math.max(1, Math.round(file.size / 1024)) + ' KB';
          previewSize.innerText = `(${sz})`;
          previewStrip.style.display = 'flex';
        }
        document.getElementById('chat-text-input')?.focus();
      };
      reader.readAsDataURL(file);
    });

    cancelPreviewBtn?.addEventListener('click', () => {
      this.pendingFileAttachment = null;
      if (previewStrip) previewStrip.style.display = 'none';
      if (fileInput) fileInput.value = '';
    });

    // Custom Vorbello Leave Conference Modal Handlers
    const leaveModal = document.getElementById('confirm-leave-modal');
    const cancelLeaveBtn = document.getElementById('btn-cancel-leave-modal');
    const confirmLeaveBtn = document.getElementById('btn-confirm-leave-modal');

    document.getElementById('btn-leave-conf')?.addEventListener('click', () => {
      if (leaveModal) {
        leaveModal.style.display = 'flex';
      }
    });

    cancelLeaveBtn?.addEventListener('click', () => {
      if (leaveModal) leaveModal.style.display = 'none';
    });

    leaveModal?.addEventListener('click', (e) => {
      if (e.target === leaveModal) {
        leaveModal.style.display = 'none';
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && leaveModal && leaveModal.style.display === 'flex') {
        leaveModal.style.display = 'none';
      }
    });

    confirmLeaveBtn?.addEventListener('click', () => {
      if (leaveModal) leaveModal.style.display = 'none';
      if (this.isRecording) {
        this.stopRecording();
      }
      this.cleanup();
      this.config.onLeave();
    });

    document.getElementById('btn-end-meeting-for-all')?.addEventListener('click', async () => {
      if (leaveModal) leaveModal.style.display = 'none';

      // 1. Broadcast meeting_ended_by_host to all participants
      try {
        await this.manager.sendDataMessage({
          type: 'meeting_ended_by_host',
          hostName: this.config.displayName,
          roomSlug: this.config.roomSlug,
          reason: 'Conferința a fost încheiată de către gazdă pentru toți participanții.',
        });
      } catch (e) {
        console.warn('[ConferenceUI] Broadcast meeting_ended_by_host error:', e);
      }

      // 2. Stop any active recording
      if (this.isRecording) {
        this.stopRecording();
      }

      // 3. Local cleanup and exit
      this.cleanup();
      this.config.onLeave('✅ Ați încheiat ședința pentru toți participanții.');
    });

    // Local Recording Controls (Zoom Style - Host Only)
    document.getElementById('btn-toggle-record')?.addEventListener('click', () => {
      if (!this.isHost) {
        this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a înregistra.');
        return;
      }
      if (this.isRecording || this.roomRecordingActive) {
        this.stopRecording();
      } else {
        this.openRecordingOptionsModal();
      }
    });

    // Interactive Header REC Badge Click to Stop Recording (Host Only)
    document.getElementById('conf-rec-badge')?.addEventListener('click', () => {
      if (!this.isHost) {
        this.showFloatingToast('🔒 Înregistrarea este gestionată exclusiv de către gazda conferinței.');
        return;
      }
      if (this.isRecording) {
        this.stopRecording();
      }
    });

    // Auto-resume media & wake up spotlight when returning to Vorbello tab in browser
    const resumeSpotlightPlayback = () => {
      this.updateStageLayout();
      const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
      if (spotlightVideo) {
        const screenStream = this.manager.getLocalScreenStream();
        if (this.manager.isScreenShareEnabled && screenStream && spotlightVideo.srcObject !== screenStream) {
          spotlightVideo.srcObject = screenStream;
        }
        if (spotlightVideo.srcObject) {
          spotlightVideo.play().catch(() => {});
        }
      }
    };
    window.addEventListener('focus', resumeSpotlightPlayback);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        resumeSpotlightPlayback();
      }
    });

    document.getElementById('btn-close-rec-options')?.addEventListener('click', () => {
      this.closeRecordingOptionsModal();
    });

    document.getElementById('btn-cancel-recording')?.addEventListener('click', () => {
      this.closeRecordingOptionsModal();
    });

    // Recording type selection cards (HD 720p 30fps, Full HD 1080p 30fps, Audio HD)
    const videoHdCard = document.getElementById('rec-card-video-hd');
    const videoFhdCard = document.getElementById('rec-card-video-fhd');
    const audioCard = document.getElementById('rec-card-audio');
    const recLayoutSection = document.getElementById('rec-layout-section');

    const selectRecCard = (type: 'video-hd' | 'video-fhd' | 'audio') => {
      this.selectedRecordingType = type;
      videoHdCard?.classList.toggle('selected', type === 'video-hd');
      videoFhdCard?.classList.toggle('selected', type === 'video-fhd');
      audioCard?.classList.toggle('selected', type === 'audio');
      if (recLayoutSection) {
        recLayoutSection.style.display = type === 'audio' ? 'none' : 'flex';
      }
    };

    videoHdCard?.addEventListener('click', () => selectRecCard('video-hd'));
    videoFhdCard?.addEventListener('click', () => selectRecCard('video-fhd'));
    audioCard?.addEventListener('click', () => selectRecCard('audio'));

    // Recording layout selector buttons
    const layoutCompositeBtn = document.getElementById('rec-layout-composite');
    const layoutScreenBtn = document.getElementById('rec-layout-screen');
    const layoutCamerasBtn = document.getElementById('rec-layout-cameras');

    const selectRecLayout = (layout: 'composite' | 'screen_only' | 'cameras_only') => {
      this.selectedRecordingLayout = layout;
      try {
        localStorage.setItem('vorbello_default_auto_record_layout', layout);
      } catch (_) {}
      layoutCompositeBtn?.classList.toggle('selected', layout === 'composite');
      layoutScreenBtn?.classList.toggle('selected', layout === 'screen_only');
      layoutCamerasBtn?.classList.toggle('selected', layout === 'cameras_only');
    };

    layoutCompositeBtn?.addEventListener('click', () => selectRecLayout('composite'));
    layoutScreenBtn?.addEventListener('click', () => selectRecLayout('screen_only'));
    layoutCamerasBtn?.addEventListener('click', () => selectRecLayout('cameras_only'));

    // Recording container selector buttons (WebM vs MP4)
    const containerWebmBtn = document.getElementById('rec-container-webm');
    const containerMp4Btn = document.getElementById('rec-container-mp4');

    const selectRecContainer = (container: 'webm' | 'mp4') => {
      this.selectedRecordingContainer = container;
      try {
        localStorage.setItem('vorbello_default_auto_record_container', container);
      } catch (_) {}
      containerWebmBtn?.classList.toggle('selected', container === 'webm');
      containerMp4Btn?.classList.toggle('selected', container === 'mp4');
    };

    containerWebmBtn?.addEventListener('click', () => selectRecContainer('webm'));
    containerMp4Btn?.addEventListener('click', () => selectRecContainer('mp4'));

    document.getElementById('btn-confirm-start-recording')?.addEventListener('click', async () => {
      this.closeRecordingOptionsModal();
      await this.startRecording(this.selectedRecordingType, this.selectedRecordingContainer);
    });

    document.getElementById('btn-close-rec-success')?.addEventListener('click', () => {
      const modal = document.getElementById('recording-success-modal');
      if (modal) modal.style.display = 'none';
    });

    // --- Whiteboard Listeners ---
    document.getElementById('btn-toggle-whiteboard')?.addEventListener('click', () => {
      this.toggleWhiteboard();
    });

    document.getElementById('btn-wb-close')?.addEventListener('click', () => {
      this.closeWhiteboard(true);
    });

    document.getElementById('btn-wb-clear')?.addEventListener('click', () => {
      this.clearWhiteboard(true);
    });

    document.getElementById('btn-wb-download')?.addEventListener('click', () => {
      this.downloadWhiteboardPNG();
    });

    document.querySelectorAll('.wb-tool-btn[data-tool]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.wb-tool-btn[data-tool]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const tool = btn.getAttribute('data-tool');
        if (tool === 'eraser' || tool === 'pen') {
          this.wbTool = tool;
        }
      });
    });

    document.querySelectorAll('.wb-color-btn[data-color]').forEach(btn => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.wb-color-btn[data-color]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const color = btn.getAttribute('data-color');
        if (color) {
          this.wbColor = color;
          this.wbTool = 'pen';
          document.querySelectorAll('.wb-tool-btn[data-tool]').forEach(b => {
            if (b.getAttribute('data-tool') === 'pen') b.classList.add('active');
            else b.classList.remove('active');
          });
        }
      });
    });

    // --- Polls Listeners ---
    document.getElementById('btn-open-polls')?.addEventListener('click', () => {
      this.isPollsOpen = !this.isPollsOpen;
      this.isChatOpen = false;
      this.isParticipantsOpen = false;
      this.toggleDrawer();
    });

    document.getElementById('btn-create-poll-open')?.addEventListener('click', () => {
      this.openCreatePollModal();
    });

    document.getElementById('btn-close-create-poll')?.addEventListener('click', () => {
      this.closeCreatePollModal();
    });

    document.getElementById('btn-cancel-create-poll')?.addEventListener('click', () => {
      this.closeCreatePollModal();
    });

    document.getElementById('btn-add-poll-option')?.addEventListener('click', () => {
      const inputsList = document.getElementById('poll-options-inputs-list');
      if (inputsList) {
        const count = inputsList.querySelectorAll('.poll-opt-input').length;
        if (count >= 6) {
          alert('Numărul maxim este de 6 opțiuni.');
          return;
        }
        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'invite-input-field poll-opt-input';
        input.style.width = '100%';
        input.style.fontFamily = 'inherit';
        input.placeholder = `Opțiunea ${count + 1}`;
        inputsList.appendChild(input);
      }
    });

    document.getElementById('btn-submit-launch-poll')?.addEventListener('click', () => {
      this.submitNewPoll();
    });
  }

  private toggleDrawer() {
    const panel = document.getElementById('side-panel');
    const chatContainer = document.getElementById('chat-container');
    const partContainer = document.getElementById('participants-container');
    const pollsContainer = document.getElementById('polls-container');
    const title = document.getElementById('panel-title');

    if (!panel) return;

    if (this.isChatOpen) {
      this.unreadChatCount = 0;
      this.updateChatBadge();
      const popup = document.getElementById('zoom-chat-preview');
      if (popup) popup.remove();
      panel.style.display = 'flex';
      if (chatContainer) chatContainer.style.display = 'flex';
      if (partContainer) partContainer.style.display = 'none';
      if (pollsContainer) pollsContainer.style.display = 'none';
      if (title) title.innerText = 'Chat de Cameră';
      this.renderChatMessages();
      const chatInput = document.getElementById('chat-text-input') as HTMLInputElement | null;
      if (chatInput) {
        setTimeout(() => chatInput.focus(), 80);
      }
    } else if (this.isParticipantsOpen) {
      panel.style.display = 'flex';
      if (chatContainer) chatContainer.style.display = 'none';
      if (partContainer) partContainer.style.display = 'flex';
      if (pollsContainer) pollsContainer.style.display = 'none';
      if (title) title.innerText = `Participanți Conectați (${this.participants.length})`;
      const inviteUrlInput = document.getElementById('input-invite-room-url') as HTMLInputElement | null;
      if (inviteUrlInput) inviteUrlInput.value = this.getRoomInviteUrl();
      this.renderParticipantsList();
    } else if (this.isPollsOpen) {
      panel.style.display = 'flex';
      if (chatContainer) chatContainer.style.display = 'none';
      if (partContainer) partContainer.style.display = 'none';
      if (pollsContainer) pollsContainer.style.display = 'flex';
      if (title) title.innerText = 'Sondaje Live în Sală';
      const floatPopup = document.getElementById('live-poll-popup');
      if (floatPopup) floatPopup.style.display = 'none';
      this.renderPollsView();
    } else {
      panel.style.display = 'none';
      if (pollsContainer) pollsContainer.style.display = 'none';
    }
  }

  private attachDeviceSettingsListeners() {
    const modal = document.getElementById('device-settings-modal');
    const openBtn = document.getElementById('btn-open-devices');
    const closeBtn = document.getElementById('btn-close-device-settings');
    const saveBtn = document.getElementById('btn-save-device-settings');
    const testSpeakerBtn = document.getElementById('btn-test-speakers');
    const micSelect = document.getElementById('conf-select-mic') as HTMLSelectElement | null;
    const camSelect = document.getElementById('conf-select-cam') as HTMLSelectElement | null;
    const speakerSelect = document.getElementById('conf-select-speaker') as HTMLSelectElement | null;

    const populateDevices = async () => {
      const devices = await LiveKitClientManager.getAvailableDevices();
      let currentMic = localStorage.getItem('vorbello_selected_mic') || '';
      let currentCam = localStorage.getItem('vorbello_selected_cam') || '';
      const currentSpeaker = localStorage.getItem('vorbello_selected_speaker') || '';

      const isBadCam = (d: MediaDeviceInfo | undefined) => {
        if (!d) return true;
        const l = (d.label || '').toLowerCase();
        return l.includes('epson') || l.includes('scanner') || l.includes('wf-') || l.includes('print');
      };
      const usableCams = devices.videoInputs.filter(d => !isBadCam(d));
      const videoList = usableCams.length > 0 ? usableCams : devices.videoInputs;

      const selectedCamObj = devices.videoInputs.find(d => d.deviceId === currentCam);
      if ((!currentCam || isBadCam(selectedCamObj)) && videoList.length > 0) {
        currentCam = videoList[0].deviceId;
        localStorage.setItem('vorbello_selected_cam', currentCam);
      }

      if (!devices.audioInputs.some(d => d.deviceId === currentMic) && devices.audioInputs.length > 0) {
        // Prioritize Yeti Nano
        const bestMic = devices.audioInputs.find(d => {
          const l = (d.label || '').toLowerCase();
          return l.includes('yeti') || l.includes('nano');
        }) || devices.audioInputs[0];
        currentMic = bestMic.deviceId;
        localStorage.setItem('vorbello_selected_mic', currentMic);
      }

      if (micSelect) {
        micSelect.innerHTML = devices.audioInputs.length > 0
          ? devices.audioInputs.map(d => `<option value="${d.deviceId}" ${d.deviceId === currentMic ? 'selected' : ''}>${d.label || 'Microfon'}</option>`).join('')
          : '<option value="">Microfon implicit de sistem</option>';
      }

      if (camSelect) {
        camSelect.innerHTML = videoList.length > 0
          ? videoList.map(d => `<option value="${d.deviceId}" ${d.deviceId === currentCam ? 'selected' : ''}>${d.label || 'Cameră Web'}</option>`).join('')
          : '<option value="">Cameră implicită de sistem</option>';
      }

      if (speakerSelect) {
        speakerSelect.innerHTML = devices.audioOutputs.length > 0
          ? devices.audioOutputs.map((d, i) => `<option value="${d.deviceId}" ${d.deviceId === currentSpeaker || (!currentSpeaker && i === 0) ? 'selected' : ''}>${d.label || `Difuzor / Căști ${i + 1}`}</option>`).join('')
          : '<option value="">Dispozitiv audio implicit de sistem</option>';
      }

      // Highlight selected video effect
      const currentEff = this.selectedVideoEffect;
      modal?.querySelectorAll('.effect-btn').forEach(btn => {
        const eff = btn.getAttribute('data-effect');
        if (eff === currentEff) {
          btn.classList.add('active');
        } else {
          btn.classList.remove('active');
        }
      });
      const effectLabel = document.getElementById('label-active-effect');
      if (effectLabel) {
        effectLabel.innerText = currentEff === 'none' ? 'Normal' : (currentEff === 'blur-soft' ? 'Blur Fin' : (currentEff === 'blur-heavy' ? 'Blur Intens' : (currentEff === 'studio' ? 'Studio HD' : 'Ambient')));
      }

      const activeMic = micSelect?.value || currentMic;
      this.startVuMeter(activeMic);
    };

    // Effect buttons selection in modal
    modal?.querySelectorAll('.effect-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        modal.querySelectorAll('.effect-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        const eff = btn.getAttribute('data-effect') || 'none';
        this.applyVideoEffect(eff);
        const effectLabel = document.getElementById('label-active-effect');
        if (effectLabel) {
          effectLabel.innerText = eff === 'none' ? 'Normal' : (eff === 'blur-soft' ? 'Blur Fin' : (eff === 'blur-heavy' ? 'Blur Intens' : (eff === 'studio' ? 'Studio HD' : 'Ambient')));
        }
      });
    });

    openBtn?.addEventListener('click', async () => {
      if (modal) {
        modal.style.display = 'flex';
        await populateDevices();
      }
    });

    const closeModal = () => {
      if (modal) modal.style.display = 'none';
      this.stopVuMeter();
    };

    closeBtn?.addEventListener('click', closeModal);

    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') {
        closeModal();
      }
    });

    micSelect?.addEventListener('change', () => {
      const selectedMic = micSelect.value;
      this.startVuMeter(selectedMic);
    });

    testSpeakerBtn?.addEventListener('click', () => {
      this.playTestSound();
    });

    saveBtn?.addEventListener('click', async () => {
      const micVal = micSelect?.value || '';
      const camVal = camSelect?.value || '';
      const speakerVal = speakerSelect?.value || '';

      if (micVal) localStorage.setItem('vorbello_selected_mic', micVal);
      if (camVal) localStorage.setItem('vorbello_selected_cam', camVal);
      if (speakerVal) localStorage.setItem('vorbello_selected_speaker', speakerVal);

      // Instant 0ms modal close and VU meter release
      closeModal();
      this.showFloatingToast('✅ Setările dispozitivelor au fost salvate și aplicate!');

      // Asynchronously apply selected hardware devices without blocking UI
      if (micVal) {
        try {
          await this.manager.switchDevice('audioinput', micVal);
          if (this.manager.isMicEnabled) {
            await this.manager.enableMicrophone(true);
          }
        } catch (e) {
          console.warn('[ConferenceUI] Switch mic error:', e);
        }
      }
      if (camVal) {
        try {
          await this.manager.switchDevice('videoinput', camVal);
          if (this.manager.isCameraEnabled) {
            await this.manager.enableCamera(true);
            this.updateSelfTile();
            this.updateStageLayout();
          }
        } catch (e) {
          console.warn('[ConferenceUI] Switch cam error:', e);
        }
      }
      if (speakerVal) {
        try {
          await this.manager.switchDevice('audiooutput', speakerVal);
        } catch (e) {
          console.warn('[ConferenceUI] Switch speaker error:', e);
        }
      }
    });
  }

  private attachVersionModalListeners() {
    const modal = document.getElementById('version-info-modal');
    const openBadge = document.getElementById('btn-conf-version-panel');
    const openHeaderPill = document.getElementById('btn-header-version');
    const closeBtn = document.getElementById('btn-close-version-info');
    const okBtn = document.getElementById('btn-version-ok');
    const openDevicesFromVersion = document.getElementById('btn-version-open-devices');
    const camNameEl = document.getElementById('version-modal-cam-name');
    const micNameEl = document.getElementById('version-modal-mic-name');

    const updateDeviceNames = async () => {
      try {
        const devices = await LiveKitClientManager.getAvailableDevices();
        const currentCamId = localStorage.getItem('vorbello_selected_cam');
        const currentMicId = localStorage.getItem('vorbello_selected_mic');

        const isBadCam = (d: MediaDeviceInfo | undefined) => {
          if (!d) return true;
          const l = (d.label || '').toLowerCase();
          return l.includes('epson') || l.includes('scanner') || l.includes('wf-') || l.includes('print');
        };

        const activeCam = devices.videoInputs.find(d => d.deviceId === currentCamId) || devices.videoInputs.find(d => !isBadCam(d)) || devices.videoInputs[0];
        const activeMic = devices.audioInputs.find(d => d.deviceId === currentMicId) || devices.audioInputs.find(d => (d.label || '').toLowerCase().includes('yeti')) || devices.audioInputs[0];

        if (camNameEl) {
          camNameEl.textContent = activeCam?.label || 'Angetube Live Camera (HD)';
        }
        if (micNameEl) {
          micNameEl.textContent = activeMic?.label || 'Microfono (Yeti Nano)';
        }
      } catch (e) {
        if (camNameEl) camNameEl.textContent = 'Hardware webcam detectat';
        if (micNameEl) micNameEl.textContent = 'Hardware microfon detectat';
      }
    };

    const openModal = async () => {
      if (modal) {
        modal.style.display = 'flex';
        await updateDeviceNames();
      }
    };

    const closeModal = () => {
      if (modal) modal.style.display = 'none';
    };

    openBadge?.addEventListener('click', openModal);
    openHeaderPill?.addEventListener('click', openModal);
    closeBtn?.addEventListener('click', closeModal);
    okBtn?.addEventListener('click', closeModal);

    openDevicesFromVersion?.addEventListener('click', () => {
      closeModal();
      const openDevBtn = document.getElementById('btn-open-devices');
      openDevBtn?.click();
    });

    modal?.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal && modal.style.display === 'flex') {
        closeModal();
      }
    });
  }

  private async startVuMeter(deviceId?: string) {
    this.stopVuMeter();
    try {
      const constraints: MediaStreamConstraints = {
        audio: (deviceId && deviceId.trim()) ? { deviceId: { exact: deviceId } } : true,
        video: false,
      };
      this.vuMediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      this.vuAudioContext = new AudioCtx();
      const source = this.vuAudioContext.createMediaStreamSource(this.vuMediaStream);
      const analyser = this.vuAudioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const bar = document.getElementById('device-vu-meter-bar');

      const updateMeter = () => {
        if (!this.vuAudioContext) return;
        analyser.getByteFrequencyData(dataArray);
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const avg = sum / bufferLength;
        const percent = Math.min(100, Math.round((avg / 128) * 100 * 1.5));
        if (bar) {
          bar.style.width = `${percent}%`;
        }
        this.vuAnimationId = requestAnimationFrame(updateMeter);
      };

      updateMeter();
    } catch (e) {
      console.warn('[ConferenceUI] startVuMeter error:', e);
    }
  }

  private stopVuMeter() {
    if (this.vuAnimationId !== null) {
      cancelAnimationFrame(this.vuAnimationId);
      this.vuAnimationId = null;
    }
    if (this.vuMediaStream) {
      this.vuMediaStream.getTracks().forEach(t => t.stop());
      this.vuMediaStream = null;
    }
    if (this.vuAudioContext) {
      this.vuAudioContext.close().catch(() => {});
      this.vuAudioContext = null;
    }
    const bar = document.getElementById('device-vu-meter-bar');
    if (bar) bar.style.width = '0%';
  }

  private playTestSound() {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const now = ctx.currentTime;
      // Two-tone pleasant test chime: 523.25Hz (C5) and 659.25Hz (E5)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(523.25, now);
      gain1.gain.setValueAtTime(0.3, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.5);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(659.25, now + 0.15);
      gain2.gain.setValueAtTime(0.35, now + 0.15);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.7);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.15);
      osc2.stop(now + 0.7);
    } catch (e) {
      console.warn('[ConferenceUI] playTestSound error:', e);
    }
  }

  private playChatChime() {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      const now = ctx.currentTime;
      // High-pitched soft Zoom-style pop/chime: 880Hz (A5) then 1174.66Hz (D6)
      const osc1 = ctx.createOscillator();
      const gain1 = ctx.createGain();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now);
      gain1.gain.setValueAtTime(0.18, now);
      gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.12);
      osc1.connect(gain1);
      gain1.connect(ctx.destination);
      osc1.start(now);
      osc1.stop(now + 0.12);

      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.type = 'sine';
      osc2.frequency.setValueAtTime(1174.66, now + 0.08);
      gain2.gain.setValueAtTime(0.22, now + 0.08);
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.start(now + 0.08);
      osc2.stop(now + 0.35);

      setTimeout(() => {
        try { ctx.close(); } catch (_) {}
      }, 500);
    } catch (e) {
      console.warn('[ConferenceUI] playChatChime error:', e);
    }
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  private showZoomChatNotification(msg: ChatMessage) {
    const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
    // Suppress floating on-screen preview if recording is active (so chat text is not recorded on video)
    // and on mobile screens (where it obstructs video tiles). Unread badge counter & chat panel handle notifications cleanly.
    if (this.isRecording || isMobile) {
      return;
    }

    let popup = document.getElementById('zoom-chat-preview');
    if (!popup) {
      popup = document.createElement('div');
      popup.id = 'zoom-chat-preview';
      popup.className = 'zoom-chat-preview-popup';
      document.body.appendChild(popup);
    }

    const initial = (msg.senderName || 'P').trim().charAt(0).toUpperCase();
    const cleanText = msg.text.length > 80 ? msg.text.slice(0, 80) + '...' : msg.text;

    popup.innerHTML = `
      <div class="zoom-preview-avatar">${initial}</div>
      <div class="zoom-preview-body">
        <div class="zoom-preview-header">
          <span class="zoom-preview-sender">${this.escapeHtml(msg.senderName)}</span>
          <span class="zoom-preview-time">${msg.timestamp}</span>
        </div>
        <div class="zoom-preview-text">${this.escapeHtml(cleanText)}</div>
        <div class="zoom-preview-hint">💬 Clic pentru a deschide chatul</div>
      </div>
      <button class="zoom-preview-close" title="Închide">&times;</button>
    `;

    popup.style.display = 'flex';
    popup.style.opacity = '1';

    // Click handler to open chat
    popup.onclick = (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('zoom-preview-close')) {
        e.stopPropagation();
        popup?.remove();
        return;
      }
      this.isChatOpen = true;
      this.isParticipantsOpen = false;
      this.toggleDrawer();
      popup?.remove();
    };

    // Auto-remove after 6 seconds
    const existingTimeout = (popup as any)._timeout;
    if (existingTimeout) clearTimeout(existingTimeout);
    (popup as any)._timeout = setTimeout(() => {
      if (popup && popup.parentNode) {
        popup.style.opacity = '0';
        popup.style.transform = 'translateX(-50%) translateY(10px)';
        setTimeout(() => popup?.remove(), 250);
      }
    }, 6000);
  }

  private async kickParticipant(identity: string): Promise<void> {
    if (!identity || identity === 'self') return;
    const targetPart = this.participants.find(p => p.identity === identity);
    const targetName = targetPart?.name || '';

    console.info('[LiveKit UI] Moderation kick initiated for:', identity, targetName);

    try {
      await this.manager.sendDataMessage({
        type: 'moderation',
        action: 'kick',
        targetIdentity: identity,
        targetName: targetName,
      });
      setTimeout(() => {
        this.manager.sendDataMessage({
          type: 'moderation',
          action: 'kick',
          targetIdentity: identity,
          targetName: targetName,
        });
      }, 100);
    } catch (e) {
      console.warn('[LiveKit UI] sendDataMessage kick error:', e);
    }

    this.participants = this.participants.filter(p => p.identity !== identity);
    const audioEl = document.getElementById(`audio-remote-${identity}`);
    if (audioEl) audioEl.remove();
    const tile = document.getElementById(`tile-${identity}`);
    if (tile) tile.remove();
    if (this.pinnedIdentity === identity) this.pinnedIdentity = null;

    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();

    this.showFloatingToast(`⛔ ${targetName || 'Participantul'} a fost eliminat din sală.`);
  }

  private showFloatingToast(text: string, durationMs: number = 3500) {
    let toast = document.getElementById('conf-floating-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'conf-floating-toast';
      toast.style.cssText = 'position:fixed; top:2rem; left:50%; transform:translateX(-50%); background:rgba(15,23,42,0.92); border:1px solid rgba(255,255,255,0.2); backdrop-filter:blur(16px); color:#fff; padding:0.6rem 1.4rem; border-radius:12px; font-size:0.85rem; font-weight:600; z-index:99999; box-shadow:0 10px 30px rgba(0,0,0,0.6); transition:all 0.3s ease; pointer-events:none;';
      document.body.appendChild(toast);
    }
    toast.innerText = text;
    toast.style.opacity = '1';
    toast.style.transform = 'translateX(-50%) translateY(0)';
    setTimeout(() => {
      if (toast) {
        toast.style.opacity = '0';
        toast.style.transform = 'translateX(-50%) translateY(-10px)';
      }
    }, durationMs);
  }

  public triggerFloatingReaction(emoji: string, senderIdentity: string, senderName: string) {
    // 1. Zoom/Teams style floating reaction stream on bottom-right
    const stream = document.getElementById('floating-reactions-stream');
    if (stream) {
      const bubble = document.createElement('div');
      bubble.className = 'reaction-bubble-float';
      bubble.innerHTML = `
        <span class="reaction-bubble-emoji">${emoji}</span>
        <span class="reaction-bubble-name">${senderName}</span>
      `;
      stream.appendChild(bubble);
      setTimeout(() => {
        bubble.remove();
      }, 3300);
    }

    // 2. Sender's tile reaction badge popup
    const tile = document.getElementById(`tile-${senderIdentity}`);
    if (tile) {
      const tileBadge = document.createElement('div');
      tileBadge.className = 'tile-reaction-badge';
      tileBadge.textContent = emoji;
      tile.appendChild(tileBadge);
      setTimeout(() => {
        tileBadge.remove();
      }, 2500);
    }
  }

  // Local Meeting Recording Methods (Zoom Style - Host Only)
  private openRecordingOptionsModal() {
    if (!this.isHost) {
      this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a gestiona înregistrarea.');
      return;
    }
    if (this.isRecording || this.roomRecordingActive) {
      this.stopRecording();
      return;
    }
    const modal = document.getElementById('recording-options-modal');
    if (modal) {
      if (!this.config.roomSettings?.autoRecordLayout) {
        const savedLayout = (typeof localStorage !== 'undefined' ? localStorage.getItem('vorbello_default_auto_record_layout') : null) as any;
        if (savedLayout === 'composite' || savedLayout === 'screen_only' || savedLayout === 'cameras_only') {
          this.selectedRecordingLayout = savedLayout;
        }
      }
      const layoutCompositeBtn = document.getElementById('rec-layout-composite');
      const layoutScreenBtn = document.getElementById('rec-layout-screen');
      const layoutCamerasBtn = document.getElementById('rec-layout-cameras');
      layoutCompositeBtn?.classList.toggle('selected', this.selectedRecordingLayout === 'composite');
      layoutScreenBtn?.classList.toggle('selected', this.selectedRecordingLayout === 'screen_only');
      layoutCamerasBtn?.classList.toggle('selected', this.selectedRecordingLayout === 'cameras_only');
      modal.style.display = 'flex';
    }
  }

  private closeRecordingOptionsModal() {
    const modal = document.getElementById('recording-options-modal');
    if (modal) modal.style.display = 'none';
  }

  private syncRecordingAudioInputs() {
    if (!this.recordingAudioCtx || !this.recordingAudioDest || this.recordingAudioCtx.state === 'closed') return;

    // 1. Sync local microphone track (LiveKit publication track or local mic stream track)
    try {
      const room = this.manager.getRoom();
      const localMicPub = room?.localParticipant?.getTrackPublication(Track.Source.Microphone);
      const mst = (localMicPub?.track as any)?.mediaStreamTrack as MediaStreamTrack | undefined;
      const liveMicTrack = (mst && mst.readyState === 'live')
        ? mst
        : this.manager.getLocalMicStream()?.getAudioTracks().find(t => t.readyState === 'live');

      if (liveMicTrack && liveMicTrack !== this.recordingLocalMicTrack) {
        if (this.recordingLocalMicSource) {
          try { this.recordingLocalMicSource.disconnect(); } catch (_) {}
        }
        const micStream = new MediaStream([liveMicTrack]);
        const micSource = this.recordingAudioCtx.createMediaStreamSource(micStream);
        const micGain = this.recordingAudioCtx.createGain();
        micGain.gain.value = 1.0;
        micSource.connect(micGain);
        micGain.connect(this.recordingAudioDest);

        this.recordingLocalMicSource = micSource;
        this.recordingLocalMicTrack = liveMicTrack;
        this.recordingAudioNodes.push(micSource, micGain);
        console.info('[Recording] Connected active local microphone track:', liveMicTrack.label || liveMicTrack.id);
      }
    } catch (e) {
      console.warn('[Recording] Local mic sync warning:', e);
    }

    // 2. Sync local screen share audio
    try {
      const localScreenStream = this.manager.getLocalScreenStream();
      const screenAudioTrack = localScreenStream?.getAudioTracks().find(t => t.readyState === 'live');
      if (screenAudioTrack && screenAudioTrack !== this.recordingScreenAudioTrack) {
        if (this.recordingScreenAudioSource) {
          try { this.recordingScreenAudioSource.disconnect(); } catch (_) {}
        }
        const screenStream = new MediaStream([screenAudioTrack]);
        const screenSource = this.recordingAudioCtx.createMediaStreamSource(screenStream);
        const screenGain = this.recordingAudioCtx.createGain();
        screenGain.gain.value = 1.0;
        screenSource.connect(screenGain);
        screenGain.connect(this.recordingAudioDest);

        this.recordingScreenAudioSource = screenSource;
        this.recordingScreenAudioTrack = screenAudioTrack;
        this.recordingAudioNodes.push(screenSource, screenGain);
        console.info('[Recording] Connected local screen share audio');
      }
    } catch (e) {
      console.warn('[Recording] Local screen audio sync warning:', e);
    }

    // 3. Sync all remote participants' audio elements
    try {
      document.querySelectorAll('audio[id^="audio-remote-"]').forEach(el => {
        const audioEl = el as HTMLAudioElement;
        if (audioEl.srcObject && (audioEl.srcObject as MediaStream).getAudioTracks().length > 0) {
          const remoteStream = audioEl.srcObject as MediaStream;
          const track = remoteStream.getAudioTracks().find(t => t.readyState === 'live');
          if (track && !(audioEl as any)._vorbelloRecAttached) {
            try {
              const remoteSource = this.recordingAudioCtx!.createMediaStreamSource(remoteStream);
              const remoteGain = this.recordingAudioCtx!.createGain();
              remoteGain.gain.value = 1.0;
              remoteSource.connect(remoteGain);
              remoteGain.connect(this.recordingAudioDest!);
              (audioEl as any)._vorbelloRecAttached = true;
              this.recordingAudioNodes.push(remoteSource, remoteGain);
              console.info('[Recording] Connected remote participant audio element:', audioEl.id);
            } catch (err) {
              console.warn('[Recording] Remote participant audio attach warning:', err);
            }
          }
        }
      });
    } catch (e) {
      console.warn('[Recording] Remote audio elements sync warning:', e);
    }
  }

  private async startRecording(type: 'video-hd' | 'video-fhd' | 'audio', container: 'webm' | 'mp4' = 'webm') {
    if (!this.isHost) {
      this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a înregistra.');
      return;
    }
    if (this.isRecording) return;

    try {
      // 1. Build comprehensive audio mix of all conference participants (local + remote)
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      const recAudioCtx = new AudioCtx({ sampleRate: 48000 });
      this.recordingAudioCtx = recAudioCtx;
      if (recAudioCtx.state === 'suspended') {
        await recAudioCtx.resume().catch(() => {});
      }
      const audioDest = recAudioCtx.createMediaStreamDestination();
      this.recordingAudioDest = audioDest;
      this.recordingAudioNodes = [];

      // Keep Web Audio clock running continuously with a persistent carrier oscillator
      try {
        const silentOsc = recAudioCtx.createOscillator();
        const silentGain = recAudioCtx.createGain();
        silentGain.gain.value = 0.00001;
        silentOsc.connect(silentGain);
        silentGain.connect(audioDest);
        silentOsc.start();
        this.recordingAudioNodes.push(silentOsc, silentGain);
      } catch (oscErr) {
        console.warn('[Recording] Silent oscillator setup error:', oscErr);
      }

      // Initial synchronization of all active audio inputs
      this.syncRecordingAudioInputs();

      // Continuous audio synchronization interval (every 1.5s) to capture unmutes and new participants
      if (this.recordingAudioSyncInterval) clearInterval(this.recordingAudioSyncInterval);
      this.recordingAudioSyncInterval = setInterval(() => {
        if (this.isRecording) {
          this.syncRecordingAudioInputs();
        }
      }, 1500);

      if (type === 'video-hd' || type === 'video-fhd') {
        const quality: 'hd' | 'fhd' = type === 'video-fhd' ? 'fhd' : 'hd';
        this.recordingQuality = quality;
        let videoTrack: MediaStreamTrack | null = null;

        // 1. Direct hardware camera track for cameras_only courses layout
        if (this.selectedRecordingLayout === 'cameras_only') {
          const localCamStream = this.manager.getLocalCameraStream();
          if (localCamStream && localCamStream.getVideoTracks().length > 0 && localCamStream.getVideoTracks()[0].readyState === 'live') {
            videoTrack = localCamStream.getVideoTracks()[0];
            console.info('[Recording] Using direct hardware camera track for pristine courses recording (0 canvas latency)');
          }
        }

        // 2. Direct Conference Composite Video Track at requested resolution & layout @ 30 fps
        if (!videoTrack) {
          videoTrack = this.createConferenceCompositeTrack(quality, this.selectedRecordingLayout);
        }

        // 3. Fallback to local screen share if active
        if (!videoTrack && this.manager.isScreenShareEnabled && this.manager.getLocalScreenStream()) {
          videoTrack = this.manager.getLocalScreenStream()!.getVideoTracks()[0] || null;
        }

        // Fallback to local or remote camera track directly if canvas couldn't start
        if (!videoTrack) {
          const localCamStream = this.manager.getLocalCameraStream();
          if (localCamStream && localCamStream.getVideoTracks().length > 0) {
            videoTrack = localCamStream.getVideoTracks()[0];
          }
        }
        if (!videoTrack) {
          const room = this.manager.getRoom();
          if (room && room.remoteParticipants) {
            for (const rp of room.remoteParticipants.values()) {
              const camPub = rp.getTrackPublication(Track.Source.Camera);
              if (camPub && camPub.track && camPub.track.mediaStreamTrack && camPub.track.mediaStreamTrack.readyState === 'live') {
                videoTrack = camPub.track.mediaStreamTrack;
                break;
              }
            }
          }
        }

        if (videoTrack) {
          videoTrack.onended = () => {
            if (this.isRecording) {
              console.info('[Recording] Captured video track ended, stopping recording...');
              this.stopRecording();
            }
          };

          const combinedStream = new MediaStream([videoTrack, ...audioDest.stream.getAudioTracks()]);
          this.startRecordingStream(combinedStream, 'video', quality, container);
        } else {
          // Fallback to audio if absolutely no video track could be acquired
          this.showFloatingToast('ℹ️ Nicio cameră activă detectată. Se înregistrează în format Audio HD.');
          this.startRecordingStream(new MediaStream(audioDest.stream.getAudioTracks()), 'audio', 'hd', container);
        }
      } else {
        // Audio HD Only
        this.recordingQuality = 'hd';
        this.startRecordingStream(new MediaStream(audioDest.stream.getAudioTracks()), 'audio', 'hd', container);
      }
    } catch (err: any) {
      console.error('[Recording] startRecording error:', err);
      this.showFloatingToast(`❌ Eroare la pornirea înregistrării: ${err?.message || err}`);
    }
  }

  private createConferenceCompositeTrack(
    quality: 'hd' | 'fhd' = 'hd',
    layout: 'composite' | 'screen_only' | 'cameras_only' = 'composite'
  ): MediaStreamTrack | null {
    try {
      const isFhd = quality === 'fhd';
      document.getElementById('vorbello-recording-canvas')?.remove();

      const canvas = document.createElement('canvas');
      canvas.id = 'vorbello-recording-canvas';
      canvas.width = isFhd ? 1920 : 1280;
      canvas.height = isFhd ? 1080 : 720;
      canvas.style.cssText = 'position:fixed; top:0; left:0; width:1px; height:1px; opacity:0.99; pointer-events:none; z-index:-9999; transform:scale(0.01); clip-path:inset(100%);';
      document.body.appendChild(canvas);
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;

      const pipW = isFhd ? 420 : 280;
      const pipH = isFhd ? 236 : 158;
      const pipMargin = isFhd ? 40 : 25;
      const pipX = canvas.width - pipW - pipMargin;
      const pipY = canvas.height - pipH - pipMargin;

      // Ensure dedicated hidden admin video element is ready for raw camera capture
      let recAdminVideo = document.getElementById('vorbello-rec-admin-video') as HTMLVideoElement | null;
      if (!recAdminVideo) {
        recAdminVideo = document.createElement('video');
        recAdminVideo.id = 'vorbello-rec-admin-video';
        recAdminVideo.autoplay = true;
        recAdminVideo.muted = true;
        recAdminVideo.playsInline = true;
        recAdminVideo.style.cssText = 'position:fixed; top:0; left:0; width:160px; height:90px; opacity:0.99; pointer-events:none; z-index:-9999; transform:scale(0.01); clip-path:inset(100%);';
        document.body.appendChild(recAdminVideo);
      }
      const rawCamStream = this.manager.getLocalCameraStream();
      if (rawCamStream && recAdminVideo.srcObject !== rawCamStream) {
        recAdminVideo.srcObject = rawCamStream;
        recAdminVideo.play().catch(() => {});
      }

      const drawFrame = () => {
        // Background
        ctx.fillStyle = '#0a0f1d';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Find active video elements
        // 1. Remote videos
        const remoteVideos = Array.from(document.querySelectorAll('video.remote-cam-video')) as HTMLVideoElement[];
        const activeRemoteVideos = remoteVideos.filter(v =>
          v.srcObject &&
          v.readyState >= 2 &&
          v.videoWidth > 0 &&
          v.style.display !== 'none'
        );

        // 2. Local self video
        const selfVideo = (document.getElementById('tile-self')?.querySelector('video') as HTMLVideoElement | null) ||
          (document.querySelector('video.self-cam-video') as HTMLVideoElement | null);
        const isSelfActive = !!(selfVideo &&
          selfVideo.srcObject &&
          selfVideo.readyState >= 2 &&
          selfVideo.videoWidth > 0 &&
          selfVideo.style.display !== 'none');

        const spotlightVideo = document.getElementById('spotlight-video') as HTMLVideoElement | null;
        const spotlightContainer = document.getElementById('spotlight-container');
        const isSpotlightActive = !!(spotlightVideo &&
          spotlightVideo.srcObject &&
          spotlightVideo.readyState >= 2 &&
          spotlightVideo.videoWidth > 0 &&
          spotlightContainer &&
          spotlightContainer.style.display !== 'none');

        // 1. "Cameră Curată": EXCLUSIVELY record Admin/Instructor camera full-screen for training courses
        // Zero remote students, zero splits, zero thumbnail overlays.
        if (layout === 'cameras_only') {
          let instructorCam: HTMLVideoElement | null = null;

          // Priority 1: Raw local camera stream in dedicated recording video
          if (recAdminVideo) {
            if (!recAdminVideo.srcObject || recAdminVideo.readyState < 2) {
              const currentRaw = this.manager.getLocalCameraStream();
              if (currentRaw && recAdminVideo.srcObject !== currentRaw) {
                recAdminVideo.srcObject = currentRaw;
                recAdminVideo.play().catch(() => {});
              }
            }
            if (recAdminVideo.readyState >= 2 && recAdminVideo.videoWidth > 0) {
              instructorCam = recAdminVideo;
            }
          }
          if (!instructorCam && isSelfActive && selfVideo) {
            instructorCam = selfVideo;
          } else if (!instructorCam && isSpotlightActive && spotlightVideo && !spotlightContainer?.classList.contains('is-screen-share')) {
            // Stage camera spotlight (Admin or Instructor pinned on stage)
            instructorCam = spotlightVideo;
          } else if (!instructorCam) {
            // Priority 2: Remote Admin participant tile
            const adminP = this.participants.find(p => (p.role === 'admin' || p.name.toLowerCase().includes('admin')) && p.identity !== 'self');
            if (adminP) {
              const adminTile = document.getElementById(`tile-${adminP.identity}`);
              const adminVid = adminTile?.querySelector('video') as HTMLVideoElement | null;
              if (adminVid && adminVid.readyState >= 2 && adminVid.videoWidth > 0) {
                instructorCam = adminVid;
              }
            }
          }

          if (!instructorCam && activeRemoteVideos.length > 0) {
            instructorCam = activeRemoteVideos[0];
          }

          if (instructorCam) {
            // Draw instructor camera across 100% of the canvas in 16:9 full-screen cover mode
            this.drawVideoCentered(ctx, instructorCam, 0, 0, canvas.width, canvas.height, 'cover');
          } else {
            ctx.fillStyle = '#0a0f1d';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#6366f1';
            ctx.font = `bold ${isFhd ? '48px' : '36px'} Inter, system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('Vorbello • Cameră Admin (Cursuri)', canvas.width / 2, canvas.height / 2 - 20);
            ctx.fillStyle = '#94a3b8';
            ctx.font = `${isFhd ? '24px' : '18px'} Inter, system-ui, sans-serif`;
            ctx.fillText('Camera este oprită', canvas.width / 2, canvas.height / 2 + 25);
          }
          return;
        }

        // 2. "Doar Ecran": EXCLUSIVELY record shared screen full-screen
        if (layout === 'screen_only') {
          if (isSpotlightActive && spotlightVideo) {
            this.drawVideoCentered(ctx, spotlightVideo, 0, 0, canvas.width, canvas.height, 'contain');
          } else {
            ctx.fillStyle = '#0f172a';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#6366f1';
            ctx.font = `bold ${isFhd ? '48px' : '36px'} Inter, system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.fillText('Partajare Ecran Oprită', canvas.width / 2, canvas.height / 2);
          }
          return;
        }

        // 3. "Mixt (Composite)": Screen share full-screen with Admin camera PiP in bottom-right
        if (isSpotlightActive && spotlightVideo) {
          // Draw main presentation
          this.drawVideoCentered(ctx, spotlightVideo, 0, 0, canvas.width, canvas.height, 'contain');

          // Overlay PiP camera only if screen share is active
          const isScreenShare = spotlightContainer?.classList.contains('is-screen-share');
          if (isScreenShare) {
            const pipCam = (isSelfActive && selfVideo) ? selfVideo : (activeRemoteVideos[0] || null);
            if (pipCam) {
              ctx.save();
              ctx.fillStyle = '#0f172a';
              ctx.fillRect(pipX, pipY, pipW, pipH);
              this.drawVideoCentered(ctx, pipCam, pipX, pipY, pipW, pipH, 'cover');
              ctx.strokeStyle = 'rgba(255, 255, 255, 0.45)';
              ctx.lineWidth = isFhd ? 3 : 2;
              ctx.strokeRect(pipX, pipY, pipW, pipH);
              ctx.restore();
            }
          }
        } else if (isSelfActive && selfVideo) {
          // Only self video active
          this.drawVideoCentered(ctx, selfVideo, 0, 0, canvas.width, canvas.height, 'cover');
        } else if (activeRemoteVideos.length === 1) {
          this.drawVideoCentered(ctx, activeRemoteVideos[0], 0, 0, canvas.width, canvas.height, 'cover');
        } else if (activeRemoteVideos.length > 1) {
          const allVideos = [...activeRemoteVideos];
          if (isSelfActive && selfVideo) allVideos.push(selfVideo);
          const count = allVideos.length;
          const cols = count <= 2 ? 2 : (count <= 4 ? 2 : 3);
          const rows = Math.ceil(count / cols);
          const cellW = canvas.width / cols;
          const cellH = canvas.height / rows;
          allVideos.forEach((v, idx) => {
            const col = idx % cols;
            const row = Math.floor(idx / cols);
            this.drawVideoCentered(ctx, v, col * cellW, row * cellH, cellW, cellH, 'cover');
          });
        } else {
          // No video cameras active: Draw branded conference card with active pulsating frame
          ctx.fillStyle = '#0f172a';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Pulsing REC badge to guarantee Chromium canvas capture stream stays active
          const pulse = Math.sin(Date.now() / 250) * 0.5 + 0.5;
          ctx.fillStyle = `rgba(239, 68, 68, ${0.4 + 0.6 * pulse})`;
          ctx.beginPath();
          ctx.arc(canvas.width / 2 - (isFhd ? 260 : 190), canvas.height / 2 - (isFhd ? 35 : 23), isFhd ? 12 : 8, 0, Math.PI * 2);
          ctx.fill();

          ctx.fillStyle = '#6366f1';
          ctx.font = `bold ${isFhd ? '54px' : '40px'} Inter, system-ui, sans-serif`;
          ctx.textAlign = 'center';
          ctx.fillText('Vorbello Conference', canvas.width / 2, canvas.height / 2 - (isFhd ? 30 : 20));

          ctx.fillStyle = '#94a3b8';
          ctx.font = `${isFhd ? '28px' : '20px'} Inter, system-ui, sans-serif`;
          ctx.fillText(`Înregistrare în curs • Calitate ${isFhd ? 'Full HD 1080p' : 'HD 720p'} (30 FPS)`, canvas.width / 2, canvas.height / 2 + (isFhd ? 35 : 25));
        }

        // Microscopic 2x2 dynamic frame heart-beat in corner (alternating #0a0f1d / #0a0f1e)
        // Guarantees Chromium CanvasCaptureMediaStreamTrack encoder NEVER stalls or emits 0 kbps!
        const heartBeat = (Date.now() % 2 === 0) ? '#0a0f1d' : '#0a0f1e';
        ctx.fillStyle = heartBeat;
        ctx.fillRect(0, 0, 2, 2);
      };

      // Draw initial frame immediately
      ctx.fillStyle = '#0a0f1d';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawFrame();

      // Capture stream from canvas at solid 30 fps
      const canvasStream = (canvas as any).captureStream ? (canvas as any).captureStream(30) : null;
      const tr = canvasStream?.getVideoTracks()[0];
      if (tr && (tr as any).requestFrame) {
        try { (tr as any).requestFrame(); } catch (_) {}
      }

      // Run render loop at solid 30 fps
      if (this.recordingCanvasInterval) clearInterval(this.recordingCanvasInterval);
      this.recordingCanvasInterval = setInterval(() => {
        drawFrame();
        if (tr && (tr as any).requestFrame) {
          try { (tr as any).requestFrame(); } catch (_) {}
        }
      }, 1000 / 30);

      if (canvasStream && canvasStream.getVideoTracks().length > 0) {
        return canvasStream.getVideoTracks()[0];
      }
    } catch (e) {
      console.warn('[Recording] Composite canvas failed:', e);
    }
    return null;
  }

  private drawVideoCentered(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    x: number,
    y: number,
    w: number,
    h: number,
    fitMode: 'cover' | 'contain' = 'cover'
  ) {
    try {
      const vw = video.videoWidth || 1280;
      const vh = video.videoHeight || 720;
      const hRatio = w / vw;
      const vRatio = h / vh;
      const ratio = fitMode === 'cover' ? Math.max(hRatio, vRatio) : Math.min(hRatio, vRatio);
      const drawW = vw * ratio;
      const drawH = vh * ratio;
      const centerShiftX = x + (w - drawW) / 2;
      const centerShiftY = y + (h - drawH) / 2;

      ctx.save();
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      ctx.drawImage(video, 0, 0, vw, vh, centerShiftX, centerShiftY, drawW, drawH);
      ctx.restore();
    } catch (_) {}
  }

  private startRecordingStream(stream: MediaStream, mode: 'video' | 'audio', quality: 'hd' | 'fhd' = 'hd', container: 'webm' | 'mp4' = 'webm') {
    this.recordingStream = stream;
    this.recordedChunks = [];
    this.isRecording = true;
    this.recordingMode = mode;
    this.recordingQuality = quality;
    this.recordingContainer = container;

    // Bitrate configuration strictly following 30 FPS specs:
    // HD 720p: 1500 kbps (1.5 Mbps) video, 128 kbps audio (~12 MB/min)
    // Full HD 1080p: 2500 kbps (2.5 Mbps) video, 128 kbps audio (~20 MB/min)
    const isFhd = quality === 'fhd';
    const videoBits = mode === 'video' ? (isFhd ? 2500000 : 1500000) : undefined;
    const audioBits = 128000;
    const totalBits = videoBits ? (videoBits + audioBits) : audioBits;

    this.recordedMimeType = this.getSupportedMimeType(mode === 'video', container);
    if (this.recordedMimeType.includes('mp4')) {
      this.recordingContainer = 'mp4';
    } else {
      this.recordingContainer = 'webm';
    }
    const recOptions: MediaRecorderOptions = {
      mimeType: this.recordedMimeType,
      videoBitsPerSecond: videoBits,
      audioBitsPerSecond: audioBits,
      bitsPerSecond: totalBits,
    };

    try {
      this.mediaRecorder = new MediaRecorder(stream, recOptions);
    } catch (e) {
      console.warn('[Recording] MediaRecorder with mimeType failed, falling back to default:', e);
      try {
        this.mediaRecorder = new MediaRecorder(stream, {
          videoBitsPerSecond: videoBits,
          audioBitsPerSecond: audioBits,
          bitsPerSecond: totalBits,
        });
      } catch (e2) {
        this.mediaRecorder = new MediaRecorder(stream);
      }
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
        console.debug(`[Recording] Chunk received: ${event.data.size} bytes (total chunks: ${this.recordedChunks.length})`);
      }
    };

    this.mediaRecorder.onerror = (event: any) => {
      console.error('[Recording] MediaRecorder runtime error:', event?.error || event);
      this.showFloatingToast(`❌ Eroare codec MediaRecorder: ${event?.error?.message || 'Eroare flux'}`);
    };

    this.mediaRecorder.onstop = async () => {
      // Allow browser async threads to finish pushing final dataavailable events
      await new Promise(r => setTimeout(r, 300));
      await this.handleRecordingFinished();
    };

    // Request data every 500ms for stable streaming chunks and rapid availability
    this.mediaRecorder.start(500);

    // Update UI
    this.recordingStartTime = Date.now();
    this.recordingSeconds = 0;
    this.updateRecordingUI(true);

    if (this.recordingInterval) clearInterval(this.recordingInterval);
    this.recordingInterval = setInterval(() => {
      this.recordingSeconds = Math.max(0, Math.floor((Date.now() - this.recordingStartTime) / 1000));
      this.updateRecordingTimerDisplay();
    }, 500);

    // Broadcast to entire room via LiveKit Data Channel if participant notification is enabled
    const shouldNotify = this.config.roomSettings?.autoRecordNotify !== false;
    if (shouldNotify) {
      this.manager.sendDataMessage({
        type: 'recording_status',
        isRecording: true,
        recorderName: this.config.displayName,
      });
    }

    const qualityLabel = mode === 'video' ? (isFhd ? 'Video Full HD 1080p (30 FPS)' : 'Video HD 720p (30 FPS)') : 'Audio HD';
    this.showFloatingToast(`🔴 Înregistrarea a început (${qualityLabel}).`);
  }

  private stopRecording() {
    this.closeRecordingOptionsModal();

    if (!this.isHost) {
      this.showFloatingToast('⛔ Doar persoana care a creat conferința (Gazda) are permisiunea de a opri înregistrarea.');
      return;
    }

    if (!this.isRecording) return;

    this.isRecording = false;
    this.recordingStopTime = Date.now();

    if (this.recordingInterval) {
      clearInterval(this.recordingInterval);
      this.recordingInterval = null;
    }
    if (this.recordingAudioSyncInterval) {
      clearInterval(this.recordingAudioSyncInterval);
      this.recordingAudioSyncInterval = null;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        if (this.mediaRecorder.state === 'recording') {
          // Flush pending data before final stop
          try { this.mediaRecorder.requestData(); } catch (_) {}
        }
        this.mediaRecorder.stop();
      } catch (e) {
        console.warn('[Recording] MediaRecorder stop error:', e);
      }
    }

    // AudioContext is closed in handleRecordingFinished() so audio tracks stay alive during final flush

    // Isolate current streams and intervals so that any subsequent recording is not affected
    const streamToStop = this.recordingStream;
    const canvasIntervalToClear = this.recordingCanvasInterval;
    this.recordingStream = null;
    this.recordingCanvasInterval = null;

    setTimeout(() => {
      if (canvasIntervalToClear) {
        clearInterval(canvasIntervalToClear);
      }
      if (streamToStop) {
        streamToStop.getTracks().forEach(t => t.stop());
      }
      const recAdminVid = document.getElementById('vorbello-rec-admin-video') as HTMLVideoElement | null;
      if (recAdminVid) {
        recAdminVid.srcObject = null;
        recAdminVid.remove();
      }
      const recCanvas = document.getElementById('vorbello-recording-canvas');
      if (recCanvas) {
        recCanvas.remove();
      }
    }, 600);

    this.updateRecordingUI(false);

    // Broadcast to entire room
    this.manager.sendDataMessage({
      type: 'recording_status',
      isRecording: false,
      recorderName: this.config.displayName,
    });
  }

  private updateRecordingUI(active: boolean) {
    const btn = document.getElementById('btn-toggle-record');
    const label = document.getElementById('label-record');
    const badge = document.getElementById('conf-rec-badge');
    const timer = document.getElementById('rec-timer');
    const sheetBtn = document.getElementById('sheet-btn-record');

    if (btn && label) {
      if (active) {
        btn.classList.add('active-record');
        label.textContent = 'Oprește Înreg.';
        const icon = btn.querySelector('.btn-icon');
        if (icon) icon.textContent = '⏹️';
      } else {
        btn.classList.remove('active-record');
        label.textContent = 'Înregistrează';
        const icon = btn.querySelector('.btn-icon');
        if (icon) icon.textContent = '⏺️';
      }
    }

    if (sheetBtn) {
      const iconCircle = sheetBtn.querySelector('.sheet-icon-circle');
      const itemLabel = sheetBtn.querySelector('.sheet-item-label');
      if (active) {
        sheetBtn.classList.add('active-record');
        if (iconCircle) iconCircle.textContent = '⏹️';
        if (itemLabel) itemLabel.textContent = 'Oprește Înreg.';
      } else {
        sheetBtn.classList.remove('active-record');
        if (iconCircle) iconCircle.textContent = '⏺️';
        if (itemLabel) itemLabel.textContent = 'Înregistrare';
      }
    }

    if (badge) {
      badge.style.display = active ? 'inline-flex' : 'none';
      if (active && !this.isHost) {
        badge.title = `Conferință înregistrată de ${this.roomRecorderName || 'Gazdă'}. Gestionată exclusiv de către organizator.`;
        badge.style.cursor = 'default';
      } else if (active) {
        badge.title = 'Înregistrare activă. Click pentru a opri înregistrarea.';
        badge.style.cursor = 'pointer';
      }
      if (!active && timer) timer.textContent = '00:00';
    }
  }

  private updateRecordingTimerDisplay() {
    const timer = document.getElementById('rec-timer');
    const label = document.getElementById('label-record');
    const sheetBtn = document.getElementById('sheet-btn-record');
    const mins = Math.floor(this.recordingSeconds / 60);
    const secs = this.recordingSeconds % 60;
    const timeStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;

    if (timer) timer.textContent = timeStr;
    if (label && this.isRecording) label.textContent = `Oprește (${timeStr})`;
    if (sheetBtn && this.isRecording) {
      const itemLabel = sheetBtn.querySelector('.sheet-item-label');
      if (itemLabel) itemLabel.textContent = `Oprește (${timeStr})`;
    }
  }

  private async handleRecordingFinished() {
    if (this.recordingAudioSyncInterval) {
      clearInterval(this.recordingAudioSyncInterval);
      this.recordingAudioSyncInterval = null;
    }
    document.querySelectorAll('audio[id^="audio-remote-"]').forEach(el => {
      delete (el as any)._vorbelloRecAttached;
    });
    if (this.recordingAudioNodes && this.recordingAudioNodes.length > 0) {
      for (const node of this.recordingAudioNodes) {
        try { node.disconnect(); } catch (_) {}
      }
      this.recordingAudioNodes = [];
    }
    this.recordingLocalMicSource = null;
    this.recordingLocalMicTrack = null;
    this.recordingScreenAudioSource = null;
    this.recordingScreenAudioTrack = null;
    this.recordingAudioDest = null;

    // Safely close the recording AudioContext now that the recorder has finished flushing
    if (this.recordingAudioCtx) {
      try {
        this.recordingAudioCtx.close();
      } catch (_) {}
      this.recordingAudioCtx = null;
    }

    if (this.recordedChunks.length === 0) {
      // Extra 250ms guard for slow browser background thread chunk delivery
      await new Promise(r => setTimeout(r, 250));
    }

    if (this.recordedChunks.length === 0) {
      console.warn('[Recording] No chunks received. MediaRecorder state:', this.mediaRecorder?.state, 'mimeType:', this.mediaRecorder?.mimeType);
      this.showFloatingToast('⚠️ Înregistrarea nu conține date.');
      return;
    }

    const isVideo = this.recordingMode === 'video';
    const actualMime = (this.mediaRecorder && this.mediaRecorder.mimeType) || this.recordedMimeType || (isVideo ? 'video/webm' : 'audio/webm');
    const isMp4 = actualMime.includes('mp4') || this.recordingContainer === 'mp4';
    const isOgg = actualMime.includes('ogg');
    const ext = isMp4 ? 'mp4' : (isOgg ? 'ogg' : 'webm');
    let blob = new Blob(this.recordedChunks, { type: actualMime });
    this.recordedChunks = [];

    const stopTime = this.recordingStopTime > 0 ? this.recordingStopTime : Date.now();
    const elapsedMs = this.recordingStartTime > 0 ? (stopTime - this.recordingStartTime) : (this.recordingSeconds * 1000);
    this.recordingSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const durationMs = Math.max(1000, this.recordingSeconds * 1000);

    // Fix WebM Duration so video players can seek/scrub through the file!
    if (ext === 'webm' && durationMs > 0) {
      try {
        blob = await fixWebmDuration(blob, durationMs);
        console.info(`[Recording] Successfully injected WebM duration header: ${durationMs}ms`);
      } catch (e) {
        console.warn('[Recording] Could not inject WebM duration:', e);
      }
    }

    const sizeMB = (blob.size / (1024 * 1024)).toFixed(1) + ' MB';
    const cleanRoom = (this.config.roomName || 'Conferinta').replace(/[^a-zA-Z0-9_-]/g, '_');
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10);
    const timeStr = `${String(now.getHours()).padStart(2, '0')}-${String(now.getMinutes()).padStart(2, '0')}-${String(now.getSeconds()).padStart(2, '0')}`;
    const resTag = isVideo ? (this.recordingQuality === 'fhd' ? '_1080p' : '_720p') : '_Audio';
    const fileName = `Vorbello_${cleanRoom}${resTag}_${dateStr}_${timeStr}.${ext}`;

    console.info(`[Recording] Finalized file: ${fileName} (${sizeMB}) format: ${ext}`);

    let savedPath: string | null = null;

    // 1. In Desktop Electron app: Save automatically to Videos/Vorbello Recordings using direct Uint8Array
    if ((window as any).vorbelloDesktop?.saveRecordingFile) {
      try {
        const arrayBuf = await blob.arrayBuffer();
        const uint8Array = new Uint8Array(arrayBuf);
        const res = await (window as any).vorbelloDesktop.saveRecordingFile({
          fileName,
          uint8Array,
        });
        if (res && res.success) {
          savedPath = res.path;
          console.info(`[Recording] Native desktop save successful: ${savedPath} (${res.size} bytes)`);
        } else {
          console.warn('[Recording] Desktop save returned error:', res?.error);
        }
      } catch (err) {
        console.warn('[Recording] Desktop direct buffer save error:', err);
      }
    }

    // 2. Browser direct file download fallback ONLY when not running in Desktop or if native save failed
    let browserDownloadUrl: string | undefined;
    if (!savedPath) {
      try {
        browserDownloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = browserDownloadUrl;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
          if (browserDownloadUrl) URL.revokeObjectURL(browserDownloadUrl);
          a.remove();
        }, 10000);
      } catch (e) {
        console.warn('[Recording] Download anchor click warning:', e);
      }
    }

    // Persist recording metadata to localStorage so it appears in the Admin Portal "Înregistrări" tab
    try {
      const existing = JSON.parse(localStorage.getItem('vorbello_recordings') || '[]');
      const mins = Math.floor(this.recordingSeconds / 60);
      const secs = this.recordingSeconds % 60;
      const durationFormatted = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      existing.unshift({
        id: `rec-${Date.now()}`,
        roomName: this.config.roomName,
        roomSlug: this.config.roomSlug,
        meetingTitle: `Înregistrare ${this.config.roomName}`,
        createdAt: `${dateStr} ${timeStr.replace(/-/g, ':')}`,
        duration: durationFormatted,
        durationSeconds: this.recordingSeconds,
        fileSize: sizeMB,
        format: this.selectedRecordingType,
        recordedBy: this.config.displayName || 'Administrator',
        fileName: fileName,
        filePath: savedPath || undefined,
        downloadUrl: browserDownloadUrl || undefined,
        storageType: savedPath ? 'disk' : 'download',
      });
      localStorage.setItem('vorbello_recordings', JSON.stringify(existing));
    } catch (e) {
      console.warn('[Recording] Failed to persist recording metadata:', e);
    }

    // 3. Show celebratory success modal
    this.showRecordingSuccessModal(fileName, sizeMB, savedPath);
  }

  private showRecordingSuccessModal(fileName: string, sizeMB: string, savedPath: string | null) {
    const modal = document.getElementById('recording-success-modal');
    const filenameEl = document.getElementById('rec-success-filename');
    const detailsEl = document.getElementById('rec-success-details');
    const openFolderBtn = document.getElementById('btn-open-rec-folder') as HTMLElement | null;

    if (filenameEl) filenameEl.textContent = fileName;
    if (detailsEl) {
      const mins = Math.floor(this.recordingSeconds / 60);
      const secs = this.recordingSeconds % 60;
      const durStr = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      detailsEl.innerHTML = `
        <div>Dimensiune: <strong>${sizeMB}</strong> &bull; Durată: <strong>${durStr}</strong></div>
        ${savedPath ? `<div style="margin-top: 6px; font-size: 0.72rem; color: #64748b; word-break: break-all;">Salvat în: ${savedPath}</div>` : ''}
      `;
    }

    if (openFolderBtn) {
      if (savedPath && (window as any).vorbelloDesktop?.showInFolder) {
        openFolderBtn.style.display = 'inline-flex';
        openFolderBtn.onclick = () => {
          (window as any).vorbelloDesktop.showInFolder(savedPath);
        };
      } else {
        openFolderBtn.style.display = 'none';
      }
    }

    if (modal) modal.style.display = 'flex';
  }

  private getSupportedMimeType(isVideo: boolean, container: 'webm' | 'mp4' = 'webm'): string {
    if (isVideo) {
      if (container === 'mp4') {
        // ONLY allow MP4 if dual audio/video codecs are truly supported by browser MediaRecorder!
        // Never allow video-only types like 'video/mp4;codecs=avc1' or 'video/mp4' which discard the audio track (0 kbps audio)!
        const mp4Types = [
          'video/mp4;codecs=avc1,mp4a.40.2',
          'video/mp4;codecs=avc1,opus',
        ];
        for (const t of mp4Types) {
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
            return t;
          }
        }
        console.warn('[Recording] Chromium does not support combined AVC1+AAC/Opus MP4 encoder, safely falling back to standard WebM VP8+Opus');
      }
      const types = [
        'video/webm;codecs=vp8,opus',
        'video/webm;codecs=vp9,opus',
        'video/webm',
      ];
      for (const t of types) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
          return t;
        }
      }
      return 'video/webm;codecs=vp8,opus';
    } else {
      if (container === 'mp4') {
        const mp4Types = [
          'audio/mp4;codecs=mp4a.40.2',
          'audio/mp4',
        ];
        for (const t of mp4Types) {
          if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
            return t;
          }
        }
        console.warn('[Recording] MP4 audio format not supported, falling back to WebM audio');
      }
      const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
      ];
      for (const t of types) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) {
          return t;
        }
      }
      return 'audio/webm;codecs=opus';
    }
  }

  // ==========================================
  // TABLĂ INTERACTIVĂ COLABORATIVĂ (WHITEBOARD)
  // ==========================================

  public toggleWhiteboard() {
    if (this.isWhiteboardOpen) {
      this.closeWhiteboard(true);
    } else {
      this.openWhiteboard(true);
    }
  }

  public openWhiteboard(broadcast: boolean = true) {
    this.isWhiteboardOpen = true;
    const container = document.getElementById('whiteboard-container');
    const btn = document.getElementById('btn-toggle-whiteboard');
    const label = document.getElementById('label-whiteboard');

    if (container) container.style.display = 'flex';
    if (btn) btn.classList.add('active-screen');
    if (label) label.textContent = 'Închide Tablă';

    setTimeout(() => {
      this.initWhiteboardCanvas();
    }, 50);
    setTimeout(() => {
      this.initWhiteboardCanvas();
    }, 250);

    if (broadcast) {
      this.manager.sendWhiteboardToggle(true);
    }
  }

  public closeWhiteboard(broadcast: boolean = true) {
    this.isWhiteboardOpen = false;
    const container = document.getElementById('whiteboard-container');
    const btn = document.getElementById('btn-toggle-whiteboard');
    const label = document.getElementById('label-whiteboard');

    if (container) container.style.display = 'none';
    if (btn) btn.classList.remove('active-screen');
    if (label) label.textContent = 'Tablă Albă';

    if (broadcast) {
      this.manager.sendWhiteboardToggle(false);
    }
  }

  private initWhiteboardCanvas() {
    const canvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    const wrapper = document.getElementById('wb-canvas-wrapper');
    if (!canvas || !wrapper) return;

    this.wbCanvas = canvas;
    const dpr = window.devicePixelRatio || 1;
    const rect = wrapper.getBoundingClientRect();
    const w = Math.floor(rect.width || 800);
    const h = Math.floor(rect.height || 600);

    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx && canvas.width > 0 && canvas.height > 0) {
        tempCtx.drawImage(canvas, 0, 0);
      }

      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      this.wbCtx = canvas.getContext('2d');
      if (this.wbCtx) {
        this.wbCtx.scale(dpr, dpr);
        if (tempCanvas.width > 0) {
          this.wbCtx.drawImage(tempCanvas, 0, 0, w, h);
        }
      }
    } else if (!this.wbCtx) {
      this.wbCtx = canvas.getContext('2d');
      if (this.wbCtx) {
        this.wbCtx.scale(dpr, dpr);
      }
    }

    if (typeof ResizeObserver !== 'undefined' && !(wrapper as any)._wbObserverAttached) {
      (wrapper as any)._wbObserverAttached = true;
      const ro = new ResizeObserver(() => {
        if (this.isWhiteboardOpen) {
          this.initWhiteboardCanvas();
        }
      });
      ro.observe(wrapper);
    }

    if ((canvas as any)._wbListenersAttached) return;
    (canvas as any)._wbListenersAttached = true;

    const getPos = (e: PointerEvent) => {
      const cRect = canvas.getBoundingClientRect();
      return {
        x: e.clientX - cRect.left,
        y: e.clientY - cRect.top,
        w: cRect.width,
        h: cRect.height,
      };
    };

    canvas.addEventListener('pointerdown', (e: PointerEvent) => {
      e.preventDefault();
      try { canvas.setPointerCapture(e.pointerId); } catch (_) {}
      this.isDrawing = true;
      const pos = getPos(e);
      this.lastX = pos.x;
      this.lastY = pos.y;
    });

    canvas.addEventListener('pointermove', (e: PointerEvent) => {
      if (!this.isDrawing) return;
      e.preventDefault();
      const pos = getPos(e);
      const x0 = this.lastX;
      const y0 = this.lastY;
      const x1 = pos.x;
      const y1 = pos.y;

      this.drawWhiteboardSegment(x0, y0, x1, y1, this.wbColor, this.wbSize, this.wbTool);

      if (pos.w > 0 && pos.h > 0) {
        this.manager.sendWhiteboardDraw({
          x0: x0 / pos.w,
          y0: y0 / pos.h,
          x1: x1 / pos.w,
          y1: y1 / pos.h,
          color: this.wbColor,
          size: this.wbSize,
          tool: this.wbTool,
        });
      }

      this.lastX = x1;
      this.lastY = y1;
    });

    const stopDrawing = (e: PointerEvent) => {
      if (this.isDrawing) {
        try { canvas.releasePointerCapture(e.pointerId); } catch (_) {}
      }
      this.isDrawing = false;
    };
    canvas.addEventListener('pointerup', stopDrawing);
    canvas.addEventListener('pointerleave', stopDrawing);
    canvas.addEventListener('pointercancel', stopDrawing);
  }

  private drawWhiteboardSegment(x0: number, y0: number, x1: number, y1: number, color: string, size: number, tool: string) {
    if (!this.wbCtx) {
      if (this.wbCanvas) this.wbCtx = this.wbCanvas.getContext('2d');
    }
    if (!this.wbCtx) return;

    this.wbCtx.save();
    if (tool === 'eraser') {
      this.wbCtx.globalCompositeOperation = 'destination-out';
      this.wbCtx.lineWidth = size * 6;
    } else {
      this.wbCtx.globalCompositeOperation = 'source-over';
      this.wbCtx.strokeStyle = color;
      this.wbCtx.lineWidth = size;
    }
    this.wbCtx.lineCap = 'round';
    this.wbCtx.lineJoin = 'round';
    this.wbCtx.beginPath();
    this.wbCtx.moveTo(x0, y0);
    this.wbCtx.lineTo(x1, y1);
    this.wbCtx.stroke();
    this.wbCtx.restore();
  }

  public handleRemoteWhiteboardDraw(data: { x0: number; y0: number; x1: number; y1: number; color: string; size: number; tool: string }) {
    if (!this.isWhiteboardOpen) {
      this.openWhiteboard(false);
    }
    const canvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    if (w <= 0 || h <= 0) return;

    this.drawWhiteboardSegment(
      data.x0 * w,
      data.y0 * h,
      data.x1 * w,
      data.y1 * h,
      data.color,
      data.size,
      data.tool
    );
  }

  public clearWhiteboard(broadcast: boolean = true) {
    if (!this.wbCanvas) {
      this.wbCanvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    }
    if (this.wbCanvas) {
      const ctx = this.wbCanvas.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, this.wbCanvas.width, this.wbCanvas.height);
        ctx.restore();
      }
    }
    if (broadcast) {
      this.manager.sendWhiteboardClear();
      this.showFloatingToast('🗑️ Ați curățat tabla albă.');
    } else {
      this.showFloatingToast('🗑️ Tabla albă a fost curățată de un participant.');
    }
  }

  public downloadWhiteboardPNG() {
    if (!this.wbCanvas) {
      this.wbCanvas = document.getElementById('whiteboard-canvas') as HTMLCanvasElement | null;
    }
    if (!this.wbCanvas) return;

    try {
      const exportCanvas = document.createElement('canvas');
      exportCanvas.width = this.wbCanvas.width;
      exportCanvas.height = this.wbCanvas.height;
      const expCtx = exportCanvas.getContext('2d');
      if (expCtx) {
        expCtx.fillStyle = '#0b0f19';
        expCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
        expCtx.drawImage(this.wbCanvas, 0, 0);
      }
      const dataUrl = exportCanvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = dataUrl;
      const dateStr = new Date().toISOString().slice(0, 10);
      a.download = `Vorbello-Tabla-${dateStr}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      this.showFloatingToast('💾 Imaginea tablei a fost salvată ca PNG!');
    } catch (e) {
      console.warn('[Whiteboard] Download error:', e);
    }
  }

  // ==========================================
  // SONDAJE LIVE ÎN SALĂ (LIVE POLLS)
  // ==========================================

  public openCreatePollModal() {
    const modal = document.getElementById('create-poll-modal');
    const questionInput = document.getElementById('input-poll-question') as HTMLInputElement | null;
    const inputsList = document.getElementById('poll-options-inputs-list');

    if (questionInput) questionInput.value = '';
    if (inputsList) {
      inputsList.innerHTML = `
        <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 1 (ex: Da, sunt de acord)" />
        <input type="text" class="invite-input-field poll-opt-input" style="width: 100%; font-family: inherit;" placeholder="Opțiunea 2 (ex: Nu, am obiecții)" />
      `;
    }
    if (modal) modal.style.display = 'flex';
  }

  public closeCreatePollModal() {
    const modal = document.getElementById('create-poll-modal');
    if (modal) modal.style.display = 'none';
  }

  public submitNewPoll() {
    const questionInput = document.getElementById('input-poll-question') as HTMLInputElement | null;
    const question = (questionInput?.value || '').trim();
    if (!question) {
      alert('Vă rugăm să introduceți întrebarea sondajului.');
      return;
    }

    const optInputs = document.querySelectorAll('.poll-opt-input') as NodeListOf<HTMLInputElement>;
    const options: string[] = [];
    optInputs.forEach(inp => {
      const val = inp.value.trim();
      if (val) options.push(val);
    });

    if (options.length < 2) {
      alert('Vă rugăm să introduceți cel puțin 2 opțiuni de răspuns.');
      return;
    }

    const pollId = `poll-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    const votes: { [key: number]: number } = {};
    options.forEach((_, idx) => {
      votes[idx] = 0;
    });

    this.activePoll = {
      id: pollId,
      question,
      options,
      votes,
      totalVotes: 0,
      myVoteIndex: null,
      creatorName: this.config.displayName || 'Gazda',
      isClosed: false,
    };

    this.manager.sendPollLaunch(pollId, question, options);
    this.closeCreatePollModal();

    this.isPollsOpen = true;
    this.isChatOpen = false;
    this.isParticipantsOpen = false;
    this.toggleDrawer();

    this.showFloatingToast('🚀 Sondajul a fost lansat cu succes tuturor participanților!');
  }

  public handleRemotePollLaunch(data: { pollId: string; question: string; options: string[]; creatorName: string }) {
    const votes: { [key: number]: number } = {};
    data.options.forEach((_, idx) => {
      votes[idx] = 0;
    });

    this.activePoll = {
      id: data.pollId,
      question: data.question,
      options: data.options,
      votes,
      totalVotes: 0,
      myVoteIndex: null,
      creatorName: data.creatorName,
      isClosed: false,
    };

    if (this.isPollsOpen) {
      this.renderPollsView();
    } else {
      this.showLivePollPopup();
    }

    this.playChatChime();
    this.showFloatingToast(`📊 Sondaj nou de la ${data.creatorName}: ${data.question}`);
  }

  public voteInPoll(pollId: string, optionIndex: number) {
    if (!this.activePoll || this.activePoll.id !== pollId || this.activePoll.isClosed) return;
    if (this.activePoll.myVoteIndex !== null) return;

    this.activePoll.myVoteIndex = optionIndex;
    this.activePoll.votes[optionIndex] = (this.activePoll.votes[optionIndex] || 0) + 1;
    this.activePoll.totalVotes++;

    this.manager.sendPollVote(pollId, optionIndex);
    this.renderPollsView();

    const popup = document.getElementById('live-poll-popup');
    if (popup && popup.style.display !== 'none') {
      this.showLivePollPopup();
    }

    this.showFloatingToast('✅ Votul dumneavoastră a fost înregistrat!');
  }

  public handleRemotePollVote(data: { pollId: string; optionIndex: number; voterName: string; voterIdentity: string }) {
    if (!this.activePoll || this.activePoll.id !== data.pollId) return;

    const optIdx = data.optionIndex;
    this.activePoll.votes[optIdx] = (this.activePoll.votes[optIdx] || 0) + 1;
    this.activePoll.totalVotes++;

    if (this.isPollsOpen) {
      this.renderPollsView();
    }
    const popup = document.getElementById('live-poll-popup');
    if (popup && popup.style.display !== 'none') {
      this.showLivePollPopup();
    }
  }

  public closeActivePoll(broadcast: boolean = true) {
    if (!this.activePoll) return;
    this.activePoll.isClosed = true;

    if (broadcast) {
      this.manager.sendPollClose(this.activePoll.id);
    }

    this.renderPollsView();
    const popup = document.getElementById('live-poll-popup');
    if (popup) popup.style.display = 'none';

    this.showFloatingToast('⏹️ Sondajul a fost încheiat.');
  }

  public handleRemotePollClose(data: { pollId: string }) {
    if (this.activePoll && this.activePoll.id === data.pollId) {
      this.activePoll.isClosed = true;
      if (this.isPollsOpen) {
        this.renderPollsView();
      }
      const popup = document.getElementById('live-poll-popup');
      if (popup) popup.style.display = 'none';
      this.showFloatingToast('⏹️ Sondajul a fost încheiat de organizator.');
    }
  }

  public renderPollsView() {
    const container = document.getElementById('polls-list-content');
    if (!container) return;

    if (!this.activePoll) {
      container.innerHTML = `
        <div style="text-align: center; padding: 2.5rem 1rem; color: #94a3b8; display: flex; flex-direction: column; align-items: center; gap: 0.75rem;">
          <div style="font-size: 2.2rem;">📊</div>
          <div style="font-weight: 600; font-size: 0.95rem; color: #e2e8f0;">Nu există sondaje active</div>
          <div style="font-size: 0.75rem; max-width: 240px; line-height: 1.4;">Apasă butonul de mai sus „➕ Creează Sondaj” pentru a adresa o întrebare tuturor participanților.</div>
        </div>
      `;
      return;
    }

    const poll = this.activePoll;
    const hasVoted = poll.myVoteIndex !== null;
    const total = poll.totalVotes;

    const optionsHtml = poll.options.map((optText, idx) => {
      const voteCount = poll.votes[idx] || 0;
      const pct = total > 0 ? Math.round((voteCount / total) * 100) : 0;
      const isMyChoice = poll.myVoteIndex === idx;

      return `
        <div class="poll-option-row ${isMyChoice ? 'voted' : ''}" data-poll-id="${poll.id}" data-opt-idx="${idx}" style="${!hasVoted && !poll.isClosed ? 'cursor: pointer;' : 'cursor: default;'}">
          <div class="poll-option-progress-bar" style="width: ${pct}%;"></div>
          <div class="poll-option-content">
            <span>${isMyChoice ? '✅ ' : ''}${optText}</span>
          </div>
          <div class="poll-option-stats">
            <span>${pct}%</span>
            <span style="font-size: 0.7rem; color: #64748b; margin-left: 4px;">(${voteCount})</span>
          </div>
        </div>
      `;
    }).join('');

    container.innerHTML = `
      <div class="poll-card">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 0.5rem;">
          <div>
            <div class="poll-question-title">${poll.question}</div>
            <div class="poll-creator-sub">Lansat de ${poll.creatorName} &bull; Total voturi: <strong>${total}</strong></div>
          </div>
          <span class="poll-active-badge" style="${poll.isClosed ? 'background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.35); color: #f87171;' : ''}">
            ${poll.isClosed ? '⏹️ Încheiat' : '🟢 Activ'}
          </span>
        </div>

        <div class="poll-options-list">
          ${optionsHtml}
        </div>

        <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 4px; font-size: 0.72rem; color: #94a3b8;">
          <span>${hasVoted ? 'Ai votat deja' : (poll.isClosed ? 'Votarea este încheiată' : 'Apasă pe o opțiune pentru a vota')}</span>
          ${!poll.isClosed ? `
            <button class="btn btn-secondary btn-sm" id="btn-close-this-poll" style="font-size: 0.7rem; padding: 2px 8px; color: #f87171;">
              ⏹️ Încheie Sondaj
            </button>
          ` : ''}
        </div>
      </div>
    `;

    if (!hasVoted && !poll.isClosed) {
      container.querySelectorAll('.poll-option-row').forEach(row => {
        row.addEventListener('click', () => {
          const pId = row.getAttribute('data-poll-id');
          const optIdx = Number(row.getAttribute('data-opt-idx'));
          if (pId && !isNaN(optIdx)) {
            this.voteInPoll(pId, optIdx);
          }
        });
      });
    }

    document.getElementById('btn-close-this-poll')?.addEventListener('click', () => {
      this.closeActivePoll(true);
    });
  }

  public showLivePollPopup() {
    const popup = document.getElementById('live-poll-popup');
    if (!popup || !this.activePoll || this.activePoll.isClosed) {
      if (popup) popup.style.display = 'none';
      return;
    }

    const poll = this.activePoll;
    const hasVoted = poll.myVoteIndex !== null;
    const total = poll.totalVotes;

    const optionsHtml = poll.options.map((optText, idx) => {
      const voteCount = poll.votes[idx] || 0;
      const pct = total > 0 ? Math.round((voteCount / total) * 100) : 0;
      const isMyChoice = poll.myVoteIndex === idx;

      return `
        <div class="poll-option-row ${isMyChoice ? 'voted' : ''}" data-pop-poll-id="${poll.id}" data-pop-opt-idx="${idx}" style="${!hasVoted ? 'cursor: pointer;' : 'cursor: default;'} margin-bottom: 6px;">
          <div class="poll-option-progress-bar" style="width: ${pct}%;"></div>
          <div class="poll-option-content" style="font-size: 0.8rem;">
            <span>${isMyChoice ? '✅ ' : ''}${optText}</span>
          </div>
          <div class="poll-option-stats" style="font-size: 0.75rem;">
            <span>${pct}%</span>
          </div>
        </div>
      `;
    }).join('');

    popup.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
        <div style="font-weight: 700; font-size: 0.85rem; color: #38bdf8; display: flex; align-items: center; gap: 5px;">
          <span>📊</span> Sondaj Live
        </div>
        <button id="btn-close-poll-pop" style="background: none; border: none; color: #94a3b8; font-size: 1.1rem; cursor: pointer;">&times;</button>
      </div>
      <div style="font-weight: 700; font-size: 0.9rem; color: #fff; margin-bottom: 8px;">${poll.question}</div>
      <div style="display: flex; flex-direction: column;">
        ${optionsHtml}
      </div>
      <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 8px; font-size: 0.72rem;">
        <span style="color: #94a3b8;">${hasVoted ? 'Vot salvat' : 'Alege răspunsul'}</span>
        <button id="btn-pop-open-drawer" class="btn btn-primary btn-sm" style="font-size: 0.7rem; padding: 2px 8px;">
          Deschide Panou &rarr;
        </button>
      </div>
    `;

    popup.style.display = 'block';

    if (!hasVoted) {
      popup.querySelectorAll('[data-pop-poll-id]').forEach(row => {
        row.addEventListener('click', () => {
          const pId = row.getAttribute('data-pop-poll-id');
          const optIdx = Number(row.getAttribute('data-pop-opt-idx'));
          if (pId && !isNaN(optIdx)) {
            this.voteInPoll(pId, optIdx);
          }
        });
      });
    }

    document.getElementById('btn-close-poll-pop')?.addEventListener('click', () => {
      popup.style.display = 'none';
    });

    document.getElementById('btn-pop-open-drawer')?.addEventListener('click', () => {
      popup.style.display = 'none';
      this.isPollsOpen = true;
      this.isChatOpen = false;
      this.isParticipantsOpen = false;
      this.toggleDrawer();
    });
  }

  private dataUrlToBlob(dataUrl: string, fallbackMime = 'application/octet-stream'): Blob {
    try {
      const commaIdx = dataUrl.indexOf(',');
      const header = commaIdx !== -1 ? dataUrl.substring(0, commaIdx) : '';
      const b64Data = commaIdx !== -1 ? dataUrl.substring(commaIdx + 1) : dataUrl;
      const mimeMatch = header.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : fallbackMime;
      const byteChars = atob(b64Data);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: mime });
    } catch (e) {
      console.warn('[ConferenceUI] dataUrlToBlob fallback:', e);
      return new Blob([dataUrl], { type: fallbackMime });
    }
  }

  private playKnockChime() {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(523.25, now);
      osc.frequency.setValueAtTime(659.25, now + 0.15);
      gain.gain.setValueAtTime(0.2, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.45);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.45);
    } catch (_) {}
  }

  private enterWaitingRoomLobby(isMoveBack: boolean = false) {
    this.isWaitingInLobby = true;
    try {
      if (this.manager.isMicEnabled) {
        this.manager.enableMicrophone(false).catch(() => {});
      }
      if (this.manager.isCameraEnabled) {
        this.manager.enableCamera(false).catch(() => {});
      }
    } catch (_) {}

    let overlay = document.getElementById('waiting-room-lobby-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'waiting-room-lobby-overlay';
      overlay.style.cssText = `
        position: fixed; inset: 0; z-index: 99999;
        background: radial-gradient(circle at 50% 30%, #1e293b 0%, #090d16 100%);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        padding: 24px; color: #f8fafc; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        text-align: center; backdrop-filter: blur(20px);
      `;
      document.body.appendChild(overlay);
    }

    const isWaitForHost = !this.config.roomSettings?.waitingRoomEnabled;
    const hostTitle = isWaitForHost ? 'În așteptarea gazdei...' : 'Sala de Așteptare este Activă';
    const hostDesc = isMoveBack
      ? 'Gazda v-a redirecționat temporar în sala de așteptare.'
      : (isWaitForHost
          ? `Conferința „${this.config.roomName || this.config.roomSlug}” va începe automat imediat ce gazda (${this.config.roomSettings?.creatorName || 'organizatorul'}) se conectează.`
          : 'Vă rugăm să așteptați. Gazda ședinței vă va permite accesul în conferință în scurt timp.');
    const statusText = isWaitForHost ? '● În așteptarea conectării gazdei' : '● Cerere transmisă gazdei';

    overlay.style.display = 'flex';
    overlay.innerHTML = `
      <div style="max-width: 480px; width: 100%; background: rgba(30, 41, 59, 0.7); border: 1px solid rgba(245, 158, 11, 0.35); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7); border-radius: 20px; padding: 2.2rem; display: flex; flex-direction: column; align-items: center; gap: 1.25rem;">
        <div style="position: relative; width: 80px; height: 80px; display: flex; align-items: center; justify-content: center;">
          <div style="position: absolute; inset: 0; border: 3px solid rgba(245, 158, 11, 0.2); border-top-color: #f59e0b; border-radius: 50%; animation: spin 1.2s linear infinite;"></div>
          <span style="font-size: 2.2rem;">⏳</span>
        </div>
        
        <div>
          <h2 style="font-size: 1.4rem; font-weight: 800; color: #fff; margin-bottom: 0.35rem;">${hostTitle}</h2>
          <p style="font-size: 0.875rem; color: #94a3b8; line-height: 1.5;">
            ${hostDesc}
          </p>
        </div>

        <div style="width: 100%; background: rgba(15, 23, 42, 0.6); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px; padding: 0.85rem 1rem; text-align: left; font-size: 0.8rem;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: var(--text-muted);">Sală:</span>
            <strong style="color: #38bdf8;">${this.config.roomName || this.config.roomSlug}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; margin-bottom: 4px;">
            <span style="color: var(--text-muted);">Participant:</span>
            <strong style="color: #f8fafc;">${this.config.displayName}</strong>
          </div>
          <div style="display: flex; justify-content: space-between;">
            <span style="color: var(--text-muted);">Statut:</span>
            <span style="color: #fbbf24; font-weight: 700;">${statusText}</span>
          </div>
        </div>

        <div style="display: flex; gap: 0.75rem; width: 100%;">
          <button id="btn-leave-waiting-room" class="btn btn-secondary btn-sm" style="flex: 1; padding: 0.65rem; border-radius: 10px; font-weight: 600;">
            🚪 Părăsește Ședința
          </button>
        </div>
      </div>
    `;

    document.getElementById('btn-leave-waiting-room')?.addEventListener('click', () => {
      this.cleanup();
      this.config.onLeave('Ați părăsit sala de așteptare.');
    });

    if (isMoveBack) {
      this.manager.sendWaitingRoomKnock(this.config.displayName).catch(() => {});
    }
  }

  private async handleWaitingRoomDecision(decision: 'admitted' | 'rejected') {
    if (decision === 'admitted') {
      this.isWaitingInLobby = false;
      if (this.waitingRoomKnockInterval) {
        clearInterval(this.waitingRoomKnockInterval);
        this.waitingRoomKnockInterval = null;
      }
      const overlay = document.getElementById('waiting-room-lobby-overlay');
      if (overlay) {
        overlay.style.transition = 'opacity 0.4s ease-out';
        overlay.style.opacity = '0';
        setTimeout(() => overlay.remove(), 400);
      }
      this.showFloatingToast('✅ Gazda v-a permis accesul în conferință!');

      try {
        if (this.manager.isCameraEnabled) {
          await this.manager.enableCamera(true);
        }
        if (!this.userExplicitlyMuted && this.manager.isMicEnabled) {
          await this.manager.enableMicrophone(true);
        }
      } catch (e) {
        console.warn('[ConferenceUI] Post-admission track publish error:', e);
      }
      this.updateControlsState();
      this.updateSelfTile();
      this.renderVideoGrid();
    } else if (decision === 'rejected') {
      this.isWaitingInLobby = false;
      if (this.waitingRoomKnockInterval) {
        clearInterval(this.waitingRoomKnockInterval);
        this.waitingRoomKnockInterval = null;
      }
      const overlay = document.getElementById('waiting-room-lobby-overlay');
      if (overlay) {
        overlay.innerHTML = `
          <div style="max-width: 440px; width: 100%; background: rgba(30, 41, 59, 0.9); border: 1px solid rgba(239, 68, 68, 0.4); box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7); border-radius: 20px; padding: 2.2rem; text-align: center;">
            <div style="font-size: 2.8rem; margin-bottom: 0.75rem;">⛔</div>
            <h2 style="font-size: 1.3rem; font-weight: 800; color: #f87171; margin-bottom: 0.5rem;">Acces Neautorizat</h2>
            <p style="font-size: 0.875rem; color: #cbd5e1; margin-bottom: 1.5rem; line-height: 1.5;">Gazda a respins cererea de acces la această conferință.</p>
            <button id="btn-rejected-leave" class="btn btn-primary" style="width: 100%; padding: 0.65rem; border-radius: 10px;">Înapoi la Pagina Principală</button>
          </div>
        `;
        document.getElementById('btn-rejected-leave')?.addEventListener('click', () => {
          this.cleanup();
          this.config.onLeave('Acces respins de gazdă.');
        });
      }
    }
  }

  private handleWaitingRoomKnock(data: { identity: string; name: string; timestamp: number }) {
    // Both admins and regular participants must know that this identity is waiting in lobby,
    // so that neither admin nor regular attendee shows their tile on stage before admission!
    const exists = this.waitingParticipants.find(w => w.identity === data.identity);
    if (!exists) {
      this.waitingParticipants.push({
        identity: data.identity,
        name: data.name,
        timestamp: data.timestamp || Date.now(),
      });
      // Only host/admins hear chime and see admission banner
      if (this.isAdmin || this.isModerator) {
        this.playKnockChime();
        this.showWaitingRoomBanner(data.name, data.identity);
      }
      this.renderVideoGrid();
      this.renderParticipantsList();
      this.updateControlsState();
      this.updateStageLayout();
    }
  }

  private showWaitingRoomBanner(name: string, identity: string) {
    let banner = document.getElementById('waiting-room-host-banner');
    if (!banner) {
      banner = document.createElement('div');
      banner.id = 'waiting-room-host-banner';
      banner.style.cssText = `
        position: absolute; top: 14px; left: 50%; transform: translateX(-50%);
        z-index: 1000; background: rgba(15, 23, 42, 0.95);
        border: 1px solid rgba(245, 158, 11, 0.5); box-shadow: 0 10px 25px -5px rgba(0,0,0,0.6);
        border-radius: 12px; padding: 10px 18px; display: flex; align-items: center; gap: 14px;
        backdrop-filter: blur(12px); color: #fff;
      `;
      const stage = document.getElementById('conf-container') || document.body;
      stage.appendChild(banner);
    }

    banner.innerHTML = `
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 1.25rem;">⏳</span>
        <div>
          <div style="font-weight: 700; font-size: 0.85rem; color: #fbbf24;">${name} este în sala de așteptare</div>
          <div style="font-size: 0.72rem; color: #94a3b8;">${this.waitingParticipants.length} participant(ți) în așteptare</div>
        </div>
      </div>
      <div style="display: flex; align-items: center; gap: 6px;">
        <button class="btn btn-primary btn-sm btn-admit-banner" data-id="${identity}" style="padding: 4px 10px; font-size: 0.75rem; border-radius: 6px;">✅ Admite</button>
        <button class="btn btn-secondary btn-sm btn-reject-banner" data-id="${identity}" style="padding: 4px 10px; font-size: 0.75rem; border-radius: 6px; color: #f87171;">❌ Respinge</button>
      </div>
    `;

    banner.querySelector('.btn-admit-banner')?.addEventListener('click', () => {
      this.admitWaitingParticipant(identity);
    });
    banner.querySelector('.btn-reject-banner')?.addEventListener('click', () => {
      this.rejectWaitingParticipant(identity);
    });
  }

  public async admitWaitingParticipant(identity: string) {
    await this.manager.sendWaitingRoomDecision(identity, 'admitted');
    this.waitingParticipants = this.waitingParticipants.filter(w => w.identity !== identity);
    const banner = document.getElementById('waiting-room-host-banner');
    if (banner) {
      if (this.waitingParticipants.length === 0) {
        banner.remove();
      } else {
        const next = this.waitingParticipants[0];
        this.showWaitingRoomBanner(next.name, next.identity);
      }
    }
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();
    this.showFloatingToast('Participant admis în conferință.');
  }

  public async admitAllWaitingParticipants() {
    for (const wp of [...this.waitingParticipants]) {
      await this.manager.sendWaitingRoomDecision(wp.identity, 'admitted');
    }
    this.waitingParticipants = [];
    document.getElementById('waiting-room-host-banner')?.remove();
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();
    this.showFloatingToast('Toți participanții din sala de așteptare au fost admiși.');
  }

  public async rejectWaitingParticipant(identity: string) {
    await this.manager.sendWaitingRoomDecision(identity, 'rejected');
    this.waitingParticipants = this.waitingParticipants.filter(w => w.identity !== identity);
    const banner = document.getElementById('waiting-room-host-banner');
    if (banner) {
      if (this.waitingParticipants.length === 0) {
        banner.remove();
      } else {
        const next = this.waitingParticipants[0];
        this.showWaitingRoomBanner(next.name, next.identity);
      }
    }
    this.renderVideoGrid();
    this.renderParticipantsList();
    this.updateControlsState();
    this.updateStageLayout();
    this.showFloatingToast('Acces respins.');
  }

  public async moveParticipantToWaitingRoom(identity: string) {
    await this.manager.sendWaitingRoomMoveBack(identity);
    const p = this.participants.find(part => part.identity === identity);
    if (p) {
      this.waitingParticipants.push({
        identity: p.identity,
        name: p.name,
        timestamp: Date.now(),
      });
      this.participants = this.participants.filter(part => part.identity !== identity);
      this.renderVideoGrid();
      this.renderParticipantsList();
      this.showFloatingToast(`${p.name} a fost trimis în sala de așteptare.`);
    }
  }

  public async cleanup(): Promise<void> {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
    if (this.isRecording) {
      this.stopRecording();
    }
    this.stopVuMeter();
    this.handleAudioPlaybackBanner(false);
    if (this.waitingRoomKnockInterval) {
      clearInterval(this.waitingRoomKnockInterval);
      this.waitingRoomKnockInterval = null;
    }
    document.getElementById('waiting-room-lobby-overlay')?.remove();
    document.getElementById('waiting-room-host-banner')?.remove();
    if (this.localAudioMonitorInterval) {
      clearInterval(this.localAudioMonitorInterval);
      this.localAudioMonitorInterval = null;
    }
    if (this.localAudioMonitorCtx) {
      this.localAudioMonitorCtx.close().catch(() => {});
      this.localAudioMonitorCtx = null;
    }
    if (this.trackHealthInterval) {
      clearInterval(this.trackHealthInterval);
      this.trackHealthInterval = null;
    }
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    if (this.p2pMesh) {
      try {
        this.p2pMesh.leave();
      } catch (_) {}
      this.p2pMesh = null;
    }
    try {
      await this.manager.disconnect();
    } catch (e) {
      console.warn('[ConferenceUI] Disconnect error during cleanup:', e);
    }
    // Clean up any remaining remote audio elements and global tile action dropdown
    document.querySelectorAll('audio[id^="audio-remote-"]').forEach(el => el.remove());
    if ((this as any)._globalTileClickListener) {
      document.removeEventListener('click', (this as any)._globalTileClickListener);
      (this as any)._globalTileClickListener = null;
    }
    document.getElementById('global-tile-dropdown')?.remove();
    document.getElementById('vorbello-recording-canvas')?.remove();
    document.getElementById('vorbello-rec-admin-video')?.remove();
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
    if (typeof document !== 'undefined') {
      document.body.classList.remove('in-conference');
    }
  }
}
